import React, {useState, useEffect, useTransition} from 'react';
import ReactDOM from 'react-dom';
import {ReactComponent as FolderIcon} from '../../assets/icons/select-folder.svg';
import {useSelector, useDispatch, useStore} from 'react-redux';
import {ProgressBar, Button} from 'react-bootstrap';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Legend
} from 'recharts';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import TabFilterBox from '../TabFilterBox';
import { List } from 'react-window';

import {
    setInfoProgress, setInfoIsProcessing, resetInfoFilePath, setInfoFiles, setInfoErrorMessage,
    addEnrichedFile, addEnrichedFilesBatch, updateEnrichedFile, updateEnrichedFilesBatch,
    applyAnalyzingDelta,
    clearAnalyzingFiles, resetEnrichedFiles,
    removeEnrichedFiles, relocateEnrichedFiles, markEnrichedFilesMissing,
    setChartsVisible, setSortedFiles, setSortDirection,
    setActiveColumn, recomputeGraphData, syncSortedFromEnriched, setInfoAutoAnalyze, setInfoPlaylistName
} from '../../redux/slices/infoSlice';
import { setFilterTerm, setLibraryFocusTrack, setActiveTab } from '../../redux/slices/appSlice';
import { setAudioList, addTrack, toggleAutoPlay } from '../../redux/slices/musicPlayerSlice';
import { resolveCover, artThumbUrl } from '../../utils/cover';
import { clearDefault } from '../TrackArt';
import { getArtThumb, primeArtThumbs } from '../../utils/artThumbStore';
import useBackupRun from '../../hooks/useBackupRun';
import CloudBackupGlyph from '../CloudBackupGlyph';
import { completionPct, shouldShowLibraryBackupLine, libraryBackupLineCopy } from '../../utils/cloudBackup';
import { useTierGate } from '../../hooks/useTierGate';
import { waitRemaining } from '../../utils/minVisibleDelay';
import { toggleSidebar, triggerPlaylistRefresh } from '../../redux/slices/playlistSlice';
import { useToast } from '../../contexts/ToastContext';

import {clearLogs, addLogs, addLogsLineBreak} from '../../redux/slices/logsSlice';
import { ensureToken } from '../../redux/thunk/djpalThunk';
import { track } from '../../utils/analytics';
import { collectResolveCandidates, computeArtistParts } from '../../utils/localArtistSplit';
import {formatPath} from "../../utils/formatPath";
import { positionMenuByTrigger } from '../../utils/menuPosition';
import {ReactComponent as AudioFileIcon} from '../../assets/icons/audio-file.svg';
import {ReactComponent as PlayIcon} from '../../assets/icons/play.svg';
import {ReactComponent as PauseIcon} from '../../assets/icons/pause.svg';
import {ReactComponent as StopIcon} from '../../assets/icons/stop.svg';
import {ReactComponent as RefreshIcon} from '../../assets/icons/refresh.svg';
import {ReactComponent as AnalyzeFilesIcon} from '../../assets/icons/analyze-files.svg';
import { setAutoPlay, resetAudioList } from '../../redux/slices/musicPlayerSlice';
import { setListContext } from '../../redux/slices/listContextSlice';
import RowActions from '../RowActions';
import BulkRowContextMenu from '../BulkRowContextMenu';
import useRowSelection from '../../hooks/useRowSelection';
import { usePlayerRowSync } from '../../hooks/usePlayerRowSync';
import useBackedUpPaths from '../../hooks/useBackedUpPaths';
import { formatKeyDisplay, camelotColor } from '../../utils/harmonic';
import CamelotWheel from '../CamelotWheel';
import { updateSettings } from '../../redux/slices/settingsSlice';
import AddToPlaylistModal from '../AddToPlaylistModal';
import ProgressBarWithStop from '../ProgressBarWithStop.js';
import { useDesignV2 } from '../../utils/designV2';
import ToolHeader, { SourceChip } from '../v2/ToolHeader';
import PageTabs from '../v2/PageTabs';

const PlaylistIcon = ({width = 16, height = 16, style}) => (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
        <path d="M15 6H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M15 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <path d="M15 18H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        <circle cx="19" cy="18" r="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 6V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);

// Preserve the file list's scroll position across tab switches. The tab
// component unmounts on every switch (CategoryView renders only the active
// feature), so this lives at MODULE level — same pattern as FeedTab's
// _savedScrollTop and DJPalTab's scroll maps. Both axes: top is the browsing
// position, left is the horizontal column scroll (mirrored into the frozen
// header on restore).
const _savedListScroll = { top: 0, left: 0 };

// The fields "Fill Empty Values" can fill, in the order the picker lists them.
// `source: 'local'` = computed from the audio file here; `'api'` = comes from a
// Mixpedia match. Keys match the `attributes` the backend enricher expects
// (plus 'cover', which rides along with any Mixpedia match).
const FILL_FIELDS = [
    { key: 'bpm', label: 'BPM', source: 'local' },
    { key: 'key', label: 'Key', source: 'local' },
    { key: 'loudness', label: 'Loudness', source: 'local' },
    { key: 'energy', label: 'Energy', source: 'local', note: 'slow — ~10s per track' },
    { key: 'isCompatible', label: 'Compatibility', source: 'local' },
    { key: 'genre', label: 'Genre', source: 'api' },
    { key: 'releaseDate', label: 'Release date', source: 'api' },
    { key: 'label', label: 'Label', source: 'api' },
    { key: 'cover', label: 'Cover art', source: 'api' },
];
const FILL_FIELD_SOURCE = Object.fromEntries(FILL_FIELDS.map(f => [f.key, f.source]));
const FILL_GROUPS = [
    { source: 'local', title: 'Analyzed from your files', hint: 'Read from the audio on this machine' },
    { source: 'api', title: 'Looked up on Mixpedia', hint: 'Needs a Mixpedia match — sign in for these' },
];

// Row component for the virtualized library list. Defined at module scope so
// react-window can rely on a stable identity across parent renders — only
// the rowProps object changes, and react-window memoizes rows by index +
// rowProps shallow-equality. The full row UI (icons, artist/title columns,
// analyzed-attribute columns) was previously inlined in VisualizeLibraryTab's
// JSX; moved out verbatim so the only behavioral diff is virtualization.
// Coalesces the enricher's per-file start/done events into one store update
// every ~120ms. A warm run can start and finish dozens of files a second, and
// dispatching each one re-renders the list that many times.
const createAnalyzingTracker = (dispatch) => {
    const adds = new Map();
    const removes = new Set();
    let timer = null;
    const flush = () => {
        timer = null;
        if (!adds.size && !removes.size) return;
        dispatch(applyAnalyzingDelta({
            add: Array.from(adds, ([file, fields]) => ({ file, fields })),
            remove: Array.from(removes),
        }));
        adds.clear();
        removes.clear();
    };
    const schedule = () => { if (!timer) timer = setTimeout(flush, 120); };
    return {
        start(file, fields) { if (!file) return; removes.delete(file); adds.set(file, fields || []); schedule(); },
        done(file) { if (!file) return; adds.delete(file); removes.add(file); schedule(); },
        // End of the run — drop the buffer, then clear the map in one go.
        reset() {
            if (timer) { clearTimeout(timer); timer = null; }
            adds.clear();
            removes.clear();
            dispatch(clearAnalyzingFiles());
        },
    };
};

// Empty library cell: a faint dash, not "N/A" — a wall of N/A across LUFS, Date
// and Label was the noisiest thing in the list and said nothing.
const EMPTY_CELL = '—';
const cellText = (v) => (v === EMPTY_CELL ? <span className="cell-empty">{EMPTY_CELL}</span> : v);

// A row's cover + play icon. With no artwork (no cover, or the art server's
// 1×1 "clear" image) the icon sits on the grey tile and takes the text colour.
const LibraryPlaySlot = ({ coverSrc, children }) => {
    const [bare, setBare] = useState(!coverSrc);
    useEffect(() => { setBare(!coverSrc); }, [coverSrc]);
    return (
        <span className={`lib-play-slot${bare ? ' is-bare' : ''}`}>
            {/* decoding=sync + no lazy: a 3KB thumb decodes in well under a
                frame, and lazy-loading defers offscreen work exactly when fast
                scrolling needs it NOW — it's what made covers trail the text. */}
            {coverSrc && <img className="lib-hover-cover" src={coverSrc} alt="" decoding="sync" onLoad={(e) => setBare(e.currentTarget.naturalWidth <= 1)} />}
            {children}
        </span>
    );
};

const LibraryRow = ({ index, style, files, tableMinWidth, handleContextMenu, getPlaybackIcon, handleAddToQueue, handlePlayClick, keyNotation, artistWidth, titleWidth, bpmWidth, keyWidth, loudnessWidth, energyWidth, genreWidth, dateWidth, labelWidth, compatibilityWidth, analyzeBpm, analyzeKey, analyzeLoudness, analyzeGenre, analyzeDate, analyzeLabel, analyzeCompatibility, artistPartsMap, rankMap, showFullDate, reorderEnabled, dragOverIndex, onRowDragStart, onRowDragOver, onRowDrop, onRowDragEnd, onRemoveFromLibrary, onAddToPlaylist, onLocateFile, rowCtx, selectedPaths, onRowSelectClick, focusedPath, backupMarks, backupRun, analyzingFiles, curveWidth, showEnergyColumn, showCurveColumn }) => {
    const designV2 = useDesignV2();
    const file = files[index];
    if (!file) return null;
    const track = file?.result?.track;
    const isLoading = file?.result?.isLoading === true;
    // Queued for an analysis run that hasn't reached this row yet: it has no
    // values to show, but it is NOT being worked on — no marking, no dimming.
    const isPending = file?.result?.isPending === true;
    const isNotFound = file?.result?.fileNotFound === true;
    const fp = file?.file || '';
    // Being analyzed RIGHT NOW → the fields this run is computing for it. Only
    // those cells pulse: an energy-only run must not mark the whole row.
    const busyFields = analyzingFiles ? analyzingFiles[fp] : undefined;
    const busy = (field) => !!busyFields && busyFields.includes(field);
    // Blank (not "N/A") while a row has no values yet — a queued row in a fresh
    // scan. A row being analyzed right now keeps every cell the run is NOT
    // touching exactly as it was; only the busy ones turn into a pulse.
    const awaiting = isLoading || isPending;
    const isCloudTrack = fp.startsWith('online://') || fp.startsWith('http://') || fp.startsWith('https://');
    let artist, title;
    if (track?.artist && track?.title) {
        artist = track.artist;
        title = track.title;
    } else {
        const fn = file?.fileName || '';
        const isMzaf = fn.startsWith('mzaf_');
        if (fn.includes(' - ') && !isMzaf) {
            const parts = fn.split(' - ');
            artist = track?.artist || parts[0].trim() || 'Unknown Artist';
            title = track?.title || parts.slice(1).join(' - ').trim() || 'Unknown Title';
        } else if (isMzaf && isCloudTrack) {
            artist = track?.artist || 'Unknown Artist';
            title = track?.title || 'Cloud Track';
        } else {
            artist = track?.artist || '';
            title = file?.result?.trackName || track?.title || fn || '';
        }
    }
    // Duo-aware artist splitting: the KB (artistPartsMap) says whether "A & B" is
    // a real act (keep) or a collab (split into "A, B"). Falls back to the raw
    // string until the map resolves.
    const displayArtist = (() => {
        const parts = artist && artistPartsMap ? artistPartsMap.get(artist) : null;
        return parts && parts.length > 1 ? parts.join(', ') : artist;
    })();
    const genre = track?.styles?.[0] || track?.genres?.[0] || track?.genre || (awaiting ? '' : EMPTY_CELL);
    const label = track?.label || (awaiting ? '' : EMPTY_CELL);
    const keyVal = track?.key ? formatKeyDisplay(track.key, keyNotation) : (awaiting ? '' : EMPTY_CELL);
    const keyColor = camelotColor(track?.key);
    const bpm = track?.bpm || (awaiting ? '' : EMPTY_CELL);
    // Extract the 4-digit year regardless of how release date is stored:
    // "2017", 2017, 2017.0, "2017.0", "2017-01-01", "2017/05/15" all → "2017".
    // Pyrekordbox sometimes hands back ReleaseYear as a float, which JSON-
    // serializes with a ".0" suffix and gets stored verbatim in release_date.
    const releaseYearDisplay = (() => {
        const v = track?.releaseDate;
        if (!v) return awaiting ? '' : EMPTY_CELL;
        const s = String(v).trim();
        const year = s.match(/\d{4}/)?.[0];
        if (!showFullDate) return year || s;
        // Full date: YYYYMMDD → YYYY-MM-DD; already-dashed → first 10 chars; else year.
        const digits = s.replace(/\D/g, '');
        if (digits.length === 8) return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
        return year || s;
    })();
    const loudness = track?.loudness ? `${track.loudness.toFixed(1)}` : (awaiting ? '' : EMPTY_CELL);
    // Energy reads as an INTEGER 1..10 — the Mixed In Key convention every DJ
    // app inherits, and what fits a narrow column. The exact value lives in the
    // tooltip, and sorting still uses the stored float.
    const energyValue = (track?.energy != null && !isNaN(parseFloat(track.energy))) ? parseFloat(track.energy) : null;
    const energyRounded = energyValue != null ? Math.min(10, Math.max(1, Math.round(energyValue))) : null;
    // Per-section 1..10 (12 spans) when the track was analysed with sections.
    const energySections = Array.isArray(track?.energySections) && track.energySections.length ? track.energySections : null;
    const energyPeak = energySections ? Math.max(...energySections) : null;
    const isCompatible = track?.isCompatible;
    const filePath = file.file;
    // Row cover, painted SYNCHRONOUSLY from the in-memory thumb store when
    // primed (data URI already in RAM — no request, no pop-in; this is how
    // native players stay instant). Store misses fall back to the endpoint's
    // thumbnail variant (`s=1`) — never the full art: embedded covers can be
    // multi-MB, and a slider jump decoding ~20 of those is visible jank.
    const memThumb = track?.id != null ? getArtThumb(track.id) : undefined;
    // No art → no image: the slot's quiet tile shows (TrackArt.scss), and the
    // art server sends a local file without art as transparent (clear=1).
    const coverSrc = memThumb !== undefined
        ? (memThumb || null)
        : clearDefault(artThumbUrl(track?.cover) || (fp && !isCloudTrack ? `http://localhost:3069/album-art?path=${encodeURIComponent(fp)}` : null));
    const showTopLine = reorderEnabled && dragOverIndex === index;
    const showBottomLine = reorderEnabled && dragOverIndex === index + 1 && index === files.length - 1;
    // Drag SOURCE props applied to the non-text cells only — so Artist/Title stay
    // selectable (a draggable row would swallow text selection everywhere).
    const dragCellProps = reorderEnabled
        ? { draggable: true, onDragStart: (e) => onRowDragStart(index, e), onDragEnd: onRowDragEnd }
        : {};
    return (
        <div
            style={{
                ...style,
                minWidth: `${tableMinWidth}px`,
                boxShadow: showTopLine ? 'inset 0 2px 0 var(--color-accent, #4CC2F1)'
                    : showBottomLine ? 'inset 0 -2px 0 var(--color-accent, #4CC2F1)' : 'none',
            }}
            className="tree-node-wrapper"
            onDragOver={reorderEnabled ? (e) => onRowDragOver(index, e) : undefined}
            onDrop={reorderEnabled ? (e) => onRowDrop(index, e) : undefined}
        >
            <div
                className={`tree-node${focusedPath === filePath ? ' library-row-flash' : ''}`}
                style={{
                    opacity: isNotFound ? 0.5 : 1,
                    ...(selectedPaths && selectedPaths.has(filePath) ? {
                        background: 'var(--row-selected-bg)',   // wash + painted bar (_theme.scss)
                        // selected rows TOUCH: square, no 1px gap (the blue-grey panel showed
                        // through it between grey rows), so a range is one block with one
                        // unbroken bar; the gap's 2px move into the padding — nothing shifts
                        borderRadius: 0,
                        marginTop: 0,
                        marginBottom: 0,
                        paddingTop: 6,   // top/bottom only: Organize indents rows with marginLeft
                        paddingBottom: 6,
                    } : {}),
                }}
                title={isNotFound ? `File not found: ${fp}` : ''}
                onContextMenu={(e) => handleContextMenu(filePath, e, index)}
                onDoubleClick={(e) => handlePlayClick(filePath, e)}
                onClick={onRowSelectClick ? (e) => onRowSelectClick(index, filePath, e) : undefined}
                // Shift-click extends the row selection — suppress the browser's
                // native shift-click text-selection so the two don't fight.
                onMouseDown={(e) => { if (e.shiftKey) e.preventDefault(); }}
            >
                <span {...dragCellProps} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: '5px', width: '54px', minWidth: '54px', flexShrink: 0, verticalAlign: 'middle', paddingLeft: '0', marginRight: '8px', boxSizing: 'border-box', cursor: reorderEnabled ? 'grab' : undefined }}>
                    {isNotFound ? (
                        <>
                            <span
                                title={`File not found: ${fp}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '14px',
                                    height: '14px',
                                    borderRadius: '50%',
                                    border: '1.5px solid var(--color-danger)',
                                    color: 'var(--color-danger)',
                                    fontWeight: 700,
                                    fontSize: '10px',
                                    lineHeight: 1,
                                    flexShrink: 0,
                                    opacity: 1,
                                    userSelect: 'none',
                                    cursor: 'help',
                                }}
                            >!</span>
                            {/* Missing rows get the same menu (⋯ + right-click), with the
                                file-dependent items disabled and "Locate file…" added. */}
                            <RowActions
                                track={{ title, artist, filePath, artwork: track?.cover || null, djpalId: track?.djpalId || null }}
                                fileMissing
                                onLocateFile={onLocateFile ? () => onLocateFile(filePath) : undefined}
                                onRemoveFromLibrary={onRemoveFromLibrary ? () => onRemoveFromLibrary(filePath) : undefined}
                                openAtPoint={rowCtx && rowCtx.key === filePath ? rowCtx : null}
                                desktopLike
                                analyticsFrom="library"
                            />
                        </>
                    ) : (
                        <>
                            <LibraryPlaySlot coverSrc={coverSrc}>
                                {getPlaybackIcon(filePath)}
                            </LibraryPlaySlot>
                            <RowActions
                                // artwork/djpalId ride along so a Like (and Start
                                // a set) carries the Mixpedia cover + catalog id —
                                // without them the like stores bare text the likes
                                // view can't enrich (no artwork, no id-hydrate).
                                track={{ title, artist, filePath, artwork: track?.cover || null, djpalId: track?.djpalId || null }}
                                onAddToQueue={() => handleAddToQueue(file, { stopPropagation() {} })}
                                onAddToPlaylist={onAddToPlaylist ? () => onAddToPlaylist(filePath) : undefined}
                                onRemoveFromLibrary={onRemoveFromLibrary ? () => onRemoveFromLibrary(filePath) : undefined}
                                openAtPoint={rowCtx && rowCtx.key === filePath ? rowCtx : null}
                                desktopLike
                                analyticsFrom="library"
                            />
                            {isCloudTrack && <span title="Cloud track" style={{ opacity: 0.35, fontSize: '10px', userSelect: 'none' }}>&#9729;</span>}
                        </>
                    )}
                </span>
                <span
                    className="lib-row-num"
                    {...dragCellProps}
                    title={reorderEnabled ? 'Drag to reorder' : ''}
                    style={{ position: 'relative', width: '40px', minWidth: '40px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', paddingRight: '6px', color: 'var(--color-text-secondary)', fontSize: '11px', fontVariantNumeric: 'tabular-nums', userSelect: 'none', boxSizing: 'border-box', cursor: reorderEnabled ? 'grab' : undefined }}
                >
                    {reorderEnabled && <span className="lib-drag-grip" aria-hidden="true">⠿</span>}
                    <span className="lib-row-num-value">{(rankMap && rankMap.get(file?.file || filePath)) || (index + 1)}</span>
                </span>
                {/* Cloud Backup column (only when the files layer is on).
                    Dropbox-style states: green check = stored; blue up-cloud =
                    a run is active and will handle this row (the row actually
                    transferring pulses); muted slash = not backed up and no
                    run is doing anything about it. Cloud tracks are outside
                    backup's scope — their cell stays empty. */}
                {backupMarks?.active && (() => {
                    let glyph = null;
                    if (!isCloudTrack && backupMarks.paths) {
                        if (backupMarks.paths.has(filePath)) {
                            glyph = <CloudBackupGlyph variant="check" title="Backed up" />;
                        } else if (isNotFound) {
                            glyph = <CloudBackupGlyph variant="slash" title="File is missing — not backed up" />;
                        } else if (backupRun?.running) {
                            glyph = (
                                <CloudBackupGlyph
                                    variant="up"
                                    className={backupRun.currentFile === filePath ? 'backup-pulse' : undefined}
                                    title={backupRun.currentFile === filePath ? 'Uploading…' : 'Waiting to upload'}
                                />
                            );
                        } else {
                            glyph = <CloudBackupGlyph variant="slash" title="Not backed up yet" />;
                        }
                    }
                    return (
                        <span {...dragCellProps} style={{ width: '30px', minWidth: '30px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                            {glyph}
                        </span>
                    );
                })()}
                <span data-noreorder="1" title={displayArtist} style={{ width: `${artistWidth}px`, flexShrink: 1, display: 'inline-block', color: isCloudTrack ? 'var(--color-text-secondary)' : 'var(--color-text-strong)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle', userSelect: 'text', WebkitUserDrag: 'none', cursor: 'text' }}>{displayArtist}</span>
                <span data-noreorder="1" title={title} style={{ width: `${titleWidth}px`, flexShrink: 1, display: 'inline-block', color: isCloudTrack ? 'var(--color-text-secondary)' : 'var(--color-text-strong)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle', userSelect: 'text', WebkitUserDrag: 'none', cursor: 'text' }}>{title}</span>
                {analyzeBpm && <span {...dragCellProps} title={String(bpm)} style={{ width: `${bpmWidth}px`, flexShrink: 1, display: 'inline-block', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>{busy('bpm') ? <span className="cell-skeleton" /> : cellText(bpm)}</span>}
                {analyzeKey && <span {...dragCellProps} style={{ width: `${keyWidth}px`, flexShrink: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', boxSizing: 'border-box', verticalAlign: 'middle', overflow: 'hidden' }}>
                    {busy('key') ? <span className="cell-skeleton" style={{ width: '46px' }} /> : keyColor ? (
                        <span title={keyVal} style={{ background: keyColor, color: '#1a1a1a', fontWeight: 600, padding: '1px 4px', borderRadius: '4px', minWidth: '46px', textAlign: 'center', display: 'inline-block', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{keyVal}</span>
                    ) : (
                        <span title={keyVal} style={{ color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cellText(keyVal)}</span>
                    )}
                </span>}
                {showEnergyColumn && <span {...dragCellProps} title={energyValue == null ? 'Energy not analyzed yet' : `Energy ${energyValue.toFixed(1)} / 10`} style={{ width: `${energyWidth}px`, flexShrink: 1, position: 'relative', display: 'inline-flex', alignItems: 'center', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>
                    {busy('energy')
                        ? <span className="cell-skeleton" />
                        : energyRounded != null
                            ? <>
                                {/* Bar length = the float (not the rounded digit), so 6.4 and 6.6 still differ. */}
                                <span className="energy-meter"><i style={{ width: `${Math.min(100, Math.max(0, energyValue * 10))}%` }} /></span>
                                <span className="energy-num">{energyRounded}</span>
                            </>
                            : (awaiting ? '' : cellText(EMPTY_CELL))}
                </span>}
                {/* Curve: the track in 12 equal slices on the same 1..10 scale, no
                    number — how the energy moves (build, breakdown, drop). Blank
                    until the track has been analysed with sections. */}
                {showCurveColumn && <span {...dragCellProps} title={energySections ? `Energy across the track: ${energySections.map((v) => Math.round(v)).join(' · ')}` : undefined} style={{ width: `${curveWidth}px`, flexShrink: 1, display: 'inline-flex', alignItems: 'center', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>
                    {busy('energy')
                        ? <span className="cell-skeleton" />
                        : energySections
                            ? <span className="energy-curve" aria-hidden="true">
                                {energySections.map((v, i) => (
                                    <i key={i} className={v === energyPeak ? 'peak' : undefined} style={{ height: `${Math.max(2, Math.round((v / 10) * 20))}px` }} />
                                ))}
                            </span>
                            : null}
                </span>}
                {analyzeGenre && <span {...dragCellProps} title={genre} style={{ width: `${genreWidth}px`, flexShrink: 1, display: 'inline-block', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>{busy('genre') ? <span className="cell-skeleton" /> : cellText(genre)}</span>}
                {analyzeDate && <span {...dragCellProps} title={track?.releaseDate || undefined} style={{ width: `${dateWidth}px`, flexShrink: 1, display: 'inline-block', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>{busy('releaseDate') ? <span className="cell-skeleton" /> : cellText(releaseYearDisplay)}</span>}
                {analyzeLabel && <span {...dragCellProps} title={label} style={{ width: `${labelWidth}px`, flexShrink: 1, display: 'inline-block', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>{busy('label') ? <span className="cell-skeleton" /> : cellText(label)}</span>}
                {analyzeLoudness && <span {...dragCellProps} title={loudness} style={{ width: `${loudnessWidth}px`, flexShrink: 1, display: 'inline-block', color: 'var(--color-text-secondary)', fontSize: '12px', paddingLeft: '8px', paddingRight: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle' }}>{busy('loudness') ? <span className="cell-skeleton" /> : cellText(loudness)}</span>}
                {analyzeCompatibility && <span {...dragCellProps} style={{ width: `${compatibilityWidth}px`, flexShrink: 1, display: 'inline-block', color: isCompatible === false ? (designV2 ? 'var(--color-danger)' : '#FF6B9D') : isCompatible === true ? (designV2 ? 'var(--color-success)' : '#4CC2F1') : (designV2 ? 'var(--color-text-tertiary)' : '#AAAAAA'), fontSize: '12px', paddingLeft: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxSizing: 'border-box', verticalAlign: 'middle', fontWeight: isCompatible !== undefined ? '600' : 'normal', textAlign: 'center' }}>{busy('isCompatible') ? <span className="cell-skeleton" style={{ width: '14px' }} /> : isCompatible === false ? (designV2 ? <span className="v2-compat-glyph is-bad" aria-label="Not compatible" /> : '✗') : isCompatible === true ? (designV2 ? <span className="v2-compat-glyph is-ok" aria-label="Compatible" /> : '✓') : awaiting ? '' : cellText(EMPTY_CELL)}</span>}
                {/* Trailing drag zone: fills the empty space after the last column so
                    the user can grab any non-text pixel of the row to reorder (the
                    Artist/Title spans stay non-draggable + selectable). */}
                {reorderEnabled && <span {...dragCellProps} aria-hidden="true" style={{ flex: '1 1 auto', minWidth: 0, alignSelf: 'stretch', cursor: 'grab' }} />}
            </div>
        </div>
    );
};

const VisualizeLibraryTab = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const toast = useToast();
    // Cloud Backup exception marker: { active, paths } — rows NOT yet stored
    // get a muted mark while the files layer is on; stored rows stay clean.
    const backupMarks = useBackedUpPaths();
    const backupRun = useBackupRun();

    // Local state for playback control
    const [currentlyPlayingPath, setCurrentlyPlayingPath] = React.useState(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    // Reconcile the row play/pause icon when the user pauses/skips from the
    // bottom player (otherwise the row stays stuck showing "playing"), and
    // move the highlight to the next row on list auto-advance.
    usePlayerRowSync(currentlyPlayingPath, setIsPlaying, setCurrentlyPlayingPath);

    // Right-click menus. A single-row right-click opens that row's shared
    // TrackContextMenu (via RowActions `openAtPoint`), so right-click and the
    // ⋯ button always show identical options; a right-click inside a multi-row
    // selection opens the bulk menu instead. Selection state itself lives in
    // the shared useRowSelection hook (declared after filteredFiles).
    const [rowCtx, setRowCtx] = React.useState(null);   // { key, x, y, nonce }
    const [bulkCtx, setBulkCtx] = React.useState(null); // { x, y }
    const ctxNonceRef = React.useRef(0);

    // Drag and drop state
    const [isDragging, setIsDragging] = React.useState(false);

    // Reference for the selected folder container to measure its width
    const folderPathRef = React.useRef(null);
    const [shouldTruncatePath, setShouldTruncatePath] = React.useState(false);

    // Track source (folder vs playlist) — from Redux so sidebar can set it too
    const selectedPlaylistName = useSelector(state => state.info.selectedPlaylistName);
    const setSelectedPlaylistName = (name) => dispatch(setInfoPlaylistName(name));

    // Inline playlist dropdown state
    const [playlistDropdownOpen, setPlaylistDropdownOpen] = React.useState(false);
    const [playlistList, setPlaylistList] = React.useState([]);
    const [playlistsLoading, setPlaylistsLoading] = React.useState(false);
    const [playlistSearch, setPlaylistSearch] = React.useState('');
    const [dropdownPos, setDropdownPos] = React.useState({top: 0, left: 0, width: 250});
    // Flips the "Select Playlist" picker above its button when there's no room
    // below (e.g. Actions panel collapsed short, or a short window).
    const [dropdownOpenUp, setDropdownOpenUp] = React.useState(false);
    const playlistDropdownRef = React.useRef(null);
    const playlistMenuRef = React.useRef(null); // the flyout itself (for viewport flip measuring)
    const playlistBtnRef = React.useRef(null);
    const playlistSearchRef = React.useRef(null);
    // Frozen-header viewport: its scrollLeft is synced to the row List's
    // horizontal scroll so the header tracks the columns while staying pinned
    // (the List owns BOTH scrollbars, so they stay at the panel's edges).
    const headerScrollRef = React.useRef(null);
    // The header follows the List's horizontal scroll by TRANSLATING its
    // content, not by mirroring scrollLeft into the header viewport. The List's
    // vertical scrollbar makes it narrower than the header, so the List can
    // scroll further than the header viewport is able to — the browser clamps
    // the header's scrollLeft and, at the far right, every header label sat
    // ~20px right of its column. A transform can't clamp.
    const headerInnerRef = React.useRef(null);
    const syncHeaderScroll = (left) => {
        if (headerInnerRef.current) headerInnerRef.current.style.transform = left ? `translateX(${-left}px)` : '';
    };

    // Local state for analysis options
    const [analyzeGenre, setAnalyzeGenre] = React.useState(true);
    const [analyzeDate, setAnalyzeDate] = React.useState(true);
    const [analyzeLabel, setAnalyzeLabel] = React.useState(true);
    const [analyzeKey, setAnalyzeKey] = React.useState(true);
    const [analyzeBpm, setAnalyzeBpm] = React.useState(true);
    const [analyzeLoudness, setAnalyzeLoudness] = React.useState(true);
    // Energy (1..10) is computed only when explicitly picked for a run, in the
    // Fill Empty Values dialog — it's a separate Python pass at ~10 s/track.
    // No toggle in the settings panel: that panel shows/hides COLUMNS, and the
    // Energy/Curve columns have their own checkboxes (showEnergyColumn /
    // showCurveColumn). This flag only seeds the dialog's default and the
    // attribute list of a plain Analyze run.
    const [analyzeEnergy] = React.useState(false);
    const [analyzeCompatibility, setAnalyzeCompatibility] = React.useState(true);

    // Files the running analysis has in flight right now → the fields it is
    // computing for each. Drives the per-cell pulse in the list.
    const analyzingFiles = useSelector((state) => state.info.analyzingFiles);
    // "Fill Empty Values" opens a picker first. It starts from the visible
    // columns EVERY time — a narrowing applies to that run only. Remembering
    // the last pick meant a later click could quietly fill 9 tracks on a
    // library with thousands of gaps, with nothing on screen saying why.
    const [fillPicker, setFillPicker] = React.useState(null); // { selection } | null
    // What an in-flight run is doing before it reaches per-track work.
    const [analysisStage, setAnalysisStage] = React.useState('');

    // Local state for filters
    const [bpmMin, setBpmMin] = React.useState(60);
    const [bpmMax, setBpmMax] = React.useState(180);
    const [loudnessMin, setLoudnessMin] = React.useState(-30);
    const [loudnessMax, setLoudnessMax] = React.useState(0);
    const [releaseDateMin, setReleaseDateMin] = React.useState(1980);
    const [releaseDateMax, setReleaseDateMax] = React.useState(new Date().getFullYear());
    const [selectedKeys, setSelectedKeys] = React.useState([]);
    // Hide tracks whose local file no longer exists on disk (file.result.fileNotFound).
    const [hideMissingFiles, setHideMissingFiles] = React.useState(false);
    // Cloud-backup filter: two mutually-exclusive chips, "Backed up" /
    // "Not backed up" (null = both off = no filtering; there is no "All"
    // chip). Only offered while the files layer is on.
    const [backupFilter, setBackupFilter] = React.useState(null); // null | 'stored' | 'unstored'

    // Duo-aware artist-split decisions: Map<rawArtistString, string[]>.
    const [artistPartsMap, setArtistPartsMap] = React.useState(null);

    // Local state for table column widths
    const [artistWidth, setArtistWidth] = React.useState(150);
    const [titleWidth, setTitleWidth] = React.useState(200);
    const [dateWidth, setDateWidth] = React.useState(100);
    const [genreWidth, setGenreWidth] = React.useState(100);
    const [labelWidth, setLabelWidth] = React.useState(100);
    const [keyWidth, setKeyWidth] = React.useState(78);
    const [bpmWidth, setBpmWidth] = React.useState(70);
    const [loudnessWidth, setLoudnessWidth] = React.useState(60);
    const [energyWidth, setEnergyWidth] = React.useState(84);
    // Curve: the 12-slice energy shape, its own column so the Energy number
    // never has to explain a picture it doesn't describe.
    const [curveWidth, setCurveWidth] = React.useState(84);
    // Energy/Curve column visibility. Separate from `analyzeEnergy`, which opts
    // into COMPUTING energy (a slow pass) — hiding a column must not turn the
    // analysis off, and vice versa.
    const [showEnergyColumn, setShowEnergyColumn] = React.useState(true);
    const [showCurveColumn, setShowCurveColumn] = React.useState(true);
    const [compatibilityWidth, setCompatibilityWidth] = React.useState(60);

    // Total width of all visible columns (+ the 54px icon col / margins). The
    // header and every row use this as their min-width so the table keeps
    // readable column widths and scrolls horizontally instead of crushing.
    const tableMinWidth = 40 + 62 + (backupMarks.active ? 30 : 0) + artistWidth + titleWidth
        + (analyzeBpm ? bpmWidth : 0) + (analyzeKey ? keyWidth : 0)
        + (analyzeLoudness ? loudnessWidth : 0) + (showEnergyColumn ? energyWidth : 0)
        + (showCurveColumn ? curveWidth : 0) + (analyzeGenre ? genreWidth : 0)
        + (analyzeDate ? dateWidth : 0) + (analyzeLabel ? labelWidth : 0)
        + (analyzeCompatibility ? compatibilityWidth : 0) + 12;

    // Local state for panel expansion
    const [actionsExpanded, setActionsExpanded] = React.useState(true);
    const [filesExpanded, setFilesExpanded] = React.useState(true);
    const [analyticsExpanded, setAnalyticsExpanded] = React.useState(false);
    const [filtersExpanded, setFiltersExpanded] = React.useState(true);

    // DESIGN V2 — one tool header instead of four panels. The classic columns
    // stay mounted; v2 re-homes them with CSS (data-v2-col) and these states:
    // the Filters column becomes a right-anchored drawer (open = filtersExpanded),
    // Analytics becomes a tab (open = analyticsExpanded), Actions is hidden and
    // its buttons live in the header + the "more" menu.
    const designV2 = useDesignV2();
    const [v2View, setV2View] = React.useState('tracks');       // 'tracks' | 'analytics'
    const [v2Menu, setV2Menu] = React.useState(null);           // 'source' | 'more' | null
    const [v2PlaylistsOpen, setV2PlaylistsOpen] = React.useState(false);
    const v2HeaderRef = React.useRef(null);
    // DESIGN V2: the filters panel closes on a click outside it (or Escape).
    React.useEffect(() => {
        if (!designV2 || !filtersExpanded) return undefined;
        const onDown = (e) => {
            const panel = document.querySelector('[data-v2-col="filters"]');
            if ((panel && panel.contains(e.target)) || (v2HeaderRef.current && v2HeaderRef.current.contains(e.target))) return;
            setFiltersExpanded(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setFiltersExpanded(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [designV2, filtersExpanded]);
    React.useEffect(() => {
        // Drawer closed by default under v2; classic layout shows the column.
        setFiltersExpanded(!designV2);
        if (!designV2) { setV2Menu(null); setV2View('tracks'); }
    }, [designV2]);
    React.useEffect(() => {
        if (!designV2) return;
        setAnalyticsExpanded(v2View === 'analytics');
    }, [designV2, v2View]);
    React.useEffect(() => {
        if (!v2Menu) return undefined;
        const onDown = (e) => { if (v2HeaderRef.current && !v2HeaderRef.current.contains(e.target)) { setV2Menu(null); setV2PlaylistsOpen(false); } };
        const onKey = (e) => { if (e.key === 'Escape') { setV2Menu(null); setV2PlaylistsOpen(false); } };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [v2Menu]);
    const loadPlaylistListV2 = async () => {
        setPlaylistsLoading(true);
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            setPlaylistList(allPlaylists || []);
        } catch (error) {
            console.error('Error loading playlists:', error);
        } finally {
            setPlaylistsLoading(false);
        }
    };

    // Below 1400px the multi-column layout is too cramped to fit the
    // Actions panel's labels AND the analytics legend AND the filters
    // settings, so we collapse Actions to icon-only and hand the
    // reclaimed flex space to Filters & Settings + the charts.
    const [narrowViewport, setNarrowViewport] = React.useState(
        typeof window !== 'undefined' && window.innerWidth < 1400
    );
    React.useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const onResize = () => setNarrowViewport(window.innerWidth < 1400);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    // Per user: icon-only actions are unclear — keep the LABELS even on narrow
    // viewports and let the column flex (reclaiming width from Files/Filters)
    // instead of collapsing to a 64px icon strip. The manual collapse arrow
    // (◀, actionsExpanded=false) still lets the user shrink the panel on
    // demand. narrowViewport is still used elsewhere (analytics/legend sizing).
    const actionsIconOnly = false;

    // Genre legend: render only if the full list fits in the section.
    // Partial-clipped lists look weirder than no legend at all, per user
    // request. Strategy: always render the legend, measure with a layout
    // effect after every relevant render, and flip visibility off if it
    // would spill past the section bottom. The donut grows into the
    // freed space when the legend hides.
    const genreSectionRef = React.useRef(null);
    const genreLegendRef = React.useRef(null);
    const [genreLegendVisible, setGenreLegendVisible] = React.useState(true);
    // Reset to "try to show" whenever inputs change so we re-evaluate.
    React.useEffect(() => {
        setGenreLegendVisible(true);
    }, [narrowViewport]);
    React.useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        let raf = 0;
        const recheck = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setGenreLegendVisible(true));
        };
        window.addEventListener('resize', recheck);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', recheck);
        };
    }, []);
    React.useLayoutEffect(() => {
        if (!genreLegendVisible) return; // already hidden, nothing to measure
        const section = genreSectionRef.current;
        const legend = genreLegendRef.current;
        if (!section || !legend) return;
        // The legend always sits inside the section so legend.bottom ≈
        // section.bottom — the actual clip happens at the scrolling
        // ancestor (file-list-container has overflow-y:auto). Walk up
        // to find it and compare against the last legend item's bottom.
        let scrollContainer = section.parentElement;
        while (scrollContainer) {
            const style = window.getComputedStyle(scrollContainer);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
            scrollContainer = scrollContainer.parentElement;
        }
        if (!scrollContainer) return;
        const lastItem = legend.lastElementChild;
        if (!lastItem) return;
        const lastBottom = lastItem.getBoundingClientRect().bottom;
        const containerBottom = scrollContainer.getBoundingClientRect().bottom;
        if (lastBottom > containerBottom - 2) {
            setGenreLegendVisible(false);
        }
    });

    // Close playlist dropdown on outside click
    React.useEffect(() => {
        const handleClickOutside = (e) => {
            if (playlistDropdownRef.current && !playlistDropdownRef.current.contains(e.target)) {
                setPlaylistDropdownOpen(false);
            }
        };
        if (playlistDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [playlistDropdownOpen]);

    // Re-measure the picker after it renders (and as its list fills in) so it
    // flips above the button when there isn't room below.
    React.useLayoutEffect(() => {
        if (!playlistDropdownOpen || !playlistMenuRef.current || !playlistBtnRef.current) return;
        const rect = playlistBtnRef.current.getBoundingClientRect();
        const { top, openUp } = positionMenuByTrigger(playlistMenuRef.current, rect, { gap: 0, align: null });
        setDropdownPos(prev => (prev.top === top ? prev : { ...prev, top }));
        setDropdownOpenUp(openUp);
    }, [playlistDropdownOpen, playlistList, playlistSearch, playlistsLoading]);

    const handlePlaylistDropdownToggle = async () => {
        if (playlistDropdownOpen) {
            setPlaylistDropdownOpen(false);
            return;
        }
        setPlaylistSearch('');
        if (playlistBtnRef.current) {
            const rect = playlistBtnRef.current.getBoundingClientRect();
            setDropdownPos({top: rect.bottom, left: rect.left, width: rect.width});
        }
        setPlaylistDropdownOpen(true);
        setTimeout(() => playlistSearchRef.current?.focus(), 50);
        setPlaylistsLoading(true);
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            setPlaylistList(allPlaylists || []);
        } catch (error) {
            console.error('Error loading playlists:', error);
            toast.error('Failed to load playlists');
        } finally {
            setPlaylistsLoading(false);
        }
    };

    const handlePlaylistSelect = async (playlist) => {
        setPlaylistDropdownOpen(false);
        try {
            const playlistData = await window.electron.db.playlists.get(playlist.id, true);
            if (!playlistData || !playlistData.tracks || playlistData.tracks.length === 0) {
                toast.warning('This playlist has no tracks');
                return;
            }
            const tracks = playlistData.tracks.filter(t => t.file || t.filePath);
            if (tracks.length === 0) {
                toast.warning('No valid file paths found in playlist');
                return;
            }
            const filePaths = tracks.map(t => t.file || t.filePath);
            setSelectedPlaylistName(playlist.name);
            dispatch(resetEnrichedFiles());
            dispatch(setInfoFiles(filePaths));
            // Pre-populate enriched data from DB so tracks show metadata without re-analyzing.
            // Playlist tracks arrive in position order, so sortOrder = index gives the
            // "#" column / sort-by-# / drag-reorder the playlist's own ordering.
            const enriched = tracks.map((t, i) => ({
                file: t.file || t.filePath,
                fileName: t.fileName || (t.file || t.filePath).split('/').pop(),
                sortOrder: i,
                result: { track: t.track }
            }));
            dispatch(addEnrichedFilesBatch(enriched));
            dispatch(setActiveColumn('sortOrder')); // enable drag-reorder in the playlist by default
            dispatch(setSortDirection('asc'));
            dispatch(recomputeGraphData());
            // Flag tracks whose files aren't on this machine (e.g. a playlist
            // synced from another computer) — rows show the red ! and lose
            // their play button, same as the full-library load. Keep the
            // playlist's own order (no missing-to-bottom resort here).
            window.electron.db.tracks.checkMissingFiles(filePaths)
                .then(missing => {
                    if (!Array.isArray(missing)) return;
                    dispatch(markEnrichedFilesMissing({ missing, checked: filePaths }));
                })
                .catch(e => console.warn('[Library] Playlist missing-file check failed:', e.message));
            // toast removed — UI already shows playlist name + track count
        } catch (error) {
            console.error('Error loading playlist tracks:', error);
            toast.error('Failed to load playlist tracks');
        }
    };

    // Available musical keys
    const handleKeyToggle = (key) => {
        setSelectedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };


    // Track whether a column resize drag is in progress to suppress sort clicks
    const isResizingRef = React.useRef(false);

    // Handle table column resizing
    const handleColumnResize = (columnName, startWidth, e) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        let didDrag = false;

        const handleMouseMove = (moveEvent) => {
            moveEvent.preventDefault();
            didDrag = true;
            const deltaX = moveEvent.clientX - startX;
            // Narrow columns (LUFS, Compat) default below the old 80px floor —
            // the first drag must not snap them wider.
            const newWidth = Math.max(48, startWidth + deltaX);

            switch(columnName) {
                case 'artist':
                    setArtistWidth(newWidth);
                    break;
                case 'title':
                    setTitleWidth(newWidth);
                    break;
                case 'date':
                    setDateWidth(newWidth);
                    break;
                case 'genre':
                    setGenreWidth(newWidth);
                    break;
                case 'label':
                    setLabelWidth(newWidth);
                    break;
                case 'key':
                    setKeyWidth(newWidth);
                    break;
                case 'bpm':
                    setBpmWidth(newWidth);
                    break;
                case 'loudness':
                    setLoudnessWidth(newWidth);
                    break;
                case 'energy':
                    setEnergyWidth(newWidth);
                    break;
                case 'curve':
                    setCurveWidth(newWidth);
                    break;
                case 'compatibility':
                    setCompatibilityWidth(newWidth);
                    break;
            }
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (didDrag) {
                // Block the click event that fires after mouseup from triggering sort
                isResizingRef.current = true;
                setTimeout(() => { isResizingRef.current = false; }, 0);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    // Helper function to format key names
    const formatKey = (key) => {
        if (!key || key === 'N/A') return 'N/A';
        // Convert "Fmajor" -> "F" and "Fminor" -> "Fm"
        return key.replace('major', '').replace('minor', 'm');
    };

    const {
        infoProgress,
        infoIsProcessing,
        infoFiles,
        infoErrorMessage,
        loudnessLevel,
        inputLoudnessLevel,
        appendFileNameBeginning,
        enrichedFiles,
        groupYears,
        topGenres,
        chartsVisible,
        sortedFiles,
        sortDirection,
        activeColumn,
        autoAnalyze
    } = useSelector((state) => state.info);

    const logs = useSelector((state) => state.logs.logs);
    // Library's own table filter (TabFilterBox, per-tab term). Cmd+F find
    // (state.app.searchTerm) no longer narrows this list — it highlights only.
    const searchTerm = useSelector((state) => state.app.filterTerms.library || '');
    const libraryFocusTrack = useSelector((state) => state.app.libraryFocusTrack);
    const { selectedPlaylistId, playlists, refreshTrigger } = useSelector((state) => state.playlist);

    // Re-load library tracks when a sidebar import triggers a refresh — but NOT
    // while the user is viewing a selected folder or playlist (a playlist-create
    // also bumps refreshTrigger, and we don't want it to blow away that view).
    React.useEffect(() => {
        if (refreshTrigger > 0 && libraryLoaded && !folderSelection && !selectedPlaylistName) {
            dispatch(resetEnrichedFiles());
            setLibraryLoaded(false);
        }
    }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

    // Recompute graph data when enrichedFiles loads from cache
    // but groupYears/topGenres are empty (happens after app restart with cached data)
    React.useEffect(() => {
        if (enrichedFiles.length > 0 && groupYears.length === 0 && topGenres.length === 0) {
            dispatch(recomputeGraphData());
        }
    }, [enrichedFiles.length, groupYears.length, topGenres.length, dispatch]); // Run when these change

    // Actual min/max of BPM / loudness / release-year present in the library.
    // Sliders span these real bounds; hardcoded defaults are only a fallback for
    // an empty library (or a metric no track carries).
    // releaseDate is stored as YYYYMMDD (e.g. "20221216") or "YYYY-MM-DD" — pull
    // just the 4-digit year, matching how the row cells display it.
    const parseYear = (v) => { const m = String(v ?? '').match(/\d{4}/); return m ? parseInt(m[0], 10) : NaN; };
    const libBounds = React.useMemo(() => {
        const bpms = [], louds = [], years = [];
        for (const f of enrichedFiles) {
            const t = f?.result?.track;
            if (!t) continue;
            const b = parseFloat(t.bpm); if (!isNaN(b) && b > 0) bpms.push(b);
            // LUFS is a headroom-below-0 measure and can't exceed 0. Drop any
            // out-of-domain (positive) value so one garbage reading doesn't
            // stretch the slider's upper bound to +100.
            const l = parseFloat(t.loudness); if (!isNaN(l) && l <= 0) louds.push(l);
            const y = parseYear(t.releaseDate); if (!isNaN(y) && y > 0) years.push(y);
        }
        // A slider needs max > min; pad by 1 when the library has a single value.
        const span = (arr, dMin, dMax) => {
            if (!arr.length) return [dMin, dMax];
            let lo = Math.floor(Math.min(...arr)), hi = Math.ceil(Math.max(...arr));
            if (hi <= lo) hi = lo + 1;
            return [lo, hi];
        };
        // Belt-and-braces: even after filtering, ceil() of a value like -0.3
        // rounds to 0, and the single-value pad could push hi to +1 — so hard
        // cap the loudness ceiling at 0 and keep min strictly below it.
        const loud = span(louds, -30, 0);
        if (loud[1] > 0) loud[1] = 0;
        if (loud[0] >= loud[1]) loud[0] = loud[1] - 1;
        return {
            bpm: span(bpms, 60, 200),
            loudness: loud,
            year: span(years, 1980, new Date().getFullYear()),
        };
    }, [enrichedFiles]);

    // One-line Cloud Backup notice in the Files header ("N tracks aren't
    // backed up") for a signed-in account with backup OFF on
    // this machine — see shouldShowLibraryBackupLine. Deliberately a sentence
    // with the count, not the cloud column: with backup off every row would
    // carry the same slash, which is noise, not status. Hidden for good on
    // close (durable per account) and the moment backup turns on. Clicking
    // opens the Backup page for anyone entitled, the GOAT wall (with the
    // count) for a free account.
    const { canAccess: canBackup, requireUpgrade: requireBackupUpgrade } = useTierGate('cloud-backup');
    const backupOwnerId = useSelector((s) => s.auth?.user?.id) || null;
    const [backupLineHiddenFor, setBackupLineHiddenFor] = React.useState(null);
    const localTrackCount = React.useMemo(() => {
        let n = 0;
        for (const f of enrichedFiles) {
            const fp = f?.file || '';
            if (!fp || fp.startsWith('online://') || fp.startsWith('http://') || fp.startsWith('https://')) continue;
            if (f?.result?.fileNotFound === true) continue;
            n += 1;
        }
        return n;
    }, [enrichedFiles]);
    const showBackupLine = filesExpanded
        && backupLineHiddenFor !== backupOwnerId
        && shouldShowLibraryBackupLine({
            signedIn: backupMarks.signedIn,
            settings: backupMarks.settings,
            localTracks: localTrackCount,
            running: backupRun.running,
        });
    const backupLineShownRef = React.useRef(null);
    React.useEffect(() => {
        if (!showBackupLine || backupLineShownRef.current === backupOwnerId) return;
        backupLineShownRef.current = backupOwnerId;
        track('backup_offer_shown', { trigger: 'library_line', tier_can_access: canBackup });
    }, [showBackupLine, backupOwnerId, canBackup]);
    const onBackupLineClick = (e) => {
        e.stopPropagation();
        track('backup_offer_accepted', { trigger: 'library_line', to: canBackup ? 'backup_page' : 'paywall' });
        if (canBackup) {
            track('backup_page_opened', { from: 'library_line' });
            dispatch(setActiveTab('backup'));
        } else {
            requireBackupUpgrade({ total: localTrackCount });
        }
    };
    const onBackupLineHide = (e) => {
        e.stopPropagation();
        track('backup_offer_dismissed', { trigger: 'library_line' });
        setBackupLineHiddenFor(backupOwnerId);
        window.electron?.backup?.setEnabled?.({ backupLibraryLineDismissed: true })?.catch?.(() => {});
    };

    // Keep the slider ranges pinned to the library's real bounds until the user
    // actually drags a slider. The library streams in progressively (paged load),
    // so bounds computed at first-populate would reflect only a partial library;
    // re-syncing on every libBounds change tracks the full range as it fills.
    // Once the user adjusts a filter (userAdjustedFiltersRef), we stop touching it.
    const userAdjustedFiltersRef = React.useRef(false);
    React.useEffect(() => {
        if (userAdjustedFiltersRef.current || enrichedFiles.length === 0) return;
        setBpmMin(libBounds.bpm[0]); setBpmMax(libBounds.bpm[1]);
        setLoudnessMin(libBounds.loudness[0]); setLoudnessMax(libBounds.loudness[1]);
        setReleaseDateMin(libBounds.year[0]); setReleaseDateMax(libBounds.year[1]);
    }, [enrichedFiles.length, libBounds]);

    // Auto-load library from SQLite on mount if no files are loaded
    const [libraryLoaded, setLibraryLoaded] = React.useState(false);
    const [libraryLoading, setLibraryLoading] = React.useState(false);
    // Tracks whether this component is still mounted. The progressive load below
    // streams across many frames; if the user leaves the Library tab (CategoryView
    // unmounts it) we stop dispatching/setState. This must NOT be tied to the load
    // effect's cleanup — that fires on every re-render — so it lives in its own
    // unmount-only effect.
    const loadMountedRef = React.useRef(true);
    React.useEffect(() => {
        loadMountedRef.current = true;
        return () => { loadMountedRef.current = false; };
    }, []);

    // Prime the in-memory thumbnail store (utils/artThumbStore) with every
    // row's cover so scrolling paints art synchronously from RAM — the same
    // architecture that keeps native players pop-in-free. Each landed batch
    // bumps artVersion, which rides through rowProps and re-renders the
    // visible rows. While the backend warmer is still extracting art on a
    // fresh install, re-primes every 15s until nothing is left unknown.
    const [artVersion, setArtVersion] = React.useState(0);
    const artPrimeStartedRef = React.useRef(false);
    React.useEffect(() => {
        if (artPrimeStartedRef.current || enrichedFiles.length === 0) return;
        artPrimeStartedRef.current = true;
        const ids = enrichedFiles.map((f) => f.result?.track?.id).filter((id) => id != null);
        let rounds = 0;
        const prime = async () => {
            if (!loadMountedRef.current) return;
            const unknown = await primeArtThumbs(ids, () => {
                if (loadMountedRef.current) setArtVersion((v) => v + 1);
            });
            if (unknown > 0 && rounds++ < 40 && loadMountedRef.current) setTimeout(prime, 15000);
        };
        // No cleanup on purpose: enrichedFiles changes identity on every
        // hydration/backfill dispatch, and a cleanup here would cancel the
        // pending timer while the started-guard blocks rescheduling — the
        // prime would never run. Unmount abort is loadMountedRef's job.
        setTimeout(prime, 800);
    }, [enrichedFiles]);

    // Defer rendering the heavy track list until after the spinner has had
    // a chance to paint. The map at the bottom of this component creates
    // tens of thousands of DOM nodes for any non-trivial library (no
    // virtualization), so the initial mount commit could block the
    // renderer for 1-3 seconds — exactly the freeze the user sees on every
    // click into the Library tab. Two rAFs guarantees the placeholder is
    // both rendered AND painted before we flip the flag and trigger the
    // heavy commit. From the user's POV: click → spinner instantly →
    // wait → list. No frozen click anymore.
    const [listReady, setListReady] = React.useState(false);
    // Concurrent-render transition. setListReady runs inside startTransition
    // so the heavy listReady=true render is non-urgent: React renders it in
    // chunks in the background, KEEPS the spinner UI on screen until done,
    // then swaps to the full UI in one paint. `isPending` is true during
    // the transition — used in the early-return below to keep the spinner
    // visible. Without this, the listReady flip caused a blocking commit
    // where the spinner disappeared, then the UI froze, then the files
    // showed — the "Freeze 3" the user reported.
    const [isPending, startTransition] = useTransition();
    // Charts (BarChart, PieChart from Recharts) are heavy at mount — they do
    // significant SVG layout work. Delaying their mount by one more paint
    // frame after the row list keeps the listReady→full-list commit
    // unencumbered. From the user's POV: spinner → row list visible →
    // a beat later → charts visible. No single React commit has to render
    // both the rows AND Recharts simultaneously.
    const [chartsReady, setChartsReady] = React.useState(false);
    React.useEffect(() => {
        if (!listReady) return undefined;
        let r2 = 0;
        const r1 = requestAnimationFrame(() => {
            r2 = requestAnimationFrame(() => setChartsReady(true));
        });
        return () => {
            cancelAnimationFrame(r1);
            if (r2) cancelAnimationFrame(r2);
        };
    }, [listReady]);
    React.useEffect(() => {
        let raf2 = 0;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                // Mark as transition so the heavy render is non-blocking.
                startTransition(() => setListReady(true));
            });
        });
        return () => {
            cancelAnimationFrame(raf1);
            if (raf2) cancelAnimationFrame(raf2);
        };
    }, []);

    // Context-menu "Add to Playlist": opens the shared AddToPlaylistModal in
    // bulk mode. Targets = one path for a single-row right-click, the whole
    // selection for a multi-select right-click.
    const [ctxPlaylistPicker, setCtxPlaylistPicker] = React.useState(false);
    const [ctxPlaylistTargets, setCtxPlaylistTargets] = React.useState([]);
    const [cleanupModal, setCleanupModal] = React.useState(null); // { groups: [{ dir, paths[] }] } | null
    const [cleanupBusy, setCleanupBusy] = React.useState(false);
    const [cleanupBusyMsg, setCleanupBusyMsg] = React.useState('Working…');
    // When set, shows a review card inside the modal instead of the group list.
    // { folder, matched: [{oldPath,newPath}], unmatched: [oldPath], source: 'group'|'all' }
    const [relocateReview, setRelocateReview] = React.useState(null);
    React.useEffect(() => {
        if (!cleanupModal) return;
        const onKey = (e) => {
            if (e.key === 'Escape' && !cleanupBusy) setCleanupModal(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cleanupModal, cleanupBusy]);
    React.useEffect(() => {
        if (!fillPicker) return;
        const onKey = (e) => { if (e.key === 'Escape') setFillPicker(null); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [fillPicker]);
    // Re-verify missing files whenever an analysis pass finishes. Analyze
    // rebuilds the enriched rows (a full Refresh even resets them), which used
    // to drop the fileNotFound flags — leaving not-on-this-device tracks
    // looking playable again.
    const wasProcessingRef = React.useRef(false);
    React.useEffect(() => {
        const finished = wasProcessingRef.current && !infoIsProcessing;
        wasProcessingRef.current = infoIsProcessing;
        if (!finished) return;
        const paths = (store.getState().info.enrichedFiles || [])
            .map(f => f.file)
            .filter(p => p && !p.startsWith('online://') && !p.startsWith('http://') && !p.startsWith('https://'));
        if (paths.length === 0) return;
        window.electron.db.tracks.checkMissingFiles(paths)
            .then(missing => {
                if (!Array.isArray(missing)) return;
                dispatch(markEnrichedFilesMissing({ missing, checked: paths }));
            })
            .catch(e => console.warn('[Library] Post-analyze missing-file check failed:', e.message));
    }, [infoIsProcessing, dispatch]);
    React.useEffect(() => {
        if (enrichedFiles.length === 0 && !infoIsProcessing && !libraryLoaded) {
            setLibraryLoaded(true);
            setLibraryLoading(true);
            // The Library is a local-file manager — cloud/streaming tracks
            // (online://, http(s)://) can't be tagged, analyzed or migrated, so
            // they're excluded from this view entirely.
            const isCloudPath = (p) => !p || p.startsWith('online://') || p.startsWith('http://') || p.startsWith('https://');
            const toEnriched = (tracks) => tracks.map(t => ({
                file: t.file || t.filePath,
                fileName: t.fileName || (t.file || t.filePath || '').split('/').pop(),
                // Carry the DB library position so the "#" column, custom-order
                // default, and drag-reorder can read/update it.
                sortOrder: t.sortOrder ?? null,
                result: { track: t.track || {} }
            }));
            // Defer the load to the next frame so the spinner paints first.
            requestAnimationFrame(() => {
                (async () => {
                    try {
                        const tracks = await window.electron.db.tracks.getAll();
                        if (!loadMountedRef.current) return;
                        if (!tracks || tracks.length === 0) return;

                        const enriched = toEnriched(tracks).filter(e => !isCloudPath(e.file));
                        // Single hydrate; addEnrichedFilesBatch also tallies the year/
                        // genre charts in the same pass, so no separate recompute here.
                        dispatch(addEnrichedFilesBatch(enriched));
                        // "#" (track-order) ascending is the default active sort, so
                        // drag-reorder is available immediately without a header click.
                        dispatch(setActiveColumn('sortOrder'));
                        dispatch(setSortDirection('asc'));
                        setLibraryLoading(false);

                        const allLocalPaths = enriched.map(e => e.file);
                        const anyMissingAnalysis = enriched.some(e => !e.result.track?.bpm || !e.result.track?.key);

                        // Flag missing files so the UI shows the red ! icon. Runs in
                        // the background — we don't block render on fs.stat of 10k files.
                        if (allLocalPaths.length > 0) {
                            window.electron.db.tracks.checkMissingFiles(allLocalPaths)
                                .then(missing => {
                                    if (!loadMountedRef.current || !Array.isArray(missing)) return;
                                    // Dispatch even with zero missing — `checked` clears
                                    // stale flags for files that came back (remounted drive).
                                    dispatch(markEnrichedFilesMissing({ missing, checked: allLocalPaths }));
                                    if (missing.length === 0) return;
                                    // Default order keeps not-found tracks at the bottom. Stable sort
                                    // preserves the sort_order sequence within each group.
                                    const missingSet = new Set(missing);
                                    const cur = store.getState().info.sortedFiles;
                                    const reSorted = [...cur].sort((a, b) =>
                                        (missingSet.has(a.file || a.filePath) ? 1 : 0) - (missingSet.has(b.file || b.filePath) ? 1 : 0)
                                    );
                                    dispatch(setSortedFiles(reSorted));
                                })
                                .catch(e => console.warn('[Library] Missing-file check failed:', e.message));
                        }

                        // Some local tracks lack BPM/key — backfill from Rekordbox in the
                        // background (off the critical path), then patch the rows in place.
                        if (anyMissingAnalysis) {
                            try {
                                const backfill = await window.electron.db.tracks.backfillFromRekordbox();
                                if (loadMountedRef.current && backfill?.updated > 0) {
                                    const refreshed = await window.electron.db.tracks.getAll();
                                    if (loadMountedRef.current && Array.isArray(refreshed) && refreshed.length > 0) {
                                        dispatch(updateEnrichedFilesBatch(toEnriched(refreshed).filter(e => !isCloudPath(e.file))));
                                        dispatch(recomputeGraphData());
                                    }
                                }
                            } catch (e) {
                                console.warn('[Library] Rekordbox backfill failed:', e.message);
                            }
                        }
                    } catch (e) {
                        console.warn('[Library] Auto-load from DB failed:', e.message);
                    } finally {
                        if (loadMountedRef.current) setLibraryLoading(false);
                    }
                })();
            });
        }
    }, [enrichedFiles.length, infoIsProcessing, libraryLoaded, dispatch]);

    // Resolve duo-aware artist splits for the loaded library. One batched call
    // to the music-DB knowledge base decides, per "A & B" string, whether it's a
    // real act (keep) or a collab (split). Cheap: only multi-artist strings are
    // sent, deduped; the result is held in state and reused for every row.
    React.useEffect(() => {
        if (!enrichedFiles.length) return undefined;
        let cancelled = false;
        (async () => {
            const raws = [...new Set(enrichedFiles.map((f) => f.result?.track?.artist).filter(Boolean))];
            const candidates = collectResolveCandidates(raws);
            if (candidates.length === 0) {
                if (!cancelled) setArtistPartsMap(new Map(raws.map((r) => [r, [r]])));
                return;
            }
            try {
                const token = await ensureToken(dispatch, store.getState);
                if (cancelled || !token) return;
                const res = await window.electron.djpal.resolveArtistNames(token, candidates);
                if (cancelled) return;
                setArtistPartsMap(computeArtistParts(raws, res?.resolved || []));
            } catch (e) { /* leave artists unsplit on failure */ }
        })();
        return () => { cancelled = true; };
    }, [enrichedFiles.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-analyze when triggered from playlist sidebar
    React.useEffect(() => {
        if (autoAnalyze && infoFiles.length > 0 && !infoIsProcessing) {
            dispatch(setInfoAutoAnalyze(false));
            handleAnalyzeInfo();
        }
    }, [autoAnalyze, infoFiles, infoIsProcessing]);

    // Helper function to truncate path from the start
    const getTruncatedPath = (path) => {
        if (!shouldTruncatePath || !folderPathRef.current) {
            return formatPath(path);
        }

        const formatted = formatPath(path);
        const containerWidth = folderPathRef.current.offsetWidth;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

        const textWidth = context.measureText(formatted).width;

        // If text fits, don't truncate
        if (textWidth <= containerWidth - 30) { // 30px for padding
            return formatted;
        }

        // Otherwise, truncate from the beginning
        const ellipsis = '...';
        const parts = formatted.split('/');
        let truncated = ellipsis + parts[parts.length - 1];

        // Add more parts from the end until we run out of space
        for (let i = parts.length - 2; i >= 0; i--) {
            const candidate = ellipsis + parts.slice(i).join('/');
            if (context.measureText(candidate).width <= containerWidth - 30) {
                truncated = candidate;
            } else {
                break;
            }
        }

        return truncated;
    };

    // Check if path needs truncation on mount and resize
    React.useEffect(() => {
        if (!infoFiles.length || !folderPathRef.current) return;

        const checkTruncation = () => {
            if (!folderPathRef.current) return;

            const containerWidth = folderPathRef.current.offsetWidth;
            const formatted = formatPath(infoFiles[0]);
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
            const textWidth = context.measureText(formatted).width;

            setShouldTruncatePath(textWidth > containerWidth - 30);
        };

        checkTruncation();
        window.addEventListener('resize', checkTruncation);

        return () => window.removeEventListener('resize', checkTruncation);
    }, [infoFiles, actionsExpanded]);

    // Modern color palette for charts
    const COLORS = designV2 ? [
        'var(--v2-chart-1)', 'var(--v2-chart-2)', 'var(--v2-chart-3)', 'var(--v2-chart-4)', 'var(--v2-chart-5)', 'var(--v2-chart-6)', 'var(--v2-chart-7)', 'var(--v2-chart-8)',
    ] : [
        '#4CC2F1', // Bright cyan
        '#FF6B9D', // Pink
        '#00D9A5', // Teal/Green
        '#FFB84D', // Orange
        '#A78BFA', // Purple
        '#FF5757', // Red
        '#4ADE80', // Green
        '#FBBF24', // Yellow
        '#818CF8', // Indigo
        '#F472B6', // Rose
    ];

    const {
        enableRecursive,
        backupOriginalFiles,
        overwriteExistingFixedFiles,
        logOnlyChanges,
        concurrencyLevel,
        keyNotation,
        showFullDate,
        theme,
    } = useSelector((state) => state.settings);
    const isLight = theme === 'light';
    // Ghost-button surface: dark keeps the original faint-white; light uses a
    // faint-black so the button is visible on a light background.
    const clearBtnBg = isLight ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)';
    const clearBtnBorder = isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.1)';
    const clearBtnColor = isLight ? '#555' : '#999';

    // Handlers
    const handleChooseFiles = async () => {
        const chosenFiles = await window.electron.openFile(['wav']);
        if (chosenFiles && chosenFiles.length > 0) {
            dispatch(setInfoFiles(chosenFiles));
        }
    };

    const handleChooseDir = async () => {
        const chosenDir = await window.electron.openDir();
        if (chosenDir && chosenDir.length > 0) {
            const dirPath = Array.isArray(chosenDir) ? chosenDir[0] : chosenDir;
            setSelectedPlaylistName(null);
            setFolderSelection({ path: dirPath, scanning: true }); // card (path + actions)
            try {
                // Async scan (non-blocking, filters ._ / dotfiles), then hand the
                // analyzer the FILE LIST — so it never does a blocking readdir on
                // the (possibly external) drive, and the first track appears fast.
                const filePaths = await window.electron.scanAudioFolder(dirPath);
                if (!filePaths || filePaths.length === 0) { toast.warning('No audio files found in that folder'); setFolderSelection(null); return; }
                setFolderSelection({ path: dirPath });
                // Paint the folder's files INSTANTLY (filename-derived title/artist),
                // so the list appears with no delay/freeze; analysis then fills in
                // BPM/key/etc. in place (keepListOnAnalyzeRef stops it re-clearing).
                dispatch(resetEnrichedFiles());
                dispatch(addEnrichedFilesBatch(filePaths.map((p, i) => ({
                    file: p, fileName: p.split(/[\\/]/).pop(), sortOrder: i, result: { track: {} },
                }))));
                appendOnAnalyzeRef.current = false;
                keepListOnAnalyzeRef.current = true;
                dispatch(setInfoFiles(filePaths));
                dispatch(setInfoAutoAnalyze(true)); // analyze in place → fills metadata
            } catch (err) { console.warn('[Library] scan folder failed', err); toast.error('Failed to scan folder'); setFolderSelection(null); }
        }
    };

    // Persist the currently-shown (analyzed folder) tracks — they already carry
    // metadata from the analyze pass.
    const persistShownTracks = async () => {
        const files = (store.getState().info.enrichedFiles || []).filter((f) => f && f.file);
        if (files.length) {
            // skipStats: these were just analyzed off a possibly-external drive; a
            // statSync per file here would freeze the main process.
            await window.electron.db.tracks.batchSave(
                files.map((f) => ({ file: f.file, filePath: f.file, fileName: f.fileName, track: f.result?.track || {} })),
                { skipStats: true }
            );
        }
        return files;
    };

    // "Add to library": save the shown folder tracks, then return to the library.
    const handleAddFolderToLibrary = async () => {
        try {
            const files = await persistShownTracks();
            if (!files.length) { toast.warning('No tracks to add'); return; }
            toast.success(files.length === 1 ? 'Added 1 track to your library' : `Added ${files.length} tracks to your library`);
            setFolderSelection(null);
            dispatch(setInfoFiles([]));
            dispatch(resetEnrichedFiles());
            setLibraryLoaded(false); // reload the full library (now includes the added tracks)
        } catch (err) { console.warn('[Library] add folder failed', err); toast.error('Failed to add to library'); }
    };

    // "Create playlist from folder": persist the shown tracks, then open the app's
    // normal create-playlist popup pre-named after the folder.
    const handleCreatePlaylistFromFolder = async () => {
        try {
            const files = await persistShownTracks();
            if (!files.length) { toast.warning('No tracks to add'); return; }
            const folderName = String(folderSelection?.path || infoFiles[0] || '').split(/[\\/]/).filter(Boolean).pop() || 'New Playlist';
            setCreatePlaylistModal({ filePaths: files.map((f) => f.file), defaultName: folderName, count: files.length });
        } catch (err) { console.warn('[Library] create playlist from folder failed', err); toast.error('Failed to prepare tracks'); }
    };

    const handleResetFilePath = () => {
        dispatch(resetInfoFilePath());
        dispatch(resetEnrichedFiles());
        setSelectedPlaylistName(null);
    }

    // When true, the next analyze run APPENDS to the current library instead of
    // replacing it — set on file-drop so dropping files adds them (dedup by path
    // in addEnrichedFilesBatch), rather than showing only the dropped files.
    const appendOnAnalyzeRef = React.useRef(false);
    // Snapshot of existing file paths at drop time, so once analysis completes we
    // persist only the NEW tracks (works for dropped files AND folders, which the
    // analyzer expands into their audio files).
    const preDropPathsRef = React.useRef(null);
    // When true, the next analyze run does NOT reset the current list — used by
    // Select Folder, which paints the folder's files instantly (filename-derived)
    // and then lets analysis fill in metadata in place (no blank/flash).
    const keepListOnAnalyzeRef = React.useRef(false);
    // { filePaths, defaultName, count } | null — drives the "create playlist from
    // folder" popup (reuses the app's normal AddToPlaylistModal).
    const [createPlaylistModal, setCreatePlaylistModal] = React.useState(null);
    // { path, filePaths } | null — a folder chosen via "Select Folder". It shows a
    // card with actions and does NOT load the folder's tracks into the main list.
    const [folderSelection, setFolderSelection] = React.useState(null);

    // Drag and drop handlers. Only real OS file drags show the "drop files here"
    // overlay — an internal row-reorder drag carries 'text/plain', not 'Files',
    // so it's ignored here and handled by the row's own drag handlers instead.
    const isFileDrag = (e) => {
        const types = e.dataTransfer && e.dataTransfer.types;
        return !!types && Array.from(types).includes('Files');
    };

    // Append the given local paths to the library: analyze them (fills metadata)
    // and persist the new ones (see the append-analyze completion). Does NOT reset
    // the current view. Used by file/folder drops and the "Add to library" button.
    const addFilesToLibrary = (paths) => {
        const list = (paths || []).filter(Boolean);
        if (list.length === 0) return;
        appendOnAnalyzeRef.current = true;
        preDropPathsRef.current = new Set((store.getState().info.enrichedFiles || []).map((f) => f.file));
        dispatch(setInfoFiles(list));
        dispatch(setInfoAutoAnalyze(true));
        toast.info('Adding to your library…');
    };
    const handleDragOver = (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = async (e) => {
        if (!isFileDrag(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const droppedFiles = Array.from(e.dataTransfer.files)
            .map(file => window.electron.getPathForFile(file))
            .filter(Boolean);
        addFilesToLibrary(droppedFiles);
    };

    // Safety net: always clear the file-drop overlay when any drag ends anywhere,
    // so it can never get stuck on (e.g. a drop that landed outside the zone).
    React.useEffect(() => {
        const clear = () => setIsDragging(false);
        window.addEventListener('dragend', clear);
        window.addEventListener('drop', clear);
        return () => { window.removeEventListener('dragend', clear); window.removeEventListener('drop', clear); };
    }, []);

    const handleClearLogs = () => {
        dispatch(clearLogs());
        dispatch(resetEnrichedFiles());
        dispatch(resetInfoFilePath());
        window.electron.resetFilePath(); // Reset the file path in the backend too
    }

    const handleRefresh = () => {
        handleAnalyzeInfo(); // Run analyze again
    }

    // One predicate decides whether a track still has a fillable gap — the
    // button's count, the picker's per-field counts and the run all read it, so
    // none of them can drift into counting tracks a run could never fill
    // (missing files, 30s Apple previews, fully-known Mixpedia tracks) — that
    // bug left a number that never reached 0.
    const FILL_API_TTL_MS = 30 * 24 * 60 * 60 * 1000; // enricher's 30-day API cooldown

    // What the button counts and what the picker starts from: the visible
    // columns, plus cover art (no column of its own, but a missing cover is a
    // real gap).
    const fillColumnDefaults = React.useMemo(() => ({
        bpm: analyzeBpm,
        key: analyzeKey,
        loudness: analyzeLoudness,
        energy: analyzeEnergy,
        isCompatible: analyzeCompatibility,
        genre: analyzeGenre,
        releaseDate: analyzeDate,
        label: analyzeLabel,
        cover: true,
    }), [analyzeBpm, analyzeKey, analyzeLoudness, analyzeEnergy, analyzeCompatibility,
         analyzeGenre, analyzeDate, analyzeLabel]);
    const fillSelection = fillColumnDefaults;

    // Is this one field still empty on this track? No judgement about whether a
    // run could fill it — see fillFieldFillable.
    const fillFieldEmpty = (f, field) => {
        const fp = f.file || '';
        const t = f.result?.track;
        const isUrlTrack = /^https?:/i.test(fp);
        // The enricher deliberately skips key/compat on 30s Apple previews
        // (unreliable on short clips) — don't count them as missing.
        const isShortPreview = isUrlTrack && (fp.includes('/AudioPreview') || fp.includes('mzaf_'));
        switch (field) {
            case 'bpm': return !t?.bpm;
            case 'key': return !t?.key && !isShortPreview;
            case 'loudness': return !t?.loudness;
            // A score without its per-section shape (analysed before shapes
            // existed) still counts — the next energy fill backfills it.
            case 'energy': return t?.energy == null || !Array.isArray(t?.energySections);
            case 'isCompatible': return t?.isCompatible === undefined && !isShortPreview;
            case 'genre': return !t?.styles?.length && !t?.genres?.length && !t?.genre;
            case 'releaseDate': return !t?.releaseDate;
            case 'label': return !t?.label;
            // `hasCover` is the real stored-cover flag; `t.cover` is the
            // /album-art?id= route for local files and is never empty, so it
            // can't be used here.
            case 'cover': return !t?.hasCover;
            default: return false;
        }
    };

    // Can a run still close that gap? Mixpedia-sourced fields are additionally
    // gated by the enricher's 30-day cooldown and its "already fully known"
    // skip — otherwise such tracks wedge the count at a number no run moves.
    const fillFieldFillable = (f, field) => {
        if (!fillFieldEmpty(f, field)) return false;
        if (FILL_FIELD_SOURCE[field] !== 'api') return true;
        const t = f.result?.track;
        const apiRecent = t?.apiAttemptedAt && (Date.now() - t.apiAttemptedAt) < FILL_API_TTL_MS;
        const fullyKnown = !!(t?.djpalId && t?.hasCover);
        return !apiRecent && !fullyKnown;
    };

    const getFillNeeds = (f, selection = fillSelection) => {
        const fp = f.file || '';
        if (fp.startsWith('online://')) return { needsLocal: false, needsApi: false, missingFile: false, fillable: false, reprobe: false };
        const missingFile = f.result?.fileNotFound === true;
        const wants = (x) => selection[x.key] && fillFieldFillable(f, x.key);
        const needsLocal = FILL_FIELDS.some(x => x.source === 'local' && wants(x));
        // Cover art comes free with any Mixpedia match, so a missing cover is
        // itself a reason to hit the API — otherwise a track that already has
        // genre+date+label+bpm+key (e.g. imported from a DJ library) is never
        // sent, and its empty cover never gets filled.
        const needsApi = FILL_FIELDS.some(x => x.source === 'api' && wants(x));
        const needsAnything = needsLocal || needsApi;
        return {
            needsLocal,
            needsApi,
            missingFile,
            // What the button counts and the run is expected to fill.
            fillable: needsAnything && !missingFile,
            // Missing-file rows are excluded from the count but still re-sent
            // on every run: the backend re-checks the disk, so a file that
            // came back gets filled and un-flagged in that same run.
            reprobe: needsAnything && missingFile,
        };
    };

    // The picker's per-field "N missing" counts. Only walked while the picker is
    // open — enrichedFiles can be tens of thousands of rows.
    const fillFieldCounts = React.useMemo(() => {
        const counts = Object.fromEntries(FILL_FIELDS.map(x => [x.key, 0]));
        if (!fillPicker) return counts;
        enrichedFiles.forEach(f => {
            const fp = f.file || '';
            if (fp.startsWith('online://') || f.result?.fileNotFound === true) return;
            FILL_FIELDS.forEach(x => { if (fillFieldFillable(f, x.key)) counts[x.key]++; });
        });
        return counts;
    }, [fillPicker, enrichedFiles]); // eslint-disable-line react-hooks/exhaustive-deps

    // How many tracks the picker's current draft would send. Same predicate as
    // the button's own count, so the two never disagree.
    const fillPickerCount = React.useMemo(() => {
        if (!fillPicker) return 0;
        return enrichedFiles.filter(f => getFillNeeds(f, fillPicker.selection).fillable).length;
    }, [fillPicker, enrichedFiles]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clicking the button opens the picker; the run starts from its Fill button.
    const openFillPicker = () => {
        const emptyCount = enrichedFiles.filter(f => getFillNeeds(f).fillable).length;
        track('library_fill_empty_opened', { track_count: emptyCount });
        setFillPicker({ selection: { ...fillColumnDefaults } });
    };

    const handleFillEmpty = async (selection = fillSelection) => {
        const chosen = FILL_FIELDS.filter(x => selection[x.key]).map(x => x.key);
        // Walk the rows in the order the user is looking at — the list as it is
        // currently sorted and filtered — so the run works down from the top of
        // the visible list instead of the master array's arbitrary order.
        // Fillable tracks the current filter hides still run, appended at the
        // end, so narrowing the view never silently skips them.
        const visible = filteredFilesRef.current || [];
        const byPath = new Map(enrichedFiles.map(f => [f.file, f]));
        const seen = new Set();
        const ordered = [];
        visible.forEach(row => {
            const f = byPath.get(row.file) || row;
            if (f && f.file && !seen.has(f.file)) { seen.add(f.file); ordered.push(f); }
        });
        enrichedFiles.forEach(f => { if (f && f.file && !seen.has(f.file)) { seen.add(f.file); ordered.push(f); } });

        const fillable = [];
        const reprobe = [];
        ordered.forEach(f => {
            const n = getFillNeeds(f, selection);
            if (n.fillable) fillable.push(f);
            else if (n.reprobe) reprobe.push(f);
        });
        track('library_fill_empty', {
            track_count: fillable.length,
            missing_file_count: reprobe.length,
            fields: chosen.join(','),
            field_count: chosen.length,
        });

        const tracksToAnalyze = [...fillable, ...reprobe];
        if (tracksToAnalyze.length === 0) {
            toast.info('All tracks already have the requested data.');
            return;
        }

        const filePaths = tracksToAnalyze.map(f => f.file).filter(Boolean);
        const fileSet = new Set(filePaths);
        dispatch(setInfoIsProcessing(true));
        dispatch(setInfoProgress(0));

        const startTime = Date.now();

        const analyzing = createAnalyzingTracker(dispatch);
        let pendingUpdates = [];
        let flushTimeout = null;
        let lastProgressUpdate = 0;
        let summaryShown = false;

        // Outcome report — the old silent finish made a count that stays >0
        // (tracks with no Mixpedia match, files that failed analysis) look
        // like a broken button. Scope: the tracks the button advertised
        // (fillable) plus any missing file that reappeared this run. Files
        // still missing on disk are background reprobes the button never
        // counted — reporting them here made "Fill (5)" answer with
        // "Filled 3 of 45 — 40 missing on disk", which reads as a failure.
        const showRunSummary = () => {
            if (summaryShown) return;
            summaryShown = true;
            const after = store.getState().info.enrichedFiles.filter(x => fileSet.has(x.file));
            let filled = 0, stillMissing = 0, noMatch = 0, analysisFailed = 0;
            after.forEach(row => {
                if (row.result?.fileNotFound === true) { stillMissing++; return; }
                // Raw emptiness, not fillFieldFillable: every track in this run
                // just had apiAttemptedAt stamped, so the cooldown gate would
                // report every remaining API gap as filled.
                const stillEmpty = (x) => selection[x.key] && fillFieldEmpty(row, x.key);
                const localGap = FILL_FIELDS.some(x => x.source === 'local' && stillEmpty(x));
                const apiGap = FILL_FIELDS.some(x => x.source === 'api' && stillEmpty(x));
                if (localGap) analysisFailed++;
                else if (apiGap) noMatch++;
                else filled++;
            });
            track('library_fill_empty_completed', {
                filled,
                missing_files: stillMissing,
                analysis_failed: analysisFailed,
                no_match: noMatch,
            });
            const attempted = filled + analysisFailed + noMatch;
            const problems = [];
            if (analysisFailed) problems.push(`${analysisFailed} couldn't be analyzed`);
            if (noMatch) problems.push(`${noMatch} had no Mixpedia match`);
            if (attempted === 0) {
                toast.info('Nothing to fill — the remaining files are missing on disk.');
            } else if (problems.length === 0) {
                toast.success(`Filled ${filled} track${filled === 1 ? '' : 's'}.`);
            } else if (filled > 0) {
                // Mixed outcome — info (blue), not success (green): the wording
                // leads with the win, the color stays neutral about the caveat.
                toast.info(`Filled ${filled} of ${attempted} tracks — ${problems.join(', ')}.`);
            } else {
                toast.info(`No new data found — ${problems.join(', ')}.`);
            }
        };

        const flushPendingUpdates = () => {
            if (pendingUpdates.length > 0) {
                dispatch(updateEnrichedFilesBatch(pendingUpdates));
                pendingUpdates = [];
            }
            flushTimeout = null;
        };

        const scheduleFlush = () => {
            if (!flushTimeout) {
                flushTimeout = setTimeout(flushPendingUpdates, 100);
            }
        };

        const updateProgressFromBackend = async ({file, fileName, result, progress, isInitial, isUpdate, isLoading, isStart, isDone, fields, stageMessage, isPrefetch, batchFiles, isBatch, isCachedBatch}) => {
            // A file entered / left the worker pool: mark just that row, with
            // just the fields being computed for it.
            if (isStart) { analyzing.start(file, fields); setAnalysisStage(''); return; }
            if (isDone) analyzing.done(file);
            if (isPrefetch) setAnalysisStage(stageMessage || '');

            const now = Date.now();
            if (now - lastProgressUpdate > 50) {
                dispatch(setInfoProgress(progress));
                lastProgressUpdate = now;
            }

            if (isBatch && batchFiles) {
                if (isCachedBatch) {
                    dispatch(updateEnrichedFilesBatch(batchFiles));
                }
                // The enricher's opening "here are all the files" batch is for
                // a fresh scan; these rows already exist with their data, and
                // applying it would blank every one of them for the whole run.
            } else if (file && fileName && result) {
                pendingUpdates.push({file, fileName, result});
                scheduleFlush();
            }

            if (progress >= 100) {
                if (flushTimeout) {
                    clearTimeout(flushTimeout);
                    flushTimeout = null;
                }
                flushPendingUpdates();
                dispatch(setInfoProgress(100));
                analyzing.reset();
                setAnalysisStage('');
                dispatch(recomputeGraphData());
                // Sync the rendered list with the master enrichedFiles so covers
                // (etc.) fetched this run appear without needing a tab remount.
                dispatch(syncSortedFromEnriched());
                showRunSummary();
                await waitRemaining(startTime);
                dispatch(setInfoIsProcessing(false));
            }
        };

        // 'cover' isn't a backend attribute — a cover rides along with any
        // Mixpedia match — so it never reaches the enricher's attribute list.
        const attributes = chosen.filter(k => k !== 'cover');

        // Get a valid DJPal token for the public API endpoint — but ONLY when
        // the pick includes something Mixpedia supplies. Without this, an
        // energy-only run still opened with a lookup pass over every track in
        // the run (minutes of nothing happening on a big library, and an API
        // hammering nobody asked for) before computing a single value.
        const wantsApi = chosen.some(k => FILL_FIELD_SOURCE[k] === 'api');
        let djpalToken = null;
        if (wantsApi) {
            try {
                djpalToken = await ensureToken(dispatch, store.getState);
            } catch (e) {
                console.warn('[FillEmpty] Could not get DJPal token — API enrichment will be skipped:', e.message);
            }
        }

        window.electron.analyzeLibrary(
            filePaths,
            updateProgressFromBackend,
            false,
            attributes,
            djpalToken
        );
    }

    const handleStopProcessing = () => {
        dispatch(setInfoIsProcessing(false));
        dispatch(clearAnalyzingFiles());
        setAnalysisStage('');
        window.electron.cancelAnalyzeInfo();
    };

    const toggleChartsVisibility = () => {
        dispatch(setChartsVisible(!chartsVisible))
    }


    // When the KB says a track's "A & B" artist is a collab, hand the now-playing
    // player the split artists[] so its MultiArtistText renders separate,
    // individually-clickable artists (instead of one combined, non-resolving
    // link). Returns {} for real acts / unresolved → player shows `singer` whole.
    const splitArtistsField = (artistStr) => {
        const parts = artistStr && artistPartsMap ? artistPartsMap.get(artistStr) : null;
        return parts && parts.length > 1 ? { artists: parts.map((name) => ({ name })) } : {};
    };

    const handleTrackDoubleClick = async (file) => {
        const musicSrc = await window.electron.getAudioUrl(file.file);
        const newTrack = {
            name: file.result.trackName,
            singer: file.result?.track?.artist || 'Unknown Artist',
            cover: resolveCover(file.result?.track?.cover),
            musicSrc
        };

        // Play the track in MusicPlayer
        dispatch(setAudioList([newTrack]));

        // Optional: Set the MusicPlayer to autoplay after adding the track
        dispatch(toggleAutoPlay(true));
    };


    const handleAnalyzeInfo = async () => {
        window.electron.analytics.track('library_scan');
        // A file-drop appends to the current library; Select Folder already painted
        // its files (keepList) and analysis just fills them in; everything else
        // (Refresh) replaces the list.
        const append = appendOnAnalyzeRef.current;
        appendOnAnalyzeRef.current = false;
        const keepList = keepListOnAnalyzeRef.current;
        keepListOnAnalyzeRef.current = false;
        if (!append && !keepList) dispatch(resetEnrichedFiles()); // Clear old data before new analysis
        dispatch(clearLogs()); // Clear logs
        dispatch(setInfoIsProcessing(true));
        dispatch(setInfoProgress(0));

        const startTime = Date.now(); // Record the start time

        // Throttle updates to avoid blocking UI - batch updates every 100ms
        const analyzing = createAnalyzingTracker(dispatch);
        let pendingUpdates = [];
        let flushTimeout = null;
        let lastProgressUpdate = 0;

        const flushPendingUpdates = () => {
            if (pendingUpdates.length > 0) {
                // Dispatch all pending updates as a batch
                dispatch(updateEnrichedFilesBatch(pendingUpdates));
                pendingUpdates = [];
            }
            flushTimeout = null;
        };

        const scheduleFlush = () => {
            if (!flushTimeout) {
                flushTimeout = setTimeout(flushPendingUpdates, 100); // Flush every 100ms max
            }
        };

        // This function will update progress based on backend reporting
        const updateProgressFromBackend = async ({file, fileName, result, progress, isInitial, isUpdate, isLoading, isStart, isDone, fields, stageMessage, isPrefetch, batchFiles, isBatch, isCachedBatch}) => {
            // A file entered / left the worker pool: mark just that row, with
            // just the fields being computed for it.
            if (isStart) { analyzing.start(file, fields); setAnalysisStage(''); return; }
            if (isDone) analyzing.done(file);
            if (isPrefetch) setAnalysisStage(stageMessage || '');

            // Throttle progress bar updates to every 50ms
            const now = Date.now();
            if (now - lastProgressUpdate > 50) {
                dispatch(setInfoProgress(progress));
                lastProgressUpdate = now;
            }

            if (isBatch && batchFiles) {
                if (isCachedBatch) {
                    dispatch(updateEnrichedFilesBatch(batchFiles));
                } else {
                    // Initial load - add all files as loading
                    dispatch(addEnrichedFilesBatch(batchFiles));
                }
            } else if (file && fileName && result) {
                pendingUpdates.push({file, fileName, result});
                scheduleFlush();
            }

            // Handle completion
            if (progress >= 100) {
                // Flush any remaining updates immediately
                if (flushTimeout) {
                    clearTimeout(flushTimeout);
                    flushTimeout = null;
                }
                flushPendingUpdates();

                // Final progress update
                dispatch(setInfoProgress(100));
                analyzing.reset();
                setAnalysisStage('');

                // Compute charts now that all data is loaded
                dispatch(recomputeGraphData());
                // Sync the rendered list with the master enrichedFiles so covers
                // (etc.) fetched this run appear without needing a tab remount.
                dispatch(syncSortedFromEnriched());

                await waitRemaining(startTime);
                dispatch(setInfoIsProcessing(false));  // Processing is done
                // A drag-add is not a folder "selection" — clear the queue so the
                // "Selected Folder" card doesn't appear for dropped tracks, and
                // persist the newly-added tracks (with their analyzed metadata) so
                // they survive a reload (append at the end via sort_order = MAX+1).
                if (append) {
                    dispatch(setInfoFiles([]));
                    dispatch(setInfoAutoAnalyze(false));
                    try {
                        const seen = preDropPathsRef.current || new Set();
                        const fresh = (store.getState().info.enrichedFiles || [])
                            .filter((f) => f && f.file && !seen.has(f.file));
                        if (fresh.length) {
                            window.electron.db.tracks.batchSave(
                                fresh.map((f) => ({ file: f.file, filePath: f.file, fileName: f.fileName, track: f.result?.track || {} }))
                            );
                        }
                    } catch (err) { console.warn('[Library] persist added tracks failed', err); }
                    preDropPathsRef.current = null;
                }
            }
        };

        // A plain Analyze run learns everything it can about these tracks —
        // hiding a column is a VIEW choice and must not silently stop data being
        // collected (you'd find the hole later, unhiding the column). The
        // Mixpedia fields (genre/date/label/cover) all ride one lookup, so they
        // cost nothing extra. Energy is the exception: a ~10 s/track pass that
        // is opted into per run in the Fill Empty Values dialog.
        const attributes = FILL_FIELDS
            .map((f) => f.key)
            .filter((k) => k !== 'cover' && (k !== 'energy' || analyzeEnergy));

        // Filter out online:// tracks (no audio). HTTP(S) URLs (preview audio) can be analyzed by ffmpeg.
        const analyzableFiles = infoFiles.filter(f => !f.startsWith('online://'));

        if (analyzableFiles.length === 0) {
            // No analyzable tracks — complete immediately
            dispatch(setInfoProgress(100));
            dispatch(recomputeGraphData());
            dispatch(setInfoIsProcessing(false));
            return;
        }

        // Call the backend function, passing in the directory and progress callback
        window.electron.analyzeLibrary(
            analyzableFiles,
            updateProgressFromBackend,
            enableRecursive,
            attributes
        );
    };

    // Filter files based on BPM range and selected keys
    const filteredFiles = React.useMemo(() => {
        return sortedFiles.filter(file => {
            const track = file?.result?.track;

            // Hide tracks whose local file no longer exists on disk (toggle).
            if (hideMissingFiles && file?.result?.fileNotFound === true) {
                return false;
            }

            // Cloud-backup chips. Cloud tracks are outside backup's scope —
            // hidden under either chip, matching their empty cell in the
            // cloud column.
            if (backupFilter && backupMarks.active && backupMarks.paths) {
                const fp = file?.file || '';
                if (/^(online:|https?:)/i.test(fp)) return false;
                if (backupMarks.paths.has(fp) !== (backupFilter === 'stored')) return false;
            }

            // Filter by search term
            if (searchTerm && searchTerm.trim() !== '') {
                const lowerSearchTerm = searchTerm.toLowerCase();
                const artist = (track?.artist || '').toLowerCase();
                const title = (track?.title || '').toLowerCase();
                const album = (track?.album || '').toLowerCase();
                const fileName = (file?.filePath || '').toLowerCase();
                const djpalId = (track?.djpalId || '').toLowerCase();
                // Match only the genre the column DISPLAYS (first style/
                // genre) — filtering must be WYSIWYG. Matching every hidden
                // tag surfaced rows whose visible genre had nothing to do
                // with the query.
                const genreText = (track?.styles?.[0] || track?.genres?.[0] || track?.genre || '').toLowerCase();
                const label = (track?.label || '').toLowerCase();

                const matchesSearch =
                    artist.includes(lowerSearchTerm) ||
                    title.includes(lowerSearchTerm) ||
                    album.includes(lowerSearchTerm) ||
                    fileName.includes(lowerSearchTerm) ||
                    genreText.includes(lowerSearchTerm) ||
                    label.includes(lowerSearchTerm) ||
                    djpalId.includes(lowerSearchTerm);

                if (!matchesSearch) {
                    return false;
                }
            }

            // Filter by BPM range
            if (track?.bpm) {
                const bpm = parseFloat(track.bpm);
                if (bpm < bpmMin || bpm > bpmMax) {
                    return false;
                }
            }

            // Filter by Loudness range
            if (track?.loudness) {
                const loudness = parseFloat(track.loudness);
                if (loudness < loudnessMin || loudness > loudnessMax) {
                    return false;
                }
            }

            // Filter by Release Date range (compare on 4-digit year)
            if (track?.releaseDate) {
                const year = parseYear(track.releaseDate);
                if (!isNaN(year) && (year < releaseDateMin || year > releaseDateMax)) {
                    return false;
                }
            }

            // Filter by selected keys (only if some keys are selected)
            if (selectedKeys.length > 0 && track?.key) {
                const formattedKey = formatKey(track.key);
                if (!selectedKeys.includes(formattedKey)) {
                    return false;
                }
            }

            return true;
        });
    }, [sortedFiles, bpmMin, bpmMax, loudnessMin, loudnessMax, releaseDateMin, releaseDateMax, selectedKeys, searchTerm, hideMissingFiles, backupFilter, backupMarks]);

    // Navbar "In Your Library" click: scroll the virtualized list to the
    // track and flash-highlight the row. If the current text filter hides
    // it, clear the filter and let the re-run find it; if other filters
    // (BPM/key/date) hide it, give up silently. The request is cleared once
    // handled so re-opening the Library tab doesn't replay the scroll.
    const libraryListRef = React.useRef(null);

    // Restore the saved scroll once the virtualized list is actually mounted
    // (listReady && !isPending — before that the spinner branch renders and
    // there is no list element). The saved value is SNAPSHOTTED at component
    // mount: the List emits onScroll(0) while initializing, which would
    // overwrite the module store before this effect reads it. The restore is
    // applied twice (now + next frame) because react-window's full-height
    // spacer may not be laid out in the mounting commit, clamping the first
    // attempt. The "In Your Library" reveal below runs in a later
    // (post-paint) effect and wins when both fire.
    const initialListScrollRef = React.useRef({ ..._savedListScroll });
    const didRestoreListScrollRef = React.useRef(false);
    React.useLayoutEffect(() => {
        if (!listReady || isPending || didRestoreListScrollRef.current) return undefined;
        const saved = initialListScrollRef.current;
        didRestoreListScrollRef.current = true;
        if (!saved.top && !saved.left) return undefined;
        const apply = () => {
            const el = libraryListRef.current?.element;
            if (!el) return;
            el.scrollTop = saved.top;
            el.scrollLeft = saved.left;
            syncHeaderScroll(saved.left);
        };
        apply();
        const raf = requestAnimationFrame(apply);
        return () => cancelAnimationFrame(raf);
    }, [listReady, isPending]);

    // ---- Multi-row selection (Finder semantics, shared hook) ----
    // Click/cmd/shift selection, Cmd+A, Escape, ArrowUp/Down (+Shift extend)
    // and Delete/Backspace all live in useRowSelection. Delete routes through
    // removePathsWithUndo (defined below — bridged via ref because the hook
    // needs filteredFiles, which is only available from here on).
    const rowKeys = React.useMemo(() => filteredFiles.map(f => f?.file).filter(Boolean), [filteredFiles]);
    const deleteSelectedRef = React.useRef(null);
    const onDeleteSelected = React.useCallback((paths) => deleteSelectedRef.current?.(paths), []);
    const onArrowFocusRow = React.useCallback((index) => {
        libraryListRef.current?.scrollToRow({ index, align: 'auto', behavior: 'instant' });
    }, []);
    const {
        selected: selectedPaths,
        orderedSelected,
        handleRowClick: handleRowSelectClick,
        clearSelection,
        selectOnly,
    } = useRowSelection({ keys: rowKeys, onDelete: onDeleteSelected, onArrowFocus: onArrowFocusRow, rowSelector: '.tree-node' });

    const [focusFlashPath, setFocusFlashPath] = useState(null);
    const focusFlashTimerRef = React.useRef(null);
    const focusSettleTimerRef = React.useRef(null);
    const filteredFilesRef = React.useRef(null);
    filteredFilesRef.current = filteredFiles;
    useEffect(() => {
        if (!libraryFocusTrack || !listReady) return;
        const targetPath = libraryFocusTrack.filePath;
        const index = filteredFiles.findIndex(f => (f.filePath || f.file) === targetPath);
        if (index === -1) {
            // The library hydrates from SQLite asynchronously after mount —
            // while it's still empty/loading, keep the request and retry when
            // the list fills in (filteredFiles is a dep).
            if (libraryLoading || enrichedFiles.length === 0) return;
            if (searchTerm && searchTerm.trim() !== '') {
                dispatch(setFilterTerm({ tab: 'library', term: '' }));
            } else {
                dispatch(setLibraryFocusTrack(null));
            }
            return;
        }
        dispatch(setLibraryFocusTrack(null));
        // Select the row (Finder-style "reveal") so the marker persists —
        // it clears the way any selection does: by clicking elsewhere.
        selectOnly(targetPath, index);
        // Settle loop. Layout keeps shifting for a beat after landing here —
        // a fresh List mount is still measuring its viewport (an immediate
        // scrollToRow gets clamped), charts mount a frame later, and the
        // background missing-file check can re-sort the rows. One scroll
        // ends up half a row off, and a row remount replays the flash
        // animation. So: re-assert the scroll a few times against the
        // CURRENT list, and start the flash once, after the last pass.
        clearTimeout(focusSettleTimerRef.current);
        let passes = 0;
        const settle = () => {
            const idx = (filteredFilesRef.current || []).findIndex(f => (f.filePath || f.file) === targetPath);
            if (idx !== -1) libraryListRef.current?.scrollToRow({ index: idx, align: 'center', behavior: 'instant' });
            passes += 1;
            if (passes < 4) {
                focusSettleTimerRef.current = setTimeout(settle, 250);
            } else if (idx !== -1) {
                setFocusFlashPath(targetPath);
                clearTimeout(focusFlashTimerRef.current);
                focusFlashTimerRef.current = setTimeout(() => setFocusFlashPath(null), 2600);
            }
        };
        requestAnimationFrame(() => requestAnimationFrame(settle));
    }, [libraryFocusTrack, listReady, filteredFiles, searchTerm, libraryLoading, enrichedFiles.length, dispatch]);
    useEffect(() => () => {
        clearTimeout(focusFlashTimerRef.current);
        clearTimeout(focusSettleTimerRef.current);
    }, []);

    const sortColumn = (key) => {
        // Skip sort if this click came from a column resize drag
        if (isResizingRef.current) return;

        // "#" (track order): not-found tracks always at the bottom; everything
        // else by sort_order in the chosen direction. First click = asc, second = desc.
        if (key === 'sortOrder') {
            const nextDir = (activeColumn === 'sortOrder' && sortDirection === 'asc') ? 'desc' : 'asc';
            const sorted = [...sortedFiles].sort((a, b) => {
                const am = a.result?.fileNotFound ? 1 : 0;
                const bm = b.result?.fileNotFound ? 1 : 0;
                if (am !== bm) return am - bm;
                const av = a.sortOrder ?? Infinity, bv = b.sortOrder ?? Infinity;
                return nextDir === 'asc' ? av - bv : bv - av;
            });
            dispatch(setSortedFiles(sorted));
            dispatch(setSortDirection(nextDir));
            dispatch(setActiveColumn('sortOrder'));
            return;
        }

        // First click on a column = asc, clicking the same column again = desc.
        const nextDir = (activeColumn === key && sortDirection === 'asc') ? 'desc' : 'asc';

        // Comparable value per column; null = empty/N-A. Empty always sorts to the
        // BOTTOM regardless of direction (the convention pro apps use), so a column
        // full of blanks doesn't push them to the top.
        const valueOf = (item) => {
            const t = item.result?.track;
            switch (key) {
                case 'trackName': return (t?.title || item.result?.trackName || '').trim().toLowerCase() || null;
                case 'artist': return (t?.artist || '').trim().toLowerCase() || null;
                case 'genre': return (t?.styles?.[0] || t?.genres?.[0] || t?.genre || '').toString().trim().toLowerCase() || null;
                case 'label': return (t?.label || '').trim().toLowerCase() || null;
                case 'bpm': { const n = parseFloat(t?.bpm); return (isNaN(n) || n <= 0) ? null : n; }
                case 'loudness': { const n = parseFloat(t?.loudness); return isNaN(n) ? null : n; }
                case 'energy': { const n = parseFloat(t?.energy); return isNaN(n) ? null : n; }
                case 'releaseDate': { const n = parseYear(t?.releaseDate); return (isNaN(n) || !n) ? null : n; }
                case 'compatibility': return t?.isCompatible === true ? 2 : t?.isCompatible === false ? 1 : null;
                // Cloud backup state: not-backed-up first on asc (the rows the
                // user wants to find); cloud tracks are out of scope → bottom.
                case 'backup': {
                    const fp2 = item.file || '';
                    if (/^(online:|https?:)/i.test(fp2)) return null;
                    return backupMarks.paths?.has(fp2) ? 2 : 1;
                }
                default: return String(t?.[key] || '').trim().toLowerCase() || null;
            }
        };
        const sorted = [...sortedFiles].sort((a, b) => {
            const av = valueOf(a), bv = valueOf(b);
            if (av == null && bv == null) return 0;
            if (av == null) return 1;   // empties/N-A always last
            if (bv == null) return -1;
            if (av < bv) return nextDir === 'asc' ? -1 : 1;
            if (av > bv) return nextDir === 'asc' ? 1 : -1;
            return 0;
        });

        dispatch(setSortedFiles(sorted));
        dispatch(setSortDirection(nextDir));
        dispatch(setActiveColumn(key));
    };

    // ---- Drag-to-reorder (the "#" / custom-order column) -------------------
    // Reordering only makes sense in the user's custom order; when sorted by any
    // other column it's disabled (dragging would fight the sort). Filters/search
    // may narrow the visible rows — we still compute a valid fractional position
    // from the visible neighbours, which lands the track correctly in the global
    // order.
    // Reorder is only allowed while the "#" (track-order) sort is active — dragging
    // would fight any other sort. activeColumn defaults to 'sortOrder' on load so
    // it's on by default. A playlist reorder rewrites the whole playlist's
    // positions, so it only makes sense on the full (unfiltered) list; the library
    // reorder uses fractional positions and still works when filtered.
    // Reorder is allowed whenever the "#" sort is active. Library reorder uses
    // fractional positions, so it works even when the view is filtered (the track
    // lands between its visible neighbours). A playlist reorder renumbers the whole
    // list, so it still requires the full unfiltered view. Works in both directions.
    const noNarrowing = filteredFiles.length === sortedFiles.length;
    const reorderEnabled = activeColumn === 'sortOrder' && (!selectedPlaylistId || noNarrowing);

    // Canonical library order: not-found tracks always at the bottom, everything
    // else by sort_order ascending. Used for the default display, the "#" rank
    // numbers, and the post-reorder re-sort so they never disagree.
    const canonicalCompare = (a, b) => {
        const am = a.result?.fileNotFound ? 1 : 0;
        const bm = b.result?.fileNotFound ? 1 : 0;
        if (am !== bm) return am - bm;
        return (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity);
    };

    // Stable "#" number for each track = its 1-based position in the canonical
    // order, INDEPENDENT of the current sort direction/filter. So a reversed sort
    // shows 3,2,1 and a filtered view shows the real ranks (1,5,9…).
    const rankMap = React.useMemo(() => {
        const ranked = [...sortedFiles].sort((a, b) => {
            const am = a.result?.fileNotFound ? 1 : 0;
            const bm = b.result?.fileNotFound ? 1 : 0;
            if (am !== bm) return am - bm;
            return (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity);
        });
        const m = new Map();
        ranked.forEach((f, i) => m.set(f.file || f.filePath, i + 1));
        return m;
    }, [sortedFiles]);

    // Header sort affordance: no permanent up/down glyphs — only the active column
    // is accent-highlighted and shows one small ▲/▼ pinned to the column's right
    // edge (the header cells are position:relative). pointerEvents:none so it never
    // blocks the sort click or the resize handle.
    const sortArrow = (col) => (activeColumn === col
        ? <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.5em', opacity: 0.8, pointerEvents: 'none' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>
        : null);
    const headActive = (col) => (activeColumn === col ? { color: 'var(--color-accent, #4CC2F1)', fontWeight: 700 } : {});
    const dragFromIndexRef = React.useRef(null);
    const dragOverIndexRef = React.useRef(null);
    const [dragOverIndex, setDragOverIndex] = React.useState(null);
    const listScrollElRef = React.useRef(null);
    const autoScrollRafRef = React.useRef(null);
    const autoScrollDyRef = React.useRef(0);

    const stopAutoScroll = React.useCallback(() => {
        autoScrollDyRef.current = 0;
        if (autoScrollRafRef.current) { cancelAnimationFrame(autoScrollRafRef.current); autoScrollRafRef.current = null; }
    }, []);

    // Scroll the list when the cursor nears its top/bottom edge mid-drag, so a
    // track can be dragged past the viewport (rows outside it aren't in the DOM).
    const autoScrollOnDrag = React.useCallback((clientY, rowEl) => {
        if (!listScrollElRef.current && rowEl) {
            let el = rowEl;
            while (el && el !== document.body) {
                const oy = window.getComputedStyle(el).overflowY;
                if ((oy === 'scroll' || oy === 'auto') && el.scrollHeight > el.clientHeight) { listScrollElRef.current = el; break; }
                el = el.parentElement;
            }
        }
        const el = listScrollElRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const EDGE = 46, SPEED = 14;
        if (clientY < r.top + EDGE) autoScrollDyRef.current = -SPEED;
        else if (clientY > r.bottom - EDGE) autoScrollDyRef.current = SPEED;
        else autoScrollDyRef.current = 0;
        if (autoScrollDyRef.current !== 0 && !autoScrollRafRef.current) {
            const step = () => {
                const s = listScrollElRef.current;
                if (!s || autoScrollDyRef.current === 0) { autoScrollRafRef.current = null; return; }
                s.scrollTop += autoScrollDyRef.current;
                autoScrollRafRef.current = requestAnimationFrame(step);
            };
            autoScrollRafRef.current = requestAnimationFrame(step);
        }
    }, []);

    const handleRowDragStart = React.useCallback((index, e) => {
        // Let the user select text: if the drag began on a selectable text cell
        // (Artist/Title), cancel the reorder so it acts as a normal selection.
        if (e.target && e.target.closest && e.target.closest('[data-noreorder]')) {
            e.preventDefault();
            return;
        }
        dragFromIndexRef.current = index;
        try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(index)); } catch (_) {}
    }, []);

    const handleRowDragOver = React.useCallback((index, e) => {
        if (dragFromIndexRef.current == null) return;
        e.preventDefault();
        e.stopPropagation();
        try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
        const rect = e.currentTarget.getBoundingClientRect();
        const below = e.clientY > rect.top + rect.height / 2;
        const over = below ? index + 1 : index;
        dragOverIndexRef.current = over;
        setDragOverIndex(over);
        autoScrollOnDrag(e.clientY, e.currentTarget);
    }, [autoScrollOnDrag]);

    const handleRowDragEnd = React.useCallback(() => {
        dragFromIndexRef.current = null;
        dragOverIndexRef.current = null;
        setDragOverIndex(null);
        stopAutoScroll();
    }, [stopAutoScroll]);

    const performReorder = React.useCallback((fromIndex, insertIndex) => {
        const list = filteredFiles;
        if (fromIndex == null || insertIndex == null || fromIndex < 0 || fromIndex >= list.length) return;
        const moved = list[fromIndex];
        if (!(moved && (moved.file || moved.filePath))) return;
        const target = fromIndex < insertIndex ? insertIndex - 1 : insertIndex; // adjust for removal
        if (target === fromIndex) return; // dropped in place
        const newVisible = [...list];
        newVisible.splice(fromIndex, 1);
        newVisible.splice(target, 0, moved);

        // Rebuild the FULL display order by anchoring the moved track next to its
        // (possibly filtered) visible neighbours, so a filtered view still moves
        // the track to the right spot in the real list.
        const visAbove = newVisible[target - 1];
        const visBelow = newVisible[target + 1];
        const full = sortedFiles.filter((f) => f !== moved);
        let insertAt;
        if (visAbove) insertAt = full.indexOf(visAbove) + 1;
        else if (visBelow) insertAt = Math.max(0, full.indexOf(visBelow));
        else insertAt = 0;
        full.splice(insertAt, 0, moved);

        // Persisted order is ASCENDING sort_order: not-found tracks always last,
        // and the non-missing block reversed when the view is descending. Renumber
        // 1..N (clean integers — no fractional drift, no reload).
        const desc = sortDirection === 'desc';
        const nonMissing = full.filter((f) => !f.result?.fileNotFound);
        const missingTail = full.filter((f) => f.result?.fileNotFound);
        const ascending = (desc ? [...nonMissing].reverse() : nonMissing).concat(missingTail);
        const orderMap = new Map();
        ascending.forEach((f, i) => orderMap.set(f, i + 1));

        // Keep the on-screen list in the current display order, with fresh sort_order.
        const nextSorted = full.map((f) => ({ ...f, sortOrder: orderMap.get(f) }));
        dispatch(setSortedFiles(nextSorted));

        const orderedPaths = ascending.map((f) => f.file || f.filePath).filter(Boolean);
        if (selectedPlaylistId) {
            window.electron.db.playlists.reorder(selectedPlaylistId, orderedPaths)
                .catch((err) => console.warn('[Library] playlist reorder persist failed', err));
        } else {
            window.electron.db.tracks.applyOrder(orderedPaths)
                .catch((err) => console.warn('[Library] reorder persist failed', err));
        }
    }, [filteredFiles, sortedFiles, selectedPlaylistId, sortDirection, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleRowDrop = React.useCallback((index, e) => {
        // Only handle an internal reorder drag. A file drop (from Finder) has no
        // in-flight reorder — let it bubble to the file-import drop zone instead of
        // swallowing it (which left the "Drop files here" overlay stuck on).
        if (dragFromIndexRef.current == null) return;
        e.preventDefault();
        e.stopPropagation();
        const from = dragFromIndexRef.current;
        const over = dragOverIndexRef.current;
        dragFromIndexRef.current = null;
        dragOverIndexRef.current = null;
        stopAutoScroll();
        setDragOverIndex(null);
        if (from != null && over != null) performReorder(from, over);
    }, [performReorder, stopAutoScroll]);

    // Set a "local" list context of the currently-visible (filtered + sorted)
    // library so playback advances to the NEXT track when one ends, instead of
    // falling through to auto-radio. Missing files stay OUT — advancing into
    // one would 404 and bounce the player around. Shared by the fresh-play and
    // resume paths of handlePlayClick.
    const registerListContext = (filePath) => {
        const playableFiles = filteredFiles.filter((f) => f?.result?.fileNotFound !== true);
        const contextItems = playableFiles.map((f) => {
            const t = f.result?.track || {};
            const fn = (f.file || '').split('/').pop().replace(/\.[^/.]+$/, '');
            let a = 'Unknown Artist';
            let n = fn;
            if (fn.includes(' - ')) { const p = fn.split(' - '); a = p[0].trim(); n = p.slice(1).join(' - ').trim(); }
            // djpalId lets the radio hand-off seed catalog-known tracks first.
            return { filePath: f.file, title: t.title || n, artist: t.artist || a, cover: t.cover || '', djpalId: t.djpalId || null };
        });
        const currentIndex = playableFiles.findIndex((f) => f.file === filePath);
        dispatch(setListContext({ items: contextItems, currentIndex, type: 'local' }));
    };

    const handlePlayClick = async (filePath, e) => {
        e.stopPropagation();

        if (!filePath) {
            return;
        }

        // Files that aren't on this machine (playlist synced from another
        // computer, unmounted drive) can't play — say why instead of handing
        // the player a URL that will 404 and cascade-skip. Don't trust the
        // fileNotFound flag alone: analysis rebuilds rows and the flag check
        // may not have landed yet, so do a live fs check (one stat call) and
        // flag the row for the UI while we're at it.
        if (enrichedFiles.find(f => f.file === filePath)?.result?.fileNotFound === true) {
            toast.warning(`"${filePath.split('/').pop()}" isn't on this device — the file wasn't found on disk.`);
            return;
        }
        try {
            const missingNow = await window.electron.db.tracks.checkMissingFiles([filePath]);
            if (Array.isArray(missingNow) && missingNow.length > 0) {
                dispatch(markEnrichedFilesMissing({ missing: missingNow, checked: [filePath] }));
                toast.warning(`"${filePath.split('/').pop()}" isn't on this device — the file wasn't found on disk.`);
                return;
            }
        } catch (_) { /* check failed — fall through and try to play */ }

        try {
            // If clicking on currently playing track that's paused, resume it
            if (currentlyPlayingPath === filePath && !isPlaying) {
                if (window.musicPlayerInstance) {
                    window.musicPlayerInstance.play();
                }
                // Re-assert the list context: it may have been cleared (radio
                // hand-off, another surface) while this row stayed current —
                // resuming must restore play-through-the-list, not solo play.
                registerListContext(filePath);
                setIsPlaying(true);
                dispatch(setAutoPlay(true));
                return;
            }

            // Parse artist and track name from filename
            const fileName = filePath.split('/').pop();
            const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');

            let artist = 'Unknown Artist';
            let trackName = fileNameWithoutExt;

            // Try to parse "Artist - Track" format
            if (fileNameWithoutExt.includes(' - ')) {
                const parts = fileNameWithoutExt.split(' - ');
                artist = parts[0].trim();
                trackName = parts.slice(1).join(' - ').trim();
            }

            // Load and play new track
            const musicSrc = await window.electron.getAudioUrl(filePath);

            // Try to get album art, but don't fail if it doesn't exist
            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(filePath);
            } catch (err) {
            }

            // Prefer the enriched Mixpedia data (real title + cover) over the
            // filename / local album art.
            const et = enrichedFiles.find(f => f.file === filePath)?.result?.track;
            const newTrack = {
                name: et?.title || trackName,
                singer: et?.artist || artist,
                cover: et?.cover || albumArtUrl,
                musicSrc,
                ...splitArtistsField(et?.artist || artist),
            };

            // Clear playlist and play new track
            dispatch(setAudioList([newTrack]));
            registerListContext(filePath);
            dispatch(setAutoPlay(true));
            setCurrentlyPlayingPath(filePath);
            setIsPlaying(true);
        } catch (error) {
            console.error('Error playing track:', error);
        }
    };

    const handlePauseClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
        }
        setIsPlaying(false);
        dispatch(setAutoPlay(false));
    };

    const handleStopClick = (e) => {
        e.stopPropagation();
        if (window.musicPlayerInstance) {
            window.musicPlayerInstance.pause();
            window.musicPlayerInstance.currentTime = 0;
        }
        setIsPlaying(false);
        setCurrentlyPlayingPath(null);
        dispatch(setAutoPlay(false));
        dispatch(resetAudioList());
    };

    const handleAddToQueue = async (file, e) => {
        e.stopPropagation();

        if (!file) {
            return;
        }

        // If file is just a string (filePath), convert to object format
        const filePath = typeof file === 'string' ? file : file.file;
        const track = typeof file === 'object' ? file.result?.track : null;

        if (enrichedFiles.find(f => f.file === filePath)?.result?.fileNotFound === true) {
            toast.warning(`"${(filePath || '').split('/').pop()}" isn't on this device — the file wasn't found on disk.`);
            return;
        }
        try {
            const missingNow = await window.electron.db.tracks.checkMissingFiles([filePath]);
            if (Array.isArray(missingNow) && missingNow.length > 0) {
                dispatch(markEnrichedFilesMissing({ missing: missingNow, checked: [filePath] }));
                toast.warning(`"${(filePath || '').split('/').pop()}" isn't on this device — the file wasn't found on disk.`);
                return;
            }
        } catch (_) { /* check failed — fall through */ }

        try {
            // If a playlist is selected, add to that playlist instead of the queue
            if (selectedPlaylistId) {
                await window.electron.db.playlists.addTracks(selectedPlaylistId, [filePath]);
                const playlistName = playlists.find(p => p.id === selectedPlaylistId)?.name || 'playlist';
                // Optional: Show a toast notification here
                return;
            }

            // Otherwise, add to player queue as before
            const musicSrc = await window.electron.getAudioUrl(filePath);

            let albumArtUrl = '';
            try {
                albumArtUrl = await window.electron.getAlbumArtUrl(filePath);
            } catch (err) {
            }

            // Use metadata if available, otherwise parse from filename
            let artist = 'Unknown Artist';
            let trackName = 'Unknown Title';

            if (track) {
                // Use track metadata if available
                artist = track.artist || 'Unknown Artist';
                trackName = typeof file === 'object' ? (file.result?.trackName || 'Unknown Title') : 'Unknown Title';
            } else {
                // Fallback to parsing filename
                const fileName = filePath.split('/').pop();
                const fileNameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
                trackName = fileNameWithoutExt;

                if (fileNameWithoutExt.includes(' - ')) {
                    const parts = fileNameWithoutExt.split(' - ');
                    artist = parts[0].trim();
                    trackName = parts.slice(1).join(' - ').trim();
                }
            }

            const et = enrichedFiles.find(f => f.file === filePath)?.result?.track;
            const newTrack = {
                name: et?.title || trackName,
                singer: et?.artist || artist,
                cover: et?.cover || albumArtUrl,
                musicSrc,
                ...splitArtistsField(et?.artist || artist),
            };

            dispatch(addTrack(newTrack));
        } catch (error) {
            console.error('Error adding track:', error);
        }
    };

    // Selection is scoped to the list being viewed — reset it when the user
    // switches playlist / folder view (indexes and paths no longer line up).
    React.useEffect(() => {
        clearSelection();
    }, [selectedPlaylistId, selectedPlaylistName, folderSelection, clearSelection]);

    // Right-click on a row. Finder semantics: inside a multi-selection the
    // menu targets the whole selection (bulk menu); outside it, the selection
    // retargets to that row and its shared TrackContextMenu opens at the
    // cursor (same options as the row's ⋯ button, by construction).
    const handleContextMenu = (filePath, e, index) => {
        e.preventDefault();
        e.stopPropagation();
        if (!filePath) return;
        if (selectedPaths.size > 1 && selectedPaths.has(filePath)) {
            setBulkCtx({ x: e.clientX, y: e.clientY });
            return;
        }
        selectOnly(filePath, index);
        setRowCtx({ key: filePath, x: e.clientX, y: e.clientY, nonce: ++ctxNonceRef.current });
    };

    // Single-row "Add to Playlist" (⋯ and right-click): the same bulk-capable
    // picker the multi-select menu uses, targeting the real local file path.
    const openPlaylistPickerFor = React.useCallback((filePath) => {
        if (!filePath) return;
        setCtxPlaylistTargets([filePath]);
        setCtxPlaylistPicker(true);
    }, []);

    // Remove a single track from the local library. Deletes from SQLite
    // (playlist_tracks cascade via FK) and from the Redux view. No server call —
    // the user-library-service stores likes/follows/playlists, not track rows.
    // Core remove-from-LIBRARY for any number of paths (single deleteMany txn;
    // playlist_tracks cascade). Reversible — files stay on disk — so EVERY entry
    // point (context menu, "..." bulk menu, Delete/Backspace) routes here and
    // gets the same Undo toast; Undo re-inserts the exact rows we removed.
    const removePathsWithUndo = React.useCallback(async (paths) => {
        if (!paths?.length) return;
        const pathSet = new Set(paths);
        // Stash full rows (enriched result + sortOrder) so Undo can restore them.
        const stash = (store.getState().info.enrichedFiles || []).filter(f => f && pathSet.has(f.file));
        try {
            await window.electron.db.tracks.deleteMany(paths);
            // (Selection self-prunes: useRowSelection drops keys that leave
            // the visible list.)
            dispatch(removeEnrichedFiles(paths));
            const undo = async () => {
                try {
                    await window.electron.db.tracks.batchSave(
                        stash.map(f => ({ file: f.file, filePath: f.file, fileName: f.fileName, track: f.result?.track || {} }))
                    );
                    dispatch(addEnrichedFilesBatch(
                        stash.map(f => ({ file: f.file, fileName: f.fileName, sortOrder: f.sortOrder, result: f.result }))
                    ));
                } catch (e) {
                    toast.error('Undo failed');
                }
            };
            toast.showToast(
                `Removed ${paths.length} track${paths.length === 1 ? '' : 's'} from library`,
                'info', 6000, { label: 'Undo', onClick: undo }
            );
        } catch (e) {
            toast.error('Failed to remove');
        }
    }, [dispatch, toast]);

    // Delete/Backspace (useRowSelection) routes here via the ref bridge —
    // same Undo toast as every other removal entry point.
    deleteSelectedRef.current = removePathsWithUndo;

    const removeFromLibraryByPath = React.useCallback((path) => (
        path ? removePathsWithUndo([path]) : Promise.resolve()
    ), [removePathsWithUndo]);

    const removeManyFromLibrary = React.useCallback((paths) => removePathsWithUndo(paths), [removePathsWithUndo]);

    // "Locate file…" (⋯ / right-click on a missing row): re-point the DB row
    // at a file the user picks in the native dialog.
    const locateFileByPath = async (oldPath) => {
        if (!oldPath) return;
        try {
            const result = await window.electron.dialog.showOpenDialog({
                title: 'Locate file',
                properties: ['openFile'],
                // Hint with the original basename so the native dialog opens on it
                defaultPath: oldPath.split(/[\\/]/).pop(),
            });
            if (result.canceled || !result.filePaths?.[0]) return;
            const newPath = result.filePaths[0];
            const res = await window.electron.db.tracks.relocate(oldPath, newPath);
            if (res?.success) {
                dispatch(relocateEnrichedFiles({ [oldPath]: newPath }));
                toast.success('File relocated');
            } else {
                toast.error(res?.error || 'Failed to relocate');
            }
        } catch (e) {
            toast.error('Failed to relocate');
        }
    };

    // Group missing files by their (now-stale) parent directory so the modal
    // can say "42 files from ~/Downloads" instead of asking folder-by-folder.
    const openCleanupModal = () => {
        const missing = enrichedFiles
            .filter(f => f?.result?.fileNotFound === true)
            .map(f => f.file)
            .filter(Boolean);
        if (missing.length === 0) {
            toast.info('No missing files');
            return;
        }
        const groupMap = new Map();
        for (const p of missing) {
            const sep = p.lastIndexOf('/') >= 0 ? '/' : '\\';
            const dir = p.substring(0, p.lastIndexOf(sep)) || '/';
            if (!groupMap.has(dir)) groupMap.set(dir, []);
            groupMap.get(dir).push(p);
        }
        const groups = Array.from(groupMap.entries())
            .map(([dir, paths]) => ({ dir, paths }))
            .sort((a, b) => b.paths.length - a.paths.length);
        setCleanupModal({ groups });
    };

    // Step 1 of relocate: pick a folder, scan it, and show a preview card inside
    // the same modal. No DB writes happen here. The user must click Apply in
    // the review card to actually relocate.
    const handleBulkRelocate = async (groupPaths, source = 'group') => {
        try {
            const result = await window.electron.dialog.showOpenDialog({
                title: 'Locate folder containing these files',
                properties: ['openDirectory'],
            });
            if (result.canceled || !result.filePaths?.[0]) return;
            const folder = result.filePaths[0];

            setCleanupBusyMsg('Scanning folder…');
            setCleanupBusy(true);
            try {
                const res = await window.electron.db.tracks.scanFolderForRelocate(groupPaths, folder);
                const matched = res?.matched || [];
                const unmatched = res?.unmatched || [];
                setRelocateReview({ folder, matched, unmatched, total: groupPaths.length, source });
            } finally {
                setCleanupBusy(false);
            }
        } catch (e) {
            toast.error('Failed to scan folder');
            setCleanupBusy(false);
        }
    };

    // Step 2 of relocate: user approved the preview — actually write the DB
    // changes, update Redux, and refresh the group list inside the modal.
    const confirmRelocateReview = async () => {
        if (!relocateReview) return;
        const { matched, unmatched } = relocateReview;
        setRelocateReview(null);
        if (matched.length === 0) return; // nothing to apply

        setCleanupBusyMsg('Relocating files…');
        setCleanupBusy(true);
        try {
            const res = await window.electron.db.tracks.applyRelocations(matched);
            const applied = res?.applied || [];
            const failed = res?.failed || [];
            if (applied.length > 0) {
                const map = {};
                for (const m of applied) map[m.oldPath] = m.newPath;
                dispatch(relocateEnrichedFiles(map));
            }
            toast[applied.length > 0 ? 'success' : 'info'](
                `Relocated ${applied.length} file${applied.length === 1 ? '' : 's'}${failed.length ? ` — ${failed.length} failed` : ''}`
            );

            // Drop applied paths from the modal's group list so it reflects
            // the remaining missing files. unmatched ones + failed ones stay.
            const relocated = new Set(applied.map(m => m.oldPath));
            setCleanupModal(prev => {
                if (!prev) return prev;
                const newGroups = prev.groups
                    .map(g => ({ dir: g.dir, paths: g.paths.filter(p => !relocated.has(p)) }))
                    .filter(g => g.paths.length > 0);
                return { groups: newGroups };
            });
        } catch (e) {
            toast.error('Failed to relocate');
        } finally {
            setCleanupBusy(false);
        }
    };

    const handleBulkRemove = async (paths) => {
        if (paths.length === 0) return;
        setCleanupBusyMsg('Removing tracks…');
        setCleanupBusy(true);
        try {
            await window.electron.db.tracks.deleteMany(paths);
            dispatch(removeEnrichedFiles(paths));
            toast.success(`Removed ${paths.length} track${paths.length === 1 ? '' : 's'}`);
            setCleanupModal(prev => {
                if (!prev) return prev;
                const removed = new Set(paths);
                const newGroups = prev.groups
                    .map(g => ({ dir: g.dir, paths: g.paths.filter(p => !removed.has(p)) }))
                    .filter(g => g.paths.length > 0);
                return { groups: newGroups };
            });
        } catch (e) {
            toast.error('Failed to remove');
        } finally {
            setCleanupBusy(false);
        }
    };

    const getPlaybackIcon = (filePath) => {
        if (!filePath) {
            return null;
        }

        const isCurrentTrack = currentlyPlayingPath === filePath;

        if (isCurrentTrack && isPlaying) {
            return (
                <PauseIcon
                    className="tree-play-icon pause"
                    onClick={(e) => handlePauseClick(e)}
                    title="Pause"
                />
            );
        } else {
            return (
                <PlayIcon
                    className="tree-play-icon"
                    onClick={(e) => handlePlayClick(filePath, e)}
                    title="Play"
                />
            );
        }
    };

    // Early-return a minimal spinner while:
    //   - listReady is still false (1-2 paint frames after mount), AND/OR
    //   - the listReady=true transition is pending (React rendering full UI
    //     concurrently in the background; we keep the spinner visible until
    //     it finishes).
    // This eliminates the two freezes the user reported:
    //   - "click → freeze → spinner": first commit was rendering the full
    //     heavy JSX tree (sidebars, charts, controls, list). Now the first
    //     commit returns this tiny tree, paints in ~16ms.
    //   - "spinner → freeze → files": the listReady flip used to be a sync
    //     re-render of the full UI in one commit. Now it's a useTransition
    //     transition, rendered non-urgently with the spinner staying on
    //     screen until the new tree is fully ready to swap in.
    // All hooks are above this line; React's rules-of-hooks stays valid.
    if (!listReady || isPending) {
        return (
            <div
                className={`tab-container ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <div style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                    <div className="loading-spinner" style={{ width: '24px', height: '24px', margin: '0 auto 12px' }}></div>
                    <div>Loading library…</div>
                </div>
            </div>
        );
    }


    // Source / housekeeping actions, shared by the classic Actions column and
    // the design-v2 tool header (same handlers, two homes).
    const handleLoadAllTracks = async () => {
        setSelectedPlaylistName(null);
        dispatch(resetEnrichedFiles());
        setLibraryLoading(true);
        try {
            const tracks = await window.electron.db.tracks.getAll();
            if (tracks && tracks.length > 0) {
                const enriched = tracks.map(t => ({
                    file: t.file || t.filePath,
                    fileName: t.fileName || (t.file || t.filePath || '').split('/').pop(),
                    result: { track: t.track || {} }
                }));
                await new Promise(r => setTimeout(r, 50));
                dispatch(addEnrichedFilesBatch(enriched));
                dispatch(recomputeGraphData());
                toast.success(`Loaded ${tracks.length} tracks from library`);
            } else {
                toast.info('Library is empty. Select a folder or playlist to add tracks.');
            }
        } catch (e) {
            toast.error('Failed to load library');
        } finally {
            setLibraryLoading(false);
        }
    };
    const handleClearSavedResults = async () => {
        const confirmed = await toast.confirm(
            'All saved BPM, Key, Loudness, and genre data will be deleted. Re-analyzing your full library can take a long time depending on library size.',
            'Clear All Analysis Data?',
            { confirmText: 'Clear All', confirmVariant: 'danger' }
        );
        if (!confirmed) return;
        // 1) Clear the enricher cache (the re-analysis skip logic).
        await window.electron.clearAnalysisCache();
        // 2) The table's saved BPM/Key/etc. actually live in the DB —
        //    delete those rows for the loaded tracks so they're gone.
        const localPaths = enrichedFiles
            .map(f => f.file)
            .filter(p => p && !p.startsWith('online://'));
        if (localPaths.length) {
            try { await window.electron.db.tracks.deleteMany(localPaths); } catch (e) { /* ignore */ }
        }
        // 3) Reset the analysis in the visible list (keep the files, drop the data).
        const resetFiles = enrichedFiles
            .filter(f => f.file && !f.file.startsWith('online://'))
            .map(f => ({
                file: f.file,
                fileName: f.fileName,
                result: {
                    trackName: f.result?.trackName,
                    track: { artist: f.result?.track?.artist || '', title: f.result?.track?.title || '' },
                    isLoading: false,
                },
            }));
        if (resetFiles.length) dispatch(updateEnrichedFilesBatch(resetFiles));
        toast.success(`Cleared saved analysis for ${resetFiles.length} track${resetFiles.length === 1 ? '' : 's'}`);
    };
    const handleClearSelection = () => {
        setSelectedPlaylistName(null);
        dispatch(resetInfoFilePath());
        dispatch(resetEnrichedFiles());
    };
    const handleClearFolderSelection = () => {
        // Fully clear the folder view and return to the library.
        // Kill any in-flight analyze first so it stops burning
        // CPU / network on a folder the user just abandoned.
        window.electron.cancelAnalyzeInfo();
        dispatch(setInfoIsProcessing(false));
        keepListOnAnalyzeRef.current = false;
        setFolderSelection(null);
        dispatch(setInfoFiles([]));
        dispatch(setInfoAutoAnalyze(false));
        dispatch(resetEnrichedFiles());
        setLibraryLoaded(false); // triggers the library reload
    };

    // DESIGN V2 — active-filter chips + the tool header. Pure presentation over
    // the existing state; every click lands on a handler the classic layout uses.
    const v2ActiveFilters = designV2 ? [
        (bpmMin > libBounds.bpm[0] || bpmMax < libBounds.bpm[1]) && {
            key: 'bpm', label: `BPM ${bpmMin}–${bpmMax}`,
            clear: () => { setBpmMin(libBounds.bpm[0]); setBpmMax(libBounds.bpm[1]); },
        },
        (loudnessMin > libBounds.loudness[0] || loudnessMax < libBounds.loudness[1]) && {
            key: 'loudness', label: `Loudness ${loudnessMin} to ${loudnessMax} LUFS`,
            clear: () => { setLoudnessMin(libBounds.loudness[0]); setLoudnessMax(libBounds.loudness[1]); },
        },
        (releaseDateMin > libBounds.year[0] || releaseDateMax < libBounds.year[1]) && {
            key: 'year', label: `Year ${releaseDateMin}–${releaseDateMax}`,
            clear: () => { setReleaseDateMin(libBounds.year[0]); setReleaseDateMax(libBounds.year[1]); },
        },
        selectedKeys.length > 0 && {
            key: 'keys', label: `Key ${selectedKeys.map((k) => formatKeyDisplay(k, keyNotation)).join(' · ')}`,
            clear: () => setSelectedKeys([]),
        },
        hideMissingFiles && { key: 'missing', label: 'Hiding missing files', clear: () => setHideMissingFiles(false) },
        backupFilter && { key: 'backup', label: backupFilter === 'stored' ? 'Backed up' : 'Not backed up', clear: () => setBackupFilter(null) },
    ].filter(Boolean) : [];
    const clearAllFiltersV2 = () => { v2ActiveFilters.forEach((f) => f.clear()); userAdjustedFiltersRef.current = false; };

    function renderV2Header() {
        const emptyCount = enrichedFiles.filter((f) => getFillNeeds(f).fillable).length;
        const missingCount = enrichedFiles.filter((f) => f?.result?.fileNotFound === true).length;
        const fillDisabled = infoIsProcessing || emptyCount === 0;
        const sourceLabel = folderSelection
            ? (folderSelection.path || '').split(/[\\/]/).filter(Boolean).pop() || 'Folder'
            : selectedPlaylistName || (infoFiles.length > 0 ? (infoFiles.length === 1 ? formatPath(infoFiles[0]) : `${infoFiles.length} files`) : 'All tracks');
        const sourceIcon = folderSelection
            ? <FolderIcon width={14} height={14} />
            : selectedPlaylistName
                ? <PlaylistIcon width={14} height={14} />
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13M9 18c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zm12-2c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" /></svg>;
        const closeMenus = () => { setV2Menu(null); setV2PlaylistsOpen(false); };
        const visiblePlaylists = playlistList.filter((pl) => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase()));
        return (
            <div className="v2-lib-head" ref={v2HeaderRef}>
                <ToolHeader
                    className="v2-lib-tool-header"
                    source={(
                        <div className="v2-lib-menu-wrap">
                            <SourceChip
                                icon={sourceIcon}
                                label={sourceLabel}
                                count={enrichedFiles.length > 0 ? enrichedFiles.length.toLocaleString() : null}
                                title="Choose what the library shows"
                                onClick={() => { setV2PlaylistsOpen(false); setV2Menu((m) => (m === 'source' ? null : 'source')); }}
                            />
                            {v2Menu === 'source' && (
                                <div className="v2-popover v2-lib-menu left" role="menu">
                                    <button type="button" className="v2-lib-menu-item" onClick={() => { closeMenus(); handleChooseDir(); }}>
                                        <FolderIcon width={14} height={14} />Select folder…
                                    </button>
                                    <button
                                        type="button"
                                        className={`v2-lib-menu-item${v2PlaylistsOpen ? ' on' : ''}`}
                                        onClick={() => {
                                            const next = !v2PlaylistsOpen;
                                            setV2PlaylistsOpen(next);
                                            if (next) { setPlaylistSearch(''); loadPlaylistListV2(); }
                                        }}
                                    >
                                        <PlaylistIcon width={14} height={14} />Select playlist
                                        <span className="v2-faint" style={{ marginLeft: 'auto' }}>{v2PlaylistsOpen ? '▴' : '▾'}</span>
                                    </button>
                                    {v2PlaylistsOpen && (
                                        <div className="v2-lib-menu-sub">
                                            <input
                                                type="text"
                                                className="v2-lib-menu-search"
                                                value={playlistSearch}
                                                onChange={(e) => setPlaylistSearch(e.target.value)}
                                                placeholder="Search playlists…"
                                                autoFocus
                                            />
                                            <div className="v2-lib-menu-list">
                                                {playlistsLoading ? (
                                                    <div className="v2-faint v2-lib-menu-note">Loading…</div>
                                                ) : visiblePlaylists.length === 0 ? (
                                                    <div className="v2-faint v2-lib-menu-note">No playlists found</div>
                                                ) : visiblePlaylists.map((pl) => (
                                                    <button key={pl.id} type="button" className="v2-lib-menu-item" title={pl.name} onClick={() => { closeMenus(); handlePlaylistSelect(pl); }}>
                                                        <span className="v2-lib-menu-ellipsis">{pl.name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <button type="button" className="v2-lib-menu-item" disabled={infoIsProcessing} onClick={() => { closeMenus(); handleLoadAllTracks(); }}>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13M9 18c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zm12-2c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z" /></svg>All tracks
                                    </button>
                                    {folderSelection && (
                                        <>
                                            <hr className="v2-hr" />
                                            <div className="v2-lib-menu-note" title={folderSelection.path}>
                                                <span className="v2-faint">Folder</span>
                                                <span className="v2-lib-menu-ellipsis">{folderSelection.path}</span>
                                                <span className="v2-faint">{folderSelection.scanning ? 'Scanning…' : infoIsProcessing ? 'Analyzing…' : `${enrichedFiles.length} track${enrichedFiles.length === 1 ? '' : 's'}`}</span>
                                            </div>
                                            {enrichedFiles.length > 0 && !infoIsProcessing && (
                                                <>
                                                    <button type="button" className="v2-lib-menu-item" onClick={() => { closeMenus(); handleAddFolderToLibrary(); }}>Add to library</button>
                                                    <button type="button" className="v2-lib-menu-item" onClick={() => { closeMenus(); handleCreatePlaylistFromFolder(); }}>Create playlist from folder</button>
                                                </>
                                            )}
                                            <button type="button" className="v2-lib-menu-item" onClick={() => { closeMenus(); handleClearFolderSelection(); }}>Clear folder</button>
                                        </>
                                    )}
                                    {infoFiles.length > 0 && !folderSelection && (
                                        <>
                                            <hr className="v2-hr" />
                                            <button type="button" className="v2-lib-menu-item" onClick={() => { closeMenus(); handleClearSelection(); }}>Clear selection</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                    actions={(
                        <>
                            {enrichedFiles.length > 0 && (
                                <button
                                    type="button"
                                    className="v2-btn primary"
                                    onClick={openFillPicker}
                                    disabled={fillDisabled}
                                    title={emptyCount === 0 ? 'All possible values are already filled' : `${emptyCount} tracks are missing at least one shown value — pick which ones to fill`}
                                >
                                    {emptyCount > 0 ? `Fill empty values (${emptyCount})` : 'Fill empty values'}
                                </button>
                            )}
                            <button type="button" className={`v2-btn quiet${filtersExpanded ? ' on' : ''}`} title="Choose columns and analysis settings" onClick={() => { closeMenus(); setFiltersExpanded((o) => !o); }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16M15 4v16" /></svg>
                                Columns
                            </button>
                            <div className="v2-lib-menu-wrap">
                                <button type="button" className={`v2-btn quiet icon${v2Menu === 'more' ? ' on' : ''}`} aria-label="More" title="More" onClick={() => setV2Menu((m) => (m === 'more' ? null : 'more'))}>
                                    <svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
                                </button>
                                {v2Menu === 'more' && (
                                    <div className="v2-popover v2-lib-menu" role="menu">
                                        {missingCount > 0 && (
                                            <button type="button" className="v2-lib-menu-item" disabled={infoIsProcessing} onClick={() => { closeMenus(); openCleanupModal(); }}>
                                                Fix missing files ({missingCount})
                                            </button>
                                        )}
                                        <hr className="v2-hr" />
                                        <button type="button" className="v2-lib-menu-item danger" disabled={infoIsProcessing} title="Clears saved BPM, Key, and analysis data so tracks are freshly analyzed next time" onClick={() => { closeMenus(); handleClearSavedResults(); }}>
                                            Clear saved results
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                >
                    <div className="v2-lib-filter-box"><TabFilterBox placeholder="Filter tracks…" fullWidth tabKey="library" /></div>
                    <button
                        type="button"
                        className={`v2-btn${filtersExpanded ? ' on' : ''}`}
                        aria-expanded={filtersExpanded}
                        onClick={() => setFiltersExpanded((o) => !o)}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                        Filters{v2ActiveFilters.length > 0 && <span className="v2-faint v2-num">· {v2ActiveFilters.length}</span>}
                    </button>
                    <PageTabs
                        className="v2-lib-tabs"
                        items={[{ key: 'tracks', label: 'Tracks' }, { key: 'analytics', label: 'Analytics' }]}
                        value={v2View}
                        onChange={setV2View}
                    />
                </ToolHeader>
                {v2ActiveFilters.length > 0 && (
                    <div className="v2-lib-chips">
                        {v2ActiveFilters.map((f) => (
                            <button key={f.key} type="button" className="v2-chip sm" onClick={f.clear} title="Remove this filter">
                                {f.label}<span aria-hidden="true">×</span>
                            </button>
                        ))}
                        <button type="button" className="v2-btn quiet sm" onClick={clearAllFiltersV2}>Clear</button>
                    </div>
                )}
                <div className="v2-lib-progress">
                    <ProgressBarWithStop
                        isProcessing={infoIsProcessing}
                        progress={infoProgress}
                        stage={analysisStage}
                        handleStopProcessing={handleStopProcessing}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            className={`tab-container ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <div className="lower-part" style={{ height: '100%' }}>
                <div className="log-container">
                    <div className="files-container comparison-view" data-v2-view={designV2 ? v2View : undefined}>
                        {designV2 && renderV2Header()}
                        <div className="comparison-container">
                            <div className="comparison-column actions-comparison-column" data-v2-col={designV2 ? 'actions' : undefined} style={{
                                // Fixed comfortable width so the (always-shown) labels have room
                                // to wrap to 2 lines instead of truncating; collapses via the ◀ arrow.
                                flex: actionsExpanded ? '0 0 172px' : 0.08,
                                transition: 'flex 0.3s ease',
                                maxWidth: actionsExpanded ? '172px' : 'none'
                            }}>
                                <h3 className="comparison-title" style={{ position: 'relative', padding: actionsExpanded ? '' : '0', display: actionsExpanded ? 'block' : 'flex', alignItems: actionsExpanded ? 'normal' : 'center', justifyContent: actionsExpanded ? 'normal' : 'center', height: actionsExpanded ? 'auto' : '100%' }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setActionsExpanded(!actionsExpanded); }}
                                        style={{
                                            position: 'absolute',
                                            top: actionsExpanded ? '4px' : '5px',
                                            right: actionsExpanded ? '8px' : '50%',
                                            transform: actionsExpanded ? 'none' : 'translateX(50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#4CC2F1',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            padding: '0',
                                            margin: '0',
                                            lineHeight: '1',
                                            minWidth: 'auto',
                                            height: 'auto',
                                            zIndex: 10
                                        }}
                                        title={actionsExpanded ? 'Minimize' : 'Expand'}
                                    >
                                        {actionsExpanded ? '◀' : '▶'}
                                    </button>
                                    <span style={{ writingMode: actionsExpanded ? 'horizontal-tb' : 'vertical-rl', transform: actionsExpanded ? 'none' : 'rotate(180deg)', transition: 'all 0.3s ease', display: 'inline-block', marginTop: actionsExpanded ? '0' : '0' }}>Actions</span>
                                </h3>
                                {actionsExpanded && <div className="file-list-container" style={{ background: 'var(--color-panel-bg)', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {/* Selected folder path (drag-add transient; folder-select uses its own card) */}
                                    {infoFiles.length > 0 && !folderSelection && (
                                        <div style={{
                                            padding: '12px',
                                            background: 'rgba(76, 194, 241, 0.08)',
                                            border: '1px solid rgba(76, 194, 241, 0.2)',
                                            borderRadius: '6px',
                                            marginBottom: '8px',
                                            position: 'relative'
                                        }}>
                                            <button
                                                onClick={handleClearSelection}
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    right: '8px',
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#FFFFFF',
                                                    fontSize: '14px',
                                                    cursor: 'pointer',
                                                    padding: '2px 4px',
                                                    lineHeight: '1',
                                                    borderRadius: '3px',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '18px',
                                                    height: '18px',
                                                    margin: 0
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.background = 'transparent';
                                                }}
                                                title="Clear selection"
                                            >
                                                ✕
                                            </button>
                                            <div style={{ fontSize: '11px', color: '#4CC2F1', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', paddingRight: '24px' }}>
                                                {selectedPlaylistName ? 'Playlist' : 'Selected Folder'}
                                            </div>
                                            <div
                                                ref={folderPathRef}
                                                style={{
                                                    fontSize: '12px',
                                                    color: 'var(--color-text-primary)',
                                                    lineHeight: '1.5',
                                                    paddingRight: '24px',
                                                    wordBreak: 'break-all',
                                                    overflowWrap: 'break-word',
                                                    maxHeight: '3em',
                                                    overflow: 'hidden',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical'
                                                }}
                                            >
                                                {selectedPlaylistName || (infoFiles.length === 1 ? formatPath(infoFiles[0]) : `${infoFiles.length} files`)}
                                            </div>
                                            {enrichedFiles.length > 0 && (
                                                <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>
                                                    {enrichedFiles.length} tracks analyzed
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Selected-folder card: actions only — does NOT load the folder into the list */}
                                    {folderSelection && (
                                        <div style={{ padding: '12px', background: 'rgba(76, 194, 241, 0.08)', border: '1px solid rgba(76, 194, 241, 0.2)', borderRadius: '6px', marginBottom: '8px', position: 'relative' }}>
                                            <button
                                                onClick={handleClearFolderSelection}
                                                title="Clear folder"
                                                style={{ position: 'absolute', top: '8px', right: '8px', background: 'transparent', border: 'none', color: '#FFFFFF', fontSize: '14px', cursor: 'pointer', padding: '2px 4px', lineHeight: 1 }}
                                            >✕</button>
                                            <div style={{ fontSize: '11px', color: '#4CC2F1', marginBottom: '6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', paddingRight: '24px' }}>Selected Folder</div>
                                            <div
                                                title={folderSelection.path}
                                                style={{ fontSize: '12px', color: 'var(--color-text-primary)', lineHeight: '1.5', paddingRight: '24px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', direction: 'rtl', textAlign: 'left' }}
                                            ><span dir="ltr">{folderSelection.path}</span></div>
                                            <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)', marginTop: '6px' }}>
                                                {folderSelection.scanning ? 'Scanning…' : infoIsProcessing ? 'Analyzing…' : `${enrichedFiles.length} track${enrichedFiles.length === 1 ? '' : 's'}`}
                                            </div>
                                            {enrichedFiles.length > 0 && !infoIsProcessing && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '10px' }}>
                                                <button
                                                    onClick={handleAddFolderToLibrary}
                                                    style={{ padding: '6px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: 'none', background: 'var(--color-accent, #4CC2F1)', color: '#0a0a0a', transition: 'filter 0.15s ease, box-shadow 0.15s ease' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.12)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(76,194,241,0.35)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                                                >Add to library</button>
                                                <button
                                                    onClick={handleCreatePlaylistFromFolder}
                                                    style={{ padding: '6px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '6px', cursor: 'pointer', border: '1px solid var(--color-accent, #4CC2F1)', background: 'transparent', color: 'var(--color-accent, #4CC2F1)', transition: 'background 0.15s ease' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(76,194,241,0.14)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                                >Create playlist from folder</button>
                                            </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Select Folder Button */}
                                    <button
                                        onClick={handleChooseDir}
                                        title="Select Folder"
                                        style={{
                                            width: '100%',
                                            padding: actionsIconOnly ? '10px 4px' : '12px 16px',
                                            background: 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.08) 100%)',
                                            border: '1px solid rgba(76, 194, 241, 0.3)',
                                            borderRadius: '6px',
                                            color: '#4CC2F1',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            minWidth: 0
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.25) 0%, rgba(76, 194, 241, 0.15) 100%)';
                                            e.target.style.borderColor = 'rgba(76, 194, 241, 0.5)';
                                            e.target.style.transform = 'translateY(-1px)';
                                            e.target.style.boxShadow = '0 4px 12px rgba(76, 194, 241, 0.3)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.08) 100%)';
                                            e.target.style.borderColor = 'rgba(76, 194, 241, 0.3)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                                        }}
                                    >
                                        <FolderIcon width={16} height={16} style={{flexShrink: 0}} />{!actionsIconOnly && ' Select Folder'}
                                    </button>

                                    {/* Select Playlist Dropdown */}
                                    <div ref={playlistDropdownRef} style={{position: 'relative', width: '100%'}}>
                                        <button
                                            ref={playlistBtnRef}
                                            onClick={handlePlaylistDropdownToggle}
                                            title="Select Playlist"
                                            style={{
                                                width: '100%',
                                                padding: actionsIconOnly ? '10px 4px' : '12px 16px',
                                                background: playlistDropdownOpen
                                                    ? 'linear-gradient(135deg, rgba(76, 194, 241, 0.25) 0%, rgba(76, 194, 241, 0.15) 100%)'
                                                    : 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.08) 100%)',
                                                border: `1px solid rgba(76, 194, 241, ${playlistDropdownOpen ? '0.5' : '0.3'})`,
                                                borderRadius: playlistDropdownOpen ? (dropdownOpenUp ? '0 0 6px 6px' : '6px 6px 0 0') : '6px',
                                                color: '#4CC2F1',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                overflow: 'hidden',
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minWidth: 0
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!playlistDropdownOpen) {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.25) 0%, rgba(76, 194, 241, 0.15) 100%)';
                                                    e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.5)';
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(76, 194, 241, 0.3)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!playlistDropdownOpen) {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.08) 100%)';
                                                    e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.3)';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                                                }
                                            }}
                                        >
                                            <PlaylistIcon width={16} height={16} style={{flexShrink: 0}} />
                                            {!actionsIconOnly && 'Select Playlist'}
                                        </button>
                                        {playlistDropdownOpen && (
                                            <div ref={playlistMenuRef} className="playlist-inline-dropdown" style={{
                                                position: 'fixed',
                                                top: dropdownPos.top,
                                                left: dropdownPos.left,
                                                width: dropdownPos.width,
                                                maxHeight: '280px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                background: 'var(--color-bg-primary)',
                                                border: '1px solid rgba(76, 194, 241, 0.3)',
                                                borderRadius: dropdownOpenUp ? '6px 6px 0 0' : '0 0 6px 6px',
                                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                                zIndex: 1000,
                                            }}>
                                                <input
                                                    ref={playlistSearchRef}
                                                    type="text"
                                                    value={playlistSearch}
                                                    onChange={(e) => setPlaylistSearch(e.target.value)}
                                                    placeholder="Search..."
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 12px',
                                                        background: 'rgba(0, 0, 0, 0.2)',
                                                        border: 'none',
                                                        borderBottom: '1px solid rgba(76, 194, 241, 0.15)',
                                                        color: 'var(--color-text-primary)',
                                                        fontSize: '11px',
                                                        outline: 'none',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                                <div style={{overflowY: 'auto', flex: 1}}>
                                                {playlistsLoading ? (
                                                    <div className="dropdown-skel" aria-label="Loading playlists">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="dropdown-skel-option"><span /></div>)}</div>
                                                ) : playlistList.filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase())).length === 0 ? (
                                                    <div style={{padding: '12px', textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '12px'}}>
                                                        No playlists found
                                                    </div>
                                                ) : (
                                                    playlistList
                                                        .filter(pl => !playlistSearch || pl.name.toLowerCase().includes(playlistSearch.toLowerCase()))
                                                        .map((pl) => (
                                                        <button
                                                            key={pl.id}
                                                            onClick={() => handlePlaylistSelect(pl)}
                                                            style={{
                                                                width: '100%',
                                                                padding: '6px 12px',
                                                                background: 'transparent',
                                                                border: 'none',
                                                                borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                                                                color: 'var(--color-text-primary)',
                                                                fontSize: '12px',
                                                                cursor: 'pointer',
                                                                textAlign: 'left',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                transition: 'background 0.15s',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                e.currentTarget.style.background = 'rgba(76, 194, 241, 0.1)';
                                                                const outer = e.currentTarget.querySelector('.playlist-item-name');
                                                                const inner = outer?.querySelector('.playlist-item-text');
                                                                if (outer && inner && inner.offsetWidth > outer.offsetWidth) {
                                                                    const dist = inner.offsetWidth - outer.offsetWidth;
                                                                    inner.style.transition = `transform ${Math.max(1.2, dist / 50)}s ease-in-out 0.4s`;
                                                                    inner.style.transform = `translateX(-${dist}px)`;
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                e.currentTarget.style.background = 'transparent';
                                                                const inner = e.currentTarget.querySelector('.playlist-item-text');
                                                                if (inner) {
                                                                    inner.style.transition = 'transform 0.3s ease';
                                                                    inner.style.transform = 'translateX(0)';
                                                                }
                                                            }}
                                                        >
                                                            <span className="playlist-item-name"><span className="playlist-item-text">{pl.name}</span></span>
                                                        </button>
                                                    ))
                                                )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* My Library Button */}
                                    <button
                                        title="Load all your saved tracks from the local library"
                                        onClick={handleLoadAllTracks}
                                        disabled={infoIsProcessing}
                                        style={{
                                            width: '100%',
                                            padding: actionsIconOnly ? '10px 4px' : '12px 16px',
                                            background: 'linear-gradient(135deg, rgba(76, 194, 241, 0.1) 0%, rgba(76, 194, 241, 0.05) 100%)',
                                            border: '1px solid rgba(76, 194, 241, 0.2)',
                                            borderRadius: '6px',
                                            color: '#4CC2F1',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            cursor: infoIsProcessing ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            overflow: 'hidden',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                            minWidth: 0,
                                            opacity: infoIsProcessing ? 0.5 : 1,
                                        }}
                                        onMouseEnter={(e) => { if (!infoIsProcessing) { e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.2) 0%, rgba(76, 194, 241, 0.1) 100%)'; e.target.style.borderColor = 'rgba(76, 194, 241, 0.4)'; } }}
                                        onMouseLeave={(e) => { if (!infoIsProcessing) { e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.1) 0%, rgba(76, 194, 241, 0.05) 100%)'; e.target.style.borderColor = 'rgba(76, 194, 241, 0.2)'; } }}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18V5l12-2v13M9 18c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3zm12-2c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z"/></svg>
                                        {!actionsIconOnly && 'All Tracks'}
                                    </button>

                                    {/* Fill Empty Values Button */}
                                    {enrichedFiles.length > 0 && (() => {
                                        // Same predicate the fill run uses (getFillNeeds), so the
                                        // count only includes tracks a run can actually fill.
                                        const emptyCount = enrichedFiles.filter(f => getFillNeeds(f).fillable).length;
                                        // Always render the button; grey it out when there's nothing
                                        // to fill (disappearing was confusing).
                                        const fillDisabled = infoIsProcessing || emptyCount === 0;
                                        return (
                                            <button
                                                onClick={openFillPicker}
                                                disabled={fillDisabled}
                                                title={emptyCount === 0 ? 'All possible values are already filled' : `${emptyCount} tracks are missing at least one shown value — pick which ones to fill`}
                                                style={{
                                                    width: '100%',
                                                    padding: actionsIconOnly ? '10px 4px' : '10px 16px',
                                                    background: fillDisabled
                                                        ? 'rgba(255, 255, 255, 0.05)'
                                                        : 'linear-gradient(135deg, rgba(76, 194, 241, 0.1) 0%, rgba(76, 194, 241, 0.05) 100%)',
                                                    border: '1px solid rgba(76, 194, 241, 0.2)',
                                                    borderRadius: '6px',
                                                    color: fillDisabled ? '#666' : '#4CC2F1',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    cursor: fillDisabled ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    overflow: 'hidden',
                                                    opacity: fillDisabled ? 0.5 : 1,
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!fillDisabled) {
                                                        e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.2) 0%, rgba(76, 194, 241, 0.1) 100%)';
                                                        e.target.style.borderColor = 'rgba(76, 194, 241, 0.4)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!fillDisabled) {
                                                        e.target.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.1) 0%, rgba(76, 194, 241, 0.05) 100%)';
                                                        e.target.style.borderColor = 'rgba(76, 194, 241, 0.2)';
                                                    }
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
                                                {!actionsIconOnly && (emptyCount > 0 ? `Fill Empty Values (${emptyCount})` : 'Fill Empty Values')}
                                            </button>
                                        );
                                    })()}

                                    {/* Clean Up Missing Files Button */}
                                    {(() => {
                                        const missingCount = enrichedFiles.filter(f => f?.result?.fileNotFound === true).length;
                                        if (missingCount === 0) return null;
                                        return (
                                            <button
                                                onClick={openCleanupModal}
                                                disabled={infoIsProcessing}
                                                title="Some files referenced by your library no longer exist on disk. Relocate or remove them."
                                                style={{
                                                    width: '100%',
                                                    padding: actionsIconOnly ? '10px 4px' : '10px 16px',
                                                    background: 'rgba(255, 255, 255, 0.05)',
                                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                                    borderRadius: '6px',
                                                    color: '#ccc',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    cursor: infoIsProcessing ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    overflow: 'hidden',
                                                    opacity: infoIsProcessing ? 0.5 : 1,
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!infoIsProcessing) {
                                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!infoIsProcessing) {
                                                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                                    }
                                                }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="13"/><circle cx="12" cy="16.5" r="0.5" fill="var(--color-danger)"/></svg>
                                                {!actionsIconOnly && (
                                                    <span style={{ textAlign: 'center', lineHeight: 1.25, flex: '1 1 0', minWidth: 0, wordBreak: 'break-word', hyphens: 'auto' }}>
                                                        Fix Missing Files ({missingCount})
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })()}

                                    {/* Clear Cache Button */}
                                    <button
                                        title="Clears saved BPM, Key, and analysis data so tracks are freshly analyzed next time"
                                        onClick={handleClearSavedResults}
                                        disabled={infoIsProcessing}
                                        style={{
                                            width: '100%',
                                            padding: actionsIconOnly ? '10px 4px' : '10px 16px',
                                            background: clearBtnBg,
                                            border: `1px solid ${clearBtnBorder}`,
                                            borderRadius: '6px',
                                            color: clearBtnColor,
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            cursor: infoIsProcessing ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s ease',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            overflow: 'hidden',
                                            opacity: infoIsProcessing ? 0.5 : 1,
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!infoIsProcessing) {
                                                e.target.style.background = 'rgba(255, 100, 100, 0.1)';
                                                e.target.style.borderColor = 'rgba(255, 100, 100, 0.3)';
                                                e.target.style.color = '#ff6b6b';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!infoIsProcessing) {
                                                e.target.style.background = clearBtnBg;
                                                e.target.style.borderColor = clearBtnBorder;
                                                e.target.style.color = clearBtnColor;
                                            }
                                        }}
                                    >
                                        {actionsIconOnly ? (
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                                        ) : 'Clear Saved Results'}
                                    </button>

                                    {/* Progress Bar */}
                                    <ProgressBarWithStop
                                        isProcessing={infoIsProcessing}
                                        progress={infoProgress}
                                        stage={analysisStage}
                                        handleStopProcessing={handleStopProcessing}
                                    />
                                </div>}
                            </div>
                            <div className="comparison-divider"></div>
                            <div className="comparison-column" data-v2-col={designV2 ? 'files' : undefined} style={{
                                flex: filesExpanded ? 2.5 : 0.08,
                                transition: 'flex 0.3s ease',
                                minWidth: 0
                            }}>
                                <h3 className="comparison-title" style={{ position: 'relative', padding: filesExpanded ? '' : '0', display: filesExpanded ? 'block' : 'flex', alignItems: filesExpanded ? 'normal' : 'center', justifyContent: filesExpanded ? 'normal' : 'center', height: filesExpanded ? 'auto' : '100%' }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setFilesExpanded(!filesExpanded); }}
                                        style={{
                                            position: 'absolute',
                                            top: filesExpanded ? '4px' : '5px',
                                            right: filesExpanded ? '8px' : '50%',
                                            transform: filesExpanded ? 'none' : 'translateX(50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#4CC2F1',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            padding: '0',
                                            margin: '0',
                                            lineHeight: '1',
                                            minWidth: 'auto',
                                            height: 'auto',
                                            zIndex: 10
                                        }}
                                        title={filesExpanded ? 'Minimize' : 'Expand'}
                                    >
                                        {filesExpanded ? '◀' : '▶'}
                                    </button>
                                    <span style={{ writingMode: filesExpanded ? 'horizontal-tb' : 'vertical-rl', transform: filesExpanded ? 'none' : 'rotate(180deg)', transition: 'all 0.3s ease', display: 'inline-block', marginTop: filesExpanded ? '0' : '0' }}>Files</span>
                                    {/* Live backup progress, always visible while the files
                                        engine is uploading. Sits left of the collapse button. */}
                                    {filesExpanded && backupRun.running && backupRun.total > 0 && (
                                        <span
                                            className="lib-backup-progress-line"
                                            title="Open Cloud Backup"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                track('backup_progress_line_clicked', { from: 'library' });
                                                dispatch(setActiveTab('backup'));
                                            }}
                                        >
                                            <span className="lib-backup-progress-text">
                                                <CloudBackupGlyph variant="up" size={12} />
                                                {`Backing up — ${backupRun.done.toLocaleString()} of ${backupRun.total.toLocaleString()} tracks`}
                                                {backupRun.failed > 0 && ` · ${backupRun.failed.toLocaleString()} failed`}
                                            </span>
                                            <span className="lib-backup-progress-bar">
                                                <span style={{ width: `${completionPct(backupRun.done, backupRun.total) ?? 0}%` }} />
                                            </span>
                                        </span>
                                    )}
                                    {/* Backup-off notice: same slot, one sentence with the
                                        count. Never together with the progress line (a run
                                        means backup is on). */}
                                    {showBackupLine && (() => {
                                        const copy = libraryBackupLineCopy({ localTracks: localTrackCount, canAccess: canBackup });
                                        return (
                                            <span
                                                className="lib-backup-progress-line lib-backup-line--off"
                                                role="button"
                                                tabIndex={0}
                                                title={canBackup ? 'Open Cloud Backup' : 'See what GOAT includes'}
                                                onClick={onBackupLineClick}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onBackupLineClick(e); }}
                                            >
                                                <span className="lib-backup-progress-text">
                                                    <CloudBackupGlyph variant="slash" size={12} />
                                                    <span className="lib-backup-line-status">{copy.status}</span>
                                                    <span className="lib-backup-line-action">{copy.action}</span>
                                                    <button
                                                        type="button"
                                                        className="lib-backup-line-hide"
                                                        title="Hide this"
                                                        aria-label="Hide this notice"
                                                        onClick={onBackupLineHide}
                                                    >×</button>
                                                </span>
                                            </span>
                                        );
                                    })()}
                                </h3>
                                {filesExpanded && <>
                                    {/* Dual-scroll table: the row List owns BOTH scrollbars so they
                                        stay pinned to the panel's edges; rows have min-width = sum of
                                        columns so the List scrolls horizontally, and this frozen header
                                        viewport mirrors the List's horizontal scrollLeft (synced below). */}
                                    <div className="files-table" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                    {filteredFiles.length > 0 && (
                                        <div className="files-header-viewport" ref={headerScrollRef} style={{ overflowX: 'hidden', flexShrink: 0 }}>
                                        <div
                                            ref={(el) => { headerInnerRef.current = el; if (el) syncHeaderScroll(libraryListRef.current?.element?.scrollLeft || 0); }}
                                            style={{ minWidth: `${tableMinWidth}px` }}
                                        >
                                        <div className="visualize-header" title="Right-click a track for more options">
                                                <span style={{ width: '54px', minWidth: '54px', flexShrink: 0, marginRight: '8px' }}></span>
                                                <span
                                                    style={{ position: 'relative', width: '40px', minWidth: '40px', flexShrink: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', borderRight: '1px solid rgba(255,255,255,0.08)', paddingRight: '6px', boxSizing: 'border-box', ...headActive('sortOrder') }}
                                                    onClick={() => sortColumn('sortOrder')}
                                                    title="Track order — click to sort; when active, drag any row to reorder"
                                                >#{sortArrow('sortOrder')}</span>
                                                {backupMarks.active && (
                                                    <span
                                                        style={{ position: 'relative', width: '30px', minWidth: '30px', flexShrink: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid rgba(255,255,255,0.08)', boxSizing: 'border-box', color: 'var(--color-text-secondary)', ...headActive('backup') }}
                                                        onClick={() => sortColumn('backup')}
                                                        title="Cloud backup — click to sort"
                                                    ><CloudBackupGlyph variant="outline" size={13} />{sortArrow('backup')}</span>
                                                )}
                                                <span style={{ width: `${artistWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('artist') }} onClick={() => sortColumn('artist')}>
                                                    Artist{sortArrow('artist')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('artist', artistWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>
                                                <span style={{ width: `${titleWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('trackName') }} onClick={() => sortColumn('trackName')}>
                                                    Title{sortArrow('trackName')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('title', titleWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>
                                                {analyzeBpm && <span style={{ width: `${bpmWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('bpm') }} onClick={() => sortColumn('bpm')}>
                                                    BPM{sortArrow('bpm')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('bpm', bpmWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeKey && <span style={{ width: `${keyWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('key') }} onClick={() => sortColumn('key')}>
                                                    Key{sortArrow('key')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('key', keyWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {showEnergyColumn && <span style={{ width: `${energyWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('energy') }} onClick={() => sortColumn('energy')}>
                                                    Energy{sortArrow('energy')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('energy', energyWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {/* Curve: not sortable — shapes have no natural order. */}
                                                {showCurveColumn && <span title="The track in 12 slices, same 1–10 scale as Energy" style={{ width: `${curveWidth}px`, flexShrink: 1, borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative' }}>
                                                    Curve
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('curve', curveWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeGenre && <span style={{ width: `${genreWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('genre') }} onClick={() => sortColumn('genre')}>
                                                    Genre{sortArrow('genre')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('genre', genreWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeDate && <span style={{ width: `${dateWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: '1px solid rgba(255,255,255,0.08)', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('releaseDate') }} onClick={() => sortColumn('releaseDate')}>
                                                    Date{sortArrow('releaseDate')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('date', dateWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeLabel && <span style={{ width: `${labelWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: (analyzeLoudness || analyzeCompatibility) ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('label') }} onClick={() => sortColumn('label')}>
                                                    Label{sortArrow('label')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('label', labelWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeLoudness && <span style={{ width: `${loudnessWidth}px`, flexShrink: 1, cursor: 'pointer', borderRight: analyzeCompatibility ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingLeft: '8px', paddingRight: '8px', position: 'relative', ...headActive('loudness') }} onClick={() => sortColumn('loudness')}>
                                                    LUFS{sortArrow('loudness')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('loudness', loudnessWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                                {analyzeCompatibility && <span style={{ width: `${compatibilityWidth}px`, flexShrink: 1, cursor: 'pointer', paddingLeft: '8px', position: 'relative', ...headActive('compatibility') }} onClick={() => sortColumn('compatibility')}>
                                                    Compat{sortArrow('compatibility')}
                                                    <span
                                                        style={{
                                                            position: 'absolute',
                                                            right: '-4px',
                                                            top: 0,
                                                            bottom: 0,
                                                            width: '8px',
                                                            cursor: 'col-resize',
                                                            background: 'transparent',
                                                            zIndex: 10
                                                        }}
                                                        onMouseDown={(e) => { e.stopPropagation(); handleColumnResize('compatibility', compatibilityWidth, e); }}
                                                        onMouseEnter={(e) => e.target.style.background = 'rgba(76, 194, 241, 0.3)'}
                                                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                    ></span>
                                                </span>}
                                        </div>
                                        </div>
                                        </div>
                                    )}
                                    <div className="file-list-container tree-view-container" style={{ background: 'var(--color-panel-bg)', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                    {!listReady && filteredFiles.length > 0 ? (
                                        // Defer the heavy list render by one paint frame so the click into
                                        // Library shows a spinner instantly. After this, react-window's
                                        // virtualization keeps the row-render cost bounded regardless of
                                        // library size (only ~20 rows in DOM at any time).
                                        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                                            <div className="loading-spinner" style={{ width: '20px', height: '20px', margin: '0 auto 12px' }}></div>
                                            <div>Loading {filteredFiles.length.toLocaleString()} tracks…</div>
                                        </div>
                                    ) : filteredFiles.length > 0 ? (
                                        // Virtualized row list. Replaces a synchronous map of all rows that
                                        // produced ~13k DOM nodes for a 664-track library and a 3-5s freeze
                                        // on every Library-tab click. With react-window, only the visible
                                        // rows + a small overscan are rendered; row-render cost is bounded
                                        // to the viewport height regardless of total row count.
                                        //
                                        // rowHeight=32: matches the `.tree-node` CSS (5px padding × 2 +
                                        // ~14px line height for 12px text + 1px margin × 2). Fixed-height
                                        // is correct because every row has `whiteSpace: nowrap` +
                                        // overflow:hidden so no row ever wraps.
                                        <List
                                            // The List owns BOTH scrollbars (panel-width, so they sit at
                                            // the panel's edges). Rows are min-width = tableMinWidth so it
                                            // scrolls horizontally; on scroll we mirror scrollLeft into the
                                            // frozen header viewport so the header tracks the columns.
                                            listRef={libraryListRef}
                                            onScroll={(e) => {
                                                _savedListScroll.top = e.currentTarget.scrollTop;
                                                _savedListScroll.left = e.currentTarget.scrollLeft;
                                                syncHeaderScroll(e.currentTarget.scrollLeft);
                                            }}
                                            style={{ height: '100%', width: '100%', overflowX: 'scroll', overflowY: 'scroll', overscrollBehaviorX: 'contain' }}
                                            rowCount={filteredFiles.length}
                                            rowHeight={designV2 ? 36 : 32}
                                            overscanCount={10}
                                            rowProps={{
                                                files: filteredFiles,
                                                // Not read by rows — its bumps invalidate
                                                // react-window's shallow-equality memo so
                                                // visible rows repaint as thumb batches land.
                                                artVersion,
                                                tableMinWidth,
                                                handleContextMenu,
                                                getPlaybackIcon,
                                                handleAddToQueue,
                                                handlePlayClick,
                                                keyNotation,
                                                artistWidth, titleWidth, bpmWidth, keyWidth,
                                                loudnessWidth, energyWidth, curveWidth, genreWidth, dateWidth, labelWidth,
                                                compatibilityWidth,
                                                analyzeBpm, analyzeKey, analyzeLoudness, analyzeGenre,
                                                analyzeDate, analyzeLabel, analyzeCompatibility,
                                                artistPartsMap,
                                                rankMap,
                                                showFullDate,
                                                reorderEnabled,
                                                dragOverIndex,
                                                onRowDragStart: handleRowDragStart,
                                                onRowDragOver: handleRowDragOver,
                                                onRowDrop: handleRowDrop,
                                                onRowDragEnd: handleRowDragEnd,
                                                onRemoveFromLibrary: removeFromLibraryByPath,
                                                onAddToPlaylist: openPlaylistPickerFor,
                                                onLocateFile: locateFileByPath,
                                                rowCtx,
                                                selectedPaths,
                                                onRowSelectClick: handleRowSelectClick,
                                                focusedPath: focusFlashPath,
                                                backupMarks,
                                                backupRun,
                                                analyzingFiles,
                                                showEnergyColumn,
                                                showCurveColumn,
                                            }}
                                            rowComponent={LibraryRow}
                                        />
                                    ) : libraryLoading ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: '12px', opacity: 0.7 }}>
                                            <span className="loading-spinner" style={{ width: '20px', height: '20px' }}></span>
                                            <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>Loading library...</span>
                                        </div>
                                    ) : (infoProgress >= 100 && !infoIsProcessing && enrichedFiles.length === 0) ? (
                                        <div className="no-files-message">
                                            <AudioFileIcon className="no-files-icon" />
                                            <span>No audio files were found in folder</span>
                                        </div>
                                    ) : (libraryLoaded && enrichedFiles.length === 0 && designV2) ? (
                                        // v2: the tool pages' empty state (title, what to do, one amber
                                        // pill); Select Folder lives in the source menu, so the pill is it.
                                        <div className="v2-lib-empty">
                                            <AudioFileIcon className="v2-lib-empty-icon" />
                                            <div className="v2-lib-empty-title">Your library is empty</div>
                                            <p className="v2-lib-empty-text">Sync from Rekordbox, Serato, Traktor or VirtualDJ in the sidebar, or drag tracks and folders anywhere here.</p>
                                            <button type="button" className="v2-lib-empty-btn" onClick={handleChooseDir}>Select folder</button>
                                        </div>
                                    ) : (libraryLoaded && enrichedFiles.length === 0) ? (
                                        // Genuinely empty library (fresh install / nothing imported yet).
                                        // Point at the real import paths — the DJ-software sync in the
                                        // sidebar + drag-and-drop — instead of leaving a blank panel.
                                        <div className="no-files-message" style={{
                                            margin: '20px',
                                            padding: '48px 28px',
                                            border: '2px dashed rgba(0, 188, 212, 0.35)',
                                            borderRadius: '12px',
                                            background: 'rgba(0, 188, 212, 0.03)'
                                        }}>
                                            <AudioFileIcon className="no-files-icon" />
                                            <span style={{ fontSize: '15px', fontWeight: 600, opacity: 0.85 }}>Your library is empty</span>
                                            <span style={{ color: 'var(--color-text-secondary)', fontSize: '12.5px', maxWidth: '300px', lineHeight: 1.6, textAlign: 'center' }}>
                                                Sync from Rekordbox, Serato, Traktor or VirtualDJ in the sidebar, use <strong style={{ fontWeight: 600, opacity: 0.9 }}>Select Folder</strong> to scan your files, or drag tracks and folders anywhere here.
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="empty-log"></div>
                                    )}
                                </div>
                                </div>
                                </>}
                            </div>
                            <div className="comparison-divider" style={{ order: 3 }}></div>
                            <div className="comparison-column" data-v2-col={designV2 ? 'analytics' : undefined} style={{ order: 4, flex: analyticsExpanded ? 1 : 0.08, transition: 'flex 0.3s ease' }}>
                                <h3 className="comparison-title" style={{ position: 'relative', padding: analyticsExpanded ? '' : '0', display: analyticsExpanded ? 'block' : 'flex', alignItems: analyticsExpanded ? 'normal' : 'center', justifyContent: analyticsExpanded ? 'normal' : 'center', height: analyticsExpanded ? 'auto' : '100%' }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setAnalyticsExpanded(!analyticsExpanded); }}
                                        style={{
                                            position: 'absolute',
                                            top: analyticsExpanded ? '4px' : '5px',
                                            right: analyticsExpanded ? '8px' : '50%',
                                            transform: analyticsExpanded ? 'none' : 'translateX(50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#4CC2F1',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            padding: '0',
                                            margin: '0',
                                            lineHeight: '1',
                                            minWidth: 'auto',
                                            height: 'auto',
                                            zIndex: 10
                                        }}
                                        title={analyticsExpanded ? 'Minimize' : 'Expand'}
                                    >
                                        {analyticsExpanded ? '◀' : '▶'}
                                    </button>
                                    <span style={{ writingMode: analyticsExpanded ? 'horizontal-tb' : 'vertical-rl', transform: analyticsExpanded ? 'none' : 'rotate(180deg)', transition: 'all 0.3s ease', display: 'inline-block', marginTop: analyticsExpanded ? '0' : '0' }}>Analytics</span>
                                </h3>
                                {analyticsExpanded && !chartsReady && (
                                    // Defer the Recharts (BarChart + PieChart) mount so it doesn't
                                    // co-render with the row list on tab open. Recharts does heavy
                                    // SVG layout/animation work at mount and was the dominant cause
                                    // of the residual Library-tab freeze after the row list got
                                    // virtualized. With this gate, the row list mounts when
                                    // listReady flips; charts mount one paint cycle later when
                                    // chartsReady flips. User sees: spinner → row list visible →
                                    // a beat later → charts visible. No single React commit has to
                                    // render both the rows and Recharts simultaneously.
                                    <div className="file-list-container" style={{ background: 'var(--color-panel-bg)', padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
                                        <div className="loading-spinner" style={{ width: '20px', height: '20px', margin: '0 auto 12px' }}></div>
                                        <div>Loading charts…</div>
                                    </div>
                                )}
                                {analyticsExpanded && chartsReady && <div className="file-list-container" style={{ background: 'var(--color-panel-bg)' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '20px', height: '100%' }}>
                                        {/* Year Distribution Chart */}
                                        <div style={{ flex: 1, minHeight: narrowViewport ? '340px' : '250px', display: 'flex', flexDirection: 'column' }}>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#4CC2F1',
                                                marginBottom: '12px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Year Distribution
                                            </h4>
                                            <div style={{ flex: 1, minHeight: 0 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={groupYears} margin={{ top: 20, right: 10, left: -20, bottom: 5 }}>
                                                    <defs>
                                                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                                            <stop offset="0%" stopColor={designV2 ? 'var(--color-accent)' : '#4CC2F1'} stopOpacity={0.9}/>
                                                            <stop offset="100%" stopColor={designV2 ? 'var(--color-accent)' : '#4CC2F1'} stopOpacity={0.4}/>
                                                        </linearGradient>
                                                    </defs>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                                    <XAxis
                                                        dataKey="year"
                                                        interval={0}
                                                        tick={{ fill: '#888', fontSize: 11 }}
                                                        stroke="rgba(255,255,255,0.1)"
                                                    />
                                                    <YAxis
                                                        tick={{ fill: '#888', fontSize: 11 }}
                                                        stroke="rgba(255,255,255,0.1)"
                                                        allowDecimals={false}
                                                    />
                                                    <Tooltip
                                                        cursor={{ fill: 'rgba(76, 194, 241, 0.1)' }}
                                                        contentStyle={{
                                                            backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                                            border: '1px solid rgba(76, 194, 241, 0.3)',
                                                            borderRadius: '6px',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                        }}
                                                        itemStyle={{
                                                            color: 'var(--color-text-primary)',
                                                            fontSize: '12px',
                                                            fontWeight: 500
                                                        }}
                                                        labelStyle={{
                                                            color: '#4CC2F1',
                                                            fontWeight: 600
                                                        }}
                                                        formatter={(value) => [`${value} tracks`, null]}
                                                    />
                                                    <Bar
                                                        dataKey="count"
                                                        fill="url(#barGradient)"
                                                        radius={[6, 6, 0, 0]}
                                                        maxBarSize={50}
                                                    />
                                                </BarChart>
                                            </ResponsiveContainer>
                                            </div>
                                        </div>

                                        {/* Genre Distribution Chart */}
                                        <div ref={genreSectionRef} style={{ flex: 1, minHeight: narrowViewport ? '340px' : '250px', display: 'flex', flexDirection: 'column' }}>
                                            <h4 style={{
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: '#4CC2F1',
                                                marginBottom: '12px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Genre Distribution
                                            </h4>
                                            <div style={{ flex: 1, minHeight: 0 }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <defs>
                                                            {COLORS.map((color, index) => (
                                                                <linearGradient key={`gradient-${index}`} id={`pieGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                                                                    <stop offset="0%" stopColor={color} stopOpacity={1}/>
                                                                    <stop offset="100%" stopColor={color} stopOpacity={0.7}/>
                                                                </linearGradient>
                                                            ))}
                                                        </defs>
                                                        <Pie
                                                            data={topGenres}
                                                            dataKey="value"
                                                            nameKey="genre"
                                                            cx="50%"
                                                            cy="50%"
                                                            innerRadius="42%"
                                                            outerRadius="70%"
                                                            paddingAngle={2}
                                                            stroke="rgba(0,0,0,0.2)"
                                                            strokeWidth={2}
                                                        >
                                                            {topGenres.map((entry, index) => (
                                                                <Cell
                                                                    key={`cell-${index}`}
                                                                    fill={`url(#pieGradient${index % COLORS.length})`}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <Tooltip
                                                            contentStyle={{
                                                                backgroundColor: 'rgba(30, 30, 30, 0.95)',
                                                                border: '1px solid rgba(76, 194, 241, 0.3)',
                                                                borderRadius: '6px',
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                            }}
                                                            itemStyle={{
                                                                color: 'var(--color-text-primary)',
                                                                fontSize: '12px',
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                            {/* Custom legend below the chart. Visibility is gated on a
                                                post-render measurement (genreLegendVisible) — if the full
                                                list would spill past the section bottom, hide it entirely
                                                rather than show a partial / clipped list. */}
                                            <div ref={genreLegendRef} style={{
                                                display: genreLegendVisible ? 'flex' : 'none',
                                                flexWrap: 'wrap',
                                                justifyContent: 'center',
                                                gap: narrowViewport ? '4px 8px' : '6px 12px',
                                                paddingTop: '12px',
                                                fontSize: narrowViewport ? '10px' : '11px',
                                                lineHeight: 1.3,
                                            }}>
                                                {(() => {
                                                    const total = topGenres.reduce((sum, item) => sum + item.value, 0) || 1;
                                                    const maxLen = narrowViewport ? 10 : 15;
                                                    return topGenres.map((entry, i) => {
                                                        const percentage = Math.round((entry.value / total) * 100);
                                                        const display = entry.genre.length > maxLen
                                                            ? entry.genre.substring(0, maxLen) + '…'
                                                            : entry.genre;
                                                        return (
                                                            <span
                                                                key={i}
                                                                title={`${entry.genre} (${percentage}%)`}
                                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--color-text-secondary)' }}
                                                            >
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    width: narrowViewport ? '6px' : '8px',
                                                                    height: narrowViewport ? '6px' : '8px',
                                                                    borderRadius: '50%',
                                                                    background: COLORS[i % COLORS.length],
                                                                    flexShrink: 0,
                                                                }}/>
                                                                {display} <span style={{ color: designV2 ? 'var(--color-text-tertiary)' : '#666' }}>({percentage}%)</span>
                                                            </span>
                                                        );
                                                    });
                                                })()}
                                            </div>
                                        </div>
                                    </div>
                                </div>}
                            </div>
                            <div className="comparison-divider" style={{ order: 1 }}></div>
                            <div className="comparison-column" data-v2-col={designV2 ? 'filters' : undefined} style={{ order: 2, flex: filtersExpanded ? (narrowViewport ? 1.1 : 0.9) : 0.08 }}>
                                <h3 className="comparison-title" style={{ position: 'relative', padding: filtersExpanded ? '' : '0', display: filtersExpanded ? 'block' : 'flex', alignItems: filtersExpanded ? 'normal' : 'center', justifyContent: filtersExpanded ? 'normal' : 'center', height: filtersExpanded ? 'auto' : '100%' }}>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setFiltersExpanded(!filtersExpanded); }}
                                        style={{
                                            position: 'absolute',
                                            top: filtersExpanded ? '4px' : '5px',
                                            right: filtersExpanded ? '8px' : '50%',
                                            transform: filtersExpanded ? 'none' : 'translateX(50%)',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#4CC2F1',
                                            cursor: 'pointer',
                                            fontSize: '11px',
                                            padding: '0',
                                            margin: '0',
                                            lineHeight: '1',
                                            minWidth: 'auto',
                                            height: 'auto',
                                            zIndex: 10
                                        }}
                                        title={filtersExpanded ? 'Minimize' : 'Expand'}
                                    >
                                        {filtersExpanded ? '◀' : '▶'}
                                    </button>
                                    <span style={{ writingMode: filtersExpanded ? 'horizontal-tb' : 'vertical-rl', transform: filtersExpanded ? 'none' : 'rotate(180deg)', transition: 'all 0.3s ease', display: 'inline-block', marginTop: filtersExpanded ? '0' : '0' }}>Filters & Settings</span>
                                </h3>
                                {designV2 && filtersExpanded && (
                                    <div className="v2-lib-drawer-head">
                                        <b>Filters{v2ActiveFilters.length > 0 && <span className="v2-faint v2-num"> · {v2ActiveFilters.length}</span>}</b>
                                        {v2ActiveFilters.length > 0 && <button type="button" className="v2-btn quiet sm" onClick={clearAllFiltersV2}>Clear all</button>}
                                        <button type="button" className="v2-btn quiet icon sm" aria-label="Close filters" onClick={() => setFiltersExpanded(false)}>×</button>
                                    </div>
                                )}
                                {filtersExpanded && <div className="file-list-container" style={{ background: 'var(--color-panel-bg)', padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                                    {/* Settings Section (rendered below Filters via flex order) */}
                                    <div style={{ marginBottom: '24px', order: 2, marginTop: '20px' }}>
                                        <h4 style={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#4CC2F1',
                                            marginBottom: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Settings
                                        </h4>
                                        <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                            Columns
                                        </label>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeDate}
                                                    onChange={(e) => setAnalyzeDate(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Date</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeGenre}
                                                    onChange={(e) => setAnalyzeGenre(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Genre</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeLabel}
                                                    onChange={(e) => setAnalyzeLabel(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Label</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeKey}
                                                    onChange={(e) => setAnalyzeKey(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Key</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }} title="Show the Energy column">
                                                <input
                                                    type="checkbox"
                                                    checked={showEnergyColumn}
                                                    onChange={(e) => setShowEnergyColumn(e.target.checked)}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Energy</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }} title="Show the Curve column — the track in 12 slices">
                                                <input
                                                    type="checkbox"
                                                    checked={showCurveColumn}
                                                    onChange={(e) => setShowCurveColumn(e.target.checked)}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Curve</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeBpm}
                                                    onChange={(e) => setAnalyzeBpm(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">BPM</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeLoudness}
                                                    onChange={(e) => setAnalyzeLoudness(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Loudness</span>
                                            </label>
                                            <label className="custom-checkbox" style={{ opacity: infoIsProcessing ? 0.5 : 1 }}>
                                                <input
                                                    type="checkbox"
                                                    checked={analyzeCompatibility}
                                                    onChange={(e) => setAnalyzeCompatibility(e.target.checked)}
                                                    disabled={infoIsProcessing}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Compatibility</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Key notation display toggle */}
                                    <div style={{ order: 3 }}>
                                        <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                            Key notation
                                        </label>
                                        <div style={{ marginBottom: '16px', display: 'flex', gap: '4px' }}>
                                            {[
                                                { key: 'musical', label: 'Musical (Am)' },
                                                { key: 'camelot', label: 'Camelot (8A)' },
                                            ].map(opt => (
                                                <button
                                                    key={opt.key}
                                                    onClick={() => dispatch(updateSettings({ keyNotation: opt.key }))}
                                                    style={{
                                                        flex: 1,
                                                        padding: '5px 8px',
                                                        fontSize: '12px',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        border: `1px solid ${keyNotation === opt.key ? 'var(--color-accent)' : 'var(--color-border)'}`,
                                                        background: keyNotation === opt.key ? 'var(--color-accent)' : 'transparent',
                                                        color: keyNotation === opt.key ? '#0a0a0a' : 'var(--color-text-secondary)',
                                                        fontWeight: keyNotation === opt.key ? 600 : 400,
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                        {/* Date display: year vs full date */}
                                        <label className="custom-checkbox" style={{ marginBottom: '4px' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!showFullDate}
                                                onChange={(e) => dispatch(updateSettings({ showFullDate: e.target.checked }))}
                                            />
                                            <span className="checkmark"></span>
                                            <span className="checkbox-label">Show full release date</span>
                                        </label>
                                    </div>

                                    {/* Filters Section (rendered first via flex order) */}
                                    <div style={{ order: 1 }}>
                                        {!designV2 && <h4 style={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#4CC2F1',
                                            marginBottom: '12px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            Filters
                                        </h4>}

                                        {/* Text filter (title/artist/etc.) — the per-tab filterTerms.library the track list filters by */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                                Search
                                            </label>
                                            <TabFilterBox placeholder="Filter tracks..." fullWidth tabKey="library" />
                                        </div>

                                        {/* BPM Range Slider */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                                BPM Range
                                            </label>
                                            <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                                <div style={{
                                                    marginBottom: '10px',
                                                    color: 'var(--color-text-secondary)',
                                                    textAlign: 'center',
                                                    fontSize: '12px'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Min: {bpmMin} <span style={{ fontSize: '0.75em', opacity: 0.7 }}>BPM</span>
                                                    </span>
                                                    &nbsp;&nbsp;
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Max: {bpmMax} <span style={{ fontSize: '0.75em', opacity: 0.7 }}>BPM</span>
                                                    </span>
                                                </div>
                                                <RangeSlider
                                                    min={libBounds.bpm[0]}
                                                    max={libBounds.bpm[1]}
                                                    step={1}
                                                    value={[bpmMin, bpmMax]}
                                                    onInput={(newValue) => {
                                                        userAdjustedFiltersRef.current = true;
                                                        setBpmMin(newValue[0]);
                                                        setBpmMax(newValue[1]);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Loudness Range Slider */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                                Loudness Range
                                            </label>
                                            <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                                <div style={{
                                                    marginBottom: '10px',
                                                    color: 'var(--color-text-secondary)',
                                                    textAlign: 'center',
                                                    fontSize: '12px'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Min: {loudnessMin} <span style={{ fontSize: '0.75em', opacity: 0.7 }}>LUFS</span>
                                                    </span>
                                                    &nbsp;&nbsp;
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Max: {loudnessMax} <span style={{ fontSize: '0.75em', opacity: 0.7 }}>LUFS</span>
                                                    </span>
                                                </div>
                                                <RangeSlider
                                                    min={libBounds.loudness[0]}
                                                    max={libBounds.loudness[1]}
                                                    step={1}
                                                    value={[loudnessMin, loudnessMax]}
                                                    onInput={(newValue) => {
                                                        userAdjustedFiltersRef.current = true;
                                                        setLoudnessMin(newValue[0]);
                                                        setLoudnessMax(newValue[1]);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Release Date Range Slider */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                                Release Date Range
                                            </label>
                                            <div style={{ paddingLeft: '10px', paddingRight: '10px' }}>
                                                <div style={{
                                                    marginBottom: '10px',
                                                    color: 'var(--color-text-secondary)',
                                                    textAlign: 'center',
                                                    fontSize: '12px'
                                                }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Min: {releaseDateMin}
                                                    </span>
                                                    &nbsp;&nbsp;
                                                    <span style={{
                                                        display: 'inline-block',
                                                        minWidth: '85px',
                                                        textAlign: 'left',
                                                        fontVariantNumeric: 'tabular-nums'
                                                    }}>
                                                        Max: {releaseDateMax}
                                                    </span>
                                                </div>
                                                <RangeSlider
                                                    min={libBounds.year[0]}
                                                    max={libBounds.year[1]}
                                                    step={1}
                                                    value={[releaseDateMin, releaseDateMax]}
                                                    onInput={(newValue) => {
                                                        userAdjustedFiltersRef.current = true;
                                                        setReleaseDateMin(newValue[0]);
                                                        setReleaseDateMax(newValue[1]);
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Hide missing files toggle */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={hideMissingFiles}
                                                    onChange={(e) => setHideMissingFiles(e.target.checked)}
                                                    style={{ cursor: 'pointer', accentColor: 'var(--color-accent, #4CC2F1)' }}
                                                />
                                                Hide missing files (not found on disk)
                                            </label>
                                        </div>

                                        {/* Key filter — interactive Camelot wheel */}
                                        <div>
                                            <label style={{ color: 'var(--color-text-secondary)', fontSize: '12px', display: 'block', marginBottom: '8px' }}>
                                                Keys
                                            </label>
                                            <CamelotWheel
                                                selectedKeys={selectedKeys}
                                                onToggle={handleKeyToggle}
                                                notation={keyNotation}
                                            />
                                        </div>

                                        {/* Cloud backup chips — mutually exclusive; both off = no filtering */}
                                        {backupMarks.active && (
                                            <div style={{ display: 'flex', gap: '5px', marginTop: '12px' }}>
                                                {[
                                                    { key: 'stored', label: 'Backed up', variant: 'check', hint: 'Show only backed-up tracks' },
                                                    { key: 'unstored', label: 'Not backed up', variant: 'slash', hint: 'Show only tracks not backed up yet' },
                                                ].map(({ key, label, variant, hint }) => {
                                                    const on = backupFilter === key;
                                                    return (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => {
                                                                const next = on ? null : key;
                                                                setBackupFilter(next);
                                                                track('library_backup_filter_toggled', { state: next || 'off' });
                                                            }}
                                                            title={on ? 'Show all tracks' : hint}
                                                            style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                                // height:auto beats the global `button { height: 40px }`
                                                                padding: '2px 8px', fontSize: '12px', lineHeight: '1.2', height: 'auto',
                                                                borderRadius: '999px', cursor: 'pointer',
                                                                border: on ? '1px solid var(--color-accent, #4CC2F1)' : '1px solid rgba(128, 128, 128, 0.35)',
                                                                color: on ? 'var(--color-accent, #4CC2F1)' : 'var(--color-text-secondary)',
                                                                background: on ? 'color-mix(in srgb, var(--color-accent, #4CC2F1) 12%, transparent)' : 'transparent',
                                                            }}
                                                        >
                                                            <CloudBackupGlyph variant={variant} size={12} style={{ stroke: 'currentColor' }} />
                                                            {label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                    </div>
                                </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bulk right-click menu: only the actions that make sense across a
                multi-row selection, in visible list order. Single-row right-clicks
                open the row's shared TrackContextMenu instead (see LibraryRow). */}
            {bulkCtx && orderedSelected.length > 1 && (
                <BulkRowContextMenu
                    x={bulkCtx.x}
                    y={bulkCtx.y}
                    onClose={() => setBulkCtx(null)}
                    analyticsFrom="library"
                    count={orderedSelected.length}
                    items={[
                        {
                            label: `Add ${orderedSelected.length} Tracks to Playlist`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/><rect x="3" y="3" width="18" height="18" rx="2"/></svg>,
                            onClick: () => {
                                setCtxPlaylistTargets(orderedSelected);
                                setCtxPlaylistPicker(true);
                            },
                        },
                        { divider: true },
                        {
                            label: `Remove ${orderedSelected.length} Tracks from Library`,
                            icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14H7L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>,
                            danger: true,
                            onClick: () => removeManyFromLibrary(orderedSelected),
                        },
                    ]}
                />
            )}
            {ctxPlaylistPicker && ctxPlaylistTargets.length > 0 && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setCtxPlaylistPicker(false)}
                    bulkFilePaths={ctxPlaylistTargets}
                    initialTab="add"
                    bulkLabel={(() => {
                        if (ctxPlaylistTargets.length > 1) return `${ctxPlaylistTargets.length} tracks`;
                        const f = enrichedFiles.find(ef => (ef.file || ef.filePath) === ctxPlaylistTargets[0]);
                        const t = f?.result?.track;
                        return t?.artist ? `${t.artist} - ${t.title || ''}` : (t?.title || ctxPlaylistTargets[0].split(/[\\/]/).pop());
                    })()}
                />
            )}
            {createPlaylistModal && (
                <AddToPlaylistModal
                    show={true}
                    onHide={() => setCreatePlaylistModal(null)}
                    bulkFilePaths={createPlaylistModal.filePaths}
                    defaultName={createPlaylistModal.defaultName}
                    bulkLabel={`${createPlaylistModal.count} track${createPlaylistModal.count === 1 ? '' : 's'} from “${createPlaylistModal.defaultName}”`}
                    onCreated={() => {
                        // Just close — the new playlist appears under "Select Playlist".
                        // (No auto-navigation; opening it there shows its tracks.)
                        setCreatePlaylistModal(null);
                    }}
                />
            )}
            {fillPicker && ReactDOM.createPortal(
                <div
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1040, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setFillPicker(null)}
                >
                    <div
                        className="fill-picker window-surface"
                        style={{
                            border: '1px solid var(--color-border)', borderRadius: 10,
                            padding: 20, width: 440, maxWidth: '90vw', maxHeight: '80vh',
                            display: 'flex', flexDirection: 'column',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                            Fill Empty Values
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 14 }}>
                            Pick which values to look up. Only empty fields are written — anything your tracks already have stays untouched.
                        </div>

                        <div style={{ overflowY: 'auto', flex: 1, marginBottom: 16 }}>
                            {FILL_GROUPS.map((group) => (
                                <div key={group.source} style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--color-text-tertiary)' }}>
                                        {group.title}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
                                        {group.hint}
                                    </div>
                                    {FILL_FIELDS.filter(f => f.source === group.source).map((field) => {
                                        const missing = fillFieldCounts[field.key];
                                        return (
                                            <label
                                                key={field.key}
                                                className="custom-checkbox"
                                                title={field.note || undefined}
                                                style={{ display: 'flex', width: '100%', padding: '6px 6px', borderRadius: 6, boxSizing: 'border-box' }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={!!fillPicker.selection[field.key]}
                                                    onChange={(e) => {
                                                        const next = e.target.checked;
                                                        setFillPicker(prev => (prev ? { selection: { ...prev.selection, [field.key]: next } } : prev));
                                                    }}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label" style={{ fontSize: 13 }}>
                                                    {field.label}
                                                    {field.note && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontWeight: 400 }}> · {field.note}</span>}
                                                </span>
                                                <span style={{ marginLeft: 'auto', paddingLeft: 10, fontSize: 11, whiteSpace: 'nowrap', color: missing > 0 ? 'var(--color-text-secondary)' : 'var(--color-text-tertiary)' }}>
                                                    {missing > 0 ? `${missing.toLocaleString()} missing` : 'all filled'}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>

                        {/* Button visuals (incl. hover/active) live in App.scss under
                            .fill-picker — inline styles would beat the :hover rules. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button
                                className="picker-link-btn"
                                onClick={() => {
                                    const allOn = FILL_FIELDS.every(f => fillPicker.selection[f.key]);
                                    setFillPicker({ selection: Object.fromEntries(FILL_FIELDS.map(f => [f.key, !allOn])) });
                                }}
                            >
                                {FILL_FIELDS.every(f => fillPicker.selection[f.key]) ? 'Clear all' : 'Select all'}
                            </button>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <button className="picker-btn picker-btn--ghost" onClick={() => setFillPicker(null)}>
                                    Cancel
                                </button>
                                <button
                                    className="picker-btn picker-btn--accent"
                                    disabled={fillPickerCount === 0}
                                    title={fillPickerCount === 0 ? 'Nothing to fill for the values you picked' : undefined}
                                    onClick={() => {
                                        const selection = fillPicker.selection;
                                        setFillPicker(null);
                                        handleFillEmpty(selection);
                                    }}
                                >
                                    {fillPickerCount > 0 ? `Fill ${fillPickerCount.toLocaleString()} track${fillPickerCount === 1 ? '' : 's'}` : 'Fill'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {cleanupModal && ReactDOM.createPortal(
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1040, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => !cleanupBusy && setCleanupModal(null)}>
                    <div className="window-surface" style={{
                        position: 'relative',
                        border: '1px solid rgba(76, 194, 241, 0.2)', borderRadius: 10,
                        padding: 20, width: 560, maxWidth: '90vw', maxHeight: '80vh',
                        display: 'flex', flexDirection: 'column',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                    }} onClick={(e) => e.stopPropagation()}>
                        {cleanupBusy && (
                            <div style={{
                                position: 'absolute', inset: 0, borderRadius: 10,
                                background: 'rgba(20, 20, 25, 0.7)',
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                gap: 12, zIndex: 10,
                            }}>
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    border: '3px solid rgba(76, 194, 241, 0.2)',
                                    borderTopColor: 'var(--color-accent-light)',
                                    animation: 'spin 0.9s linear infinite',
                                }} />
                                {/* On the dark busy veil, not the panel — so this grey is deliberate. */}
                                <div style={{ fontSize: 12, color: '#bbb' }}>{cleanupBusyMsg}</div>
                                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                            </div>
                        )}
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                            Clean Up Missing Files
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
                            These files are referenced by your library but no longer exist on disk. Pick a new location for each group, or remove them.
                        </div>
                        <div style={{ overflowY: 'auto', flex: 1, marginBottom: 16 }}>
                            {relocateReview ? (
                                <div style={{
                                    padding: 16,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(76, 194, 241, 0.25)',
                                    borderRadius: 8,
                                }}>
                                    <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 8 }}>
                                        Found <span style={{ color: 'var(--color-accent-light)', fontWeight: 600 }}>{relocateReview.matched.length}</span>
                                        <span style={{ opacity: 0.7 }}> of {relocateReview.total} files in </span>
                                        <code style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{relocateReview.folder}</code>
                                    </div>
                                    {relocateReview.unmatched.length > 0 && (
                                        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                                            {relocateReview.unmatched.length} file{relocateReview.unmatched.length === 1 ? '' : 's'} still missing — they'll stay in the list.
                                        </div>
                                    )}
                                    {relocateReview.matched.length > 0 && (
                                        <details style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 12 }}>
                                            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Show matched files</summary>
                                            <div style={{ maxHeight: 180, overflowY: 'auto', marginTop: 6 }}>
                                                {relocateReview.matched.slice(0, 100).map((m, j) => (
                                                    <div key={j} style={{ padding: '2px 0', wordBreak: 'break-all' }}>
                                                        {m.newPath.split(/[\\/]/).pop()}
                                                    </div>
                                                ))}
                                                {relocateReview.matched.length > 100 && <div style={{ opacity: 0.5 }}>…and {relocateReview.matched.length - 100} more</div>}
                                            </div>
                                        </details>
                                    )}
                                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => setRelocateReview(null)}
                                            disabled={cleanupBusy}
                                            style={{
                                                padding: '8px 14px',
                                                background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid rgba(255,255,255,0.1)',
                                                borderRadius: 6, color: 'var(--color-text-primary)', fontSize: 12, fontWeight: 500,
                                                cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s ease',
                                            }}
                                            onMouseEnter={(e) => { if (!cleanupBusy) { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)'; } }}
                                            onMouseLeave={(e) => { if (!cleanupBusy) { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; } }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmRelocateReview}
                                            disabled={cleanupBusy || relocateReview.matched.length === 0}
                                            style={{
                                                padding: '8px 14px',
                                                background: 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.05) 100%)',
                                                border: '1px solid rgba(76, 194, 241, 0.3)',
                                                borderRadius: 6, color: 'var(--color-accent-light)', fontSize: 12, fontWeight: 500,
                                                cursor: (cleanupBusy || relocateReview.matched.length === 0) ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s ease',
                                                opacity: relocateReview.matched.length === 0 ? 0.5 : 1,
                                            }}
                                            onMouseEnter={(e) => { if (!cleanupBusy && relocateReview.matched.length > 0) { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.25) 0%, rgba(76, 194, 241, 0.1) 100%)'; e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.5)'; } }}
                                            onMouseLeave={(e) => { if (!cleanupBusy && relocateReview.matched.length > 0) { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.05) 100%)'; e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.3)'; } }}
                                        >
                                            Relocate {relocateReview.matched.length} file{relocateReview.matched.length === 1 ? '' : 's'}
                                        </button>
                                    </div>
                                </div>
                            ) : (<>
                            {cleanupModal.groups.length === 0 && (
                                <div style={{
                                    padding: 32,
                                    textAlign: 'center',
                                    color: 'var(--color-text-secondary)',
                                    fontSize: 13,
                                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
                                }}>
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#4CC2F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                                    <div>All missing files resolved.</div>
                                </div>
                            )}
                            {cleanupModal.groups.map((g, i) => (
                                <div key={i} style={{
                                    padding: 12,
                                    marginBottom: 10,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    borderRadius: 8,
                                }}>
                                    <div style={{ fontSize: 12, color: 'var(--color-text-primary)', marginBottom: 4, wordBreak: 'break-all' }}>
                                        <span style={{ color: 'var(--color-accent-light)', fontWeight: 500 }}>{g.paths.length}</span>
                                        <span style={{ opacity: 0.7 }}> file{g.paths.length === 1 ? '' : 's'} from </span>
                                        <code style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{g.dir}</code>
                                    </div>
                                    <details style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 8 }}>
                                        <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Show files</summary>
                                        <div style={{ maxHeight: 120, overflowY: 'auto', marginTop: 6 }}>
                                            {g.paths.slice(0, 50).map((p, j) => (
                                                <div key={j} style={{ padding: '2px 0', wordBreak: 'break-all' }}>
                                                    {p.split(/[\\/]/).pop()}
                                                </div>
                                            ))}
                                            {g.paths.length > 50 && <div style={{ opacity: 0.5 }}>…and {g.paths.length - 50} more</div>}
                                        </div>
                                    </details>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <button
                                            onClick={() => handleBulkRelocate(g.paths)}
                                            disabled={cleanupBusy}
                                            style={{
                                                flex: 1, padding: '8px 12px',
                                                background: 'transparent',
                                                border: '1px solid rgba(76, 194, 241, 0.35)',
                                                borderRadius: 6, color: 'var(--color-accent-light)', fontSize: 12, fontWeight: 500,
                                                cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s ease',
                                                opacity: cleanupBusy ? 0.5 : 1,
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!cleanupBusy) {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.2) 0%, rgba(76, 194, 241, 0.08) 100%)';
                                                    e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.55)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!cleanupBusy) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.35)';
                                                }
                                            }}
                                        >
                                            Locate folder…
                                        </button>
                                        <button
                                            onClick={async () => {
                                                const confirmed = await toast.confirm(
                                                    `Remove ${g.paths.length} track${g.paths.length === 1 ? '' : 's'} from your library? This won't delete any files from disk.`,
                                                    'Remove missing tracks?',
                                                    { confirmText: 'Remove', confirmVariant: 'danger' }
                                                );
                                                if (confirmed) handleBulkRemove(g.paths);
                                            }}
                                            disabled={cleanupBusy}
                                            style={{
                                                flex: 1, padding: '8px 12px',
                                                background: 'transparent',
                                                border: '1px solid rgba(255, 77, 77, 0.35)',
                                                borderRadius: 6, color: 'var(--color-danger)', fontSize: 12, fontWeight: 500,
                                                cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                                transition: 'all 0.2s ease',
                                                opacity: cleanupBusy ? 0.5 : 1,
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!cleanupBusy) {
                                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 77, 77, 0.2) 0%, rgba(255, 77, 77, 0.08) 100%)';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 77, 77, 0.55)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!cleanupBusy) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.borderColor = 'rgba(255, 77, 77, 0.35)';
                                                }
                                            }}
                                        >
                                            Remove these
                                        </button>
                                    </div>
                                </div>
                            ))}
                            </>)}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                            <div style={{ display: 'flex', gap: 8, visibility: (relocateReview || cleanupModal.groups.length === 0) ? 'hidden' : 'visible' }}>
                                <button
                                    onClick={() => {
                                        const all = cleanupModal.groups.flatMap(g => g.paths);
                                        handleBulkRelocate(all, 'all');
                                    }}
                                    disabled={cleanupBusy}
                                    title="Pick one folder — we'll search it and all subfolders by filename"
                                    style={{
                                        padding: '8px 14px',
                                        background: 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.05) 100%)',
                                        border: '1px solid rgba(76, 194, 241, 0.3)',
                                        borderRadius: 6, color: 'var(--color-accent-light)', fontSize: 12, fontWeight: 500,
                                        cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        opacity: cleanupBusy ? 0.5 : 1,
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!cleanupBusy) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.25) 0%, rgba(76, 194, 241, 0.1) 100%)';
                                            e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.5)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!cleanupBusy) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(76, 194, 241, 0.15) 0%, rgba(76, 194, 241, 0.05) 100%)';
                                            e.currentTarget.style.borderColor = 'rgba(76, 194, 241, 0.3)';
                                        }
                                    }}
                                >
                                    Locate all…
                                </button>
                                <button
                                    onClick={async () => {
                                        const all = cleanupModal.groups.flatMap(g => g.paths);
                                        const confirmed = await toast.confirm(
                                            `Remove all ${all.length} missing tracks from your library? This won't delete any files from disk.`,
                                            'Remove all missing tracks?',
                                            { confirmText: 'Remove all', confirmVariant: 'danger' }
                                        );
                                        if (confirmed) handleBulkRemove(all);
                                    }}
                                    disabled={cleanupBusy}
                                    style={{
                                        padding: '8px 14px',
                                        background: 'linear-gradient(135deg, rgba(255, 77, 77, 0.15) 0%, rgba(255, 77, 77, 0.05) 100%)',
                                        border: '1px solid rgba(255, 77, 77, 0.3)',
                                        borderRadius: 6, color: 'var(--color-danger)', fontSize: 12, fontWeight: 500,
                                        cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s ease',
                                        opacity: cleanupBusy ? 0.5 : 1,
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!cleanupBusy) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 77, 77, 0.25) 0%, rgba(255, 77, 77, 0.1) 100%)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 77, 77, 0.5)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!cleanupBusy) {
                                            e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 77, 77, 0.15) 0%, rgba(255, 77, 77, 0.05) 100%)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 77, 77, 0.3)';
                                        }
                                    }}
                                >
                                    Remove all
                                </button>
                            </div>
                            <button
                                onClick={() => setCleanupModal(null)}
                                disabled={cleanupBusy}
                                style={{
                                    padding: '8px 18px',
                                    background: 'rgba(255,255,255,0.05)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 6, color: 'var(--color-text-primary)', fontSize: 12, fontWeight: 500,
                                    cursor: cleanupBusy ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    if (!cleanupBusy) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!cleanupBusy) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                                    }
                                }}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default VisualizeLibraryTab;
